/* eslint-disable @typescript-eslint/ban-types */
// Dependencies
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isSameDay } from 'date-fns';
// Types
import { Picture } from '../../picture/objects/picture';
import {
    ADD_CUSTOM_PICTURES,
    SET_PICTURE
} from '../../picture/redux/actionTypes';
import { Preferences } from '../../profile/objects/preferences';
import {
    SET_PREFERENCES
} from '../../profile/redux/actionTypes';
import { BlackOut } from '../objects/blackOut';
import { CancellationRule } from '../objects/cancellationRule';
import { Column } from '../objects/column';
import { EarlyBooking } from '../objects/earlyBooking';
import { EarlyBookingRule } from '../objects/earlyBookingRule';
import { Error } from '../objects/error';
import { Extra } from '../objects/extra';
import { ExtraPrice } from '../objects/extraPrice';
import { ManualAccommodationState } from '../objects/manualAccommodationState';
import { Offer } from '../objects/offer';
import { Room } from '../objects/room';
import { RoomBedding } from '../objects/roomBedding';
import { RoomPrice } from '../objects/roomPrice';
import { RoomStayPeriod } from '../objects/roomStayPeriod';
import { Stocks } from '../objects/stocks';
import { Yield } from '../objects/yield';
import { YieldRule } from '../objects/yieldRule';
// Constants
import { cloneDeep } from 'lodash';
import { Contract } from '../../customProvider/objects/contract';
import { CustomProvider } from '../../customProvider/objects/customProvider';
import { IataCountry } from '../../iata/objects/iataCountry';
import { ManualAccommodationChain } from '../../manualAccommodationChain/objects/manualAccommodationChain';
import { RoomProduct } from '../../stockStatus/objects/roomProduct';
import {
    EMPTY_CANCELLATION_CONDITION,
    EMPTY_COLUMNS,
    EMPTY_EARLY_BOOKING_CONDITION,
    EMPTY_EXTRA_PRICE,
    EMPTY_FACTS,
    EMPTY_INTERVAL_AGE_OFFERS,
    EMPTY_STAY_DURATION,
    EMPTY_ROOM_BEDDING,
    EMPTY_ROOM_BLACK_OUT,
    EMPTY_ROOM_MIN_STAY_PERIOD,
    EMPTY_ROOM_PRICE,
    EMPTY_ROOM_PRICE_PERIOD,
    EMPTY_YELD_RULE
} from '../constants/manualAccommodation.constant';
import { AccommodationListCriteria } from '../objects/accommodationListCriteria';
import { Contact } from '../objects/contact';
import { ContractParameters } from '../objects/contractParameters';
import { Fact } from '../objects/fact';
import { GiataAccommodation } from '../objects/giataAccommodation';
import { IntervalAgeOffer } from '../objects/intervalAgeOffer';
import { List } from '../objects/list';
import { ManualAccommodation } from '../objects/manualAccommodation';
import { MealTaxes } from '../objects/mealTaxes';
import { MinimalStay } from '../objects/minimalStay';
import { PersonType } from '../objects/personType';
import { Price } from '../objects/price';
import { PriceToType } from '../objects/priceToType';
import { PricingType } from '../objects/pricingType';
import { StockRoomProductInfo } from '../objects/stockRoomProductInfo';
import { Taxes } from '../objects/taxes';
import {
    searchOnLocalizationNameAndReference,
    sortAccommodationArray
} from '../utils/filterArray';
import { AccommodationTag } from '../../accommodationTag/objects/accommodationTag';
import { StayDuration } from '../objects/stayDuration';

const initialState: ManualAccommodationState = {
    margin_master_markup: false,
    address: '',
    address_error: '',
    address_search: null,
    best_offer_limit: 0,
    best_offer_order: 'BEST_REDUCTION',
    booking_type: 0,
    cancellation_rule_list: [],
    cancellation_rule_sorted_list: [],
    cancellation_rule_criteria: {
        order_by: 'name',
        search: '',
        order: 'asc'
    },
    cancellation_rule_create: false,
    cancellation_rule_delete: null,
    cancellation_rule_duplicate: null,
    cancellation_rule_edit: null,
    cancellation_rule_name: '',
    cancellation_rule_name_locales: {},
    cancellation_rule_name_error: '',
    cancellation_rule_reference: '',
    cancellation_rule_reference_error: '',
    cancellation_rule_start_date: null,
    cancellation_rule_start_date_error: '',
    cancellation_rule_end_date: null,
    cancellation_rule_end_date_error: '',
    cancellation_rule_free: 0,
    cancellation_rule_conditions: [{ ...EMPTY_CANCELLATION_CONDITION }],
    catalog_price: false,
    chain: null,
    check_in_hour: '00',
    check_in_minutes: '00',
    check_in_recommendation: '',
    check_in_recommendation_error: '',
    check_in_recommendation_locales: {},
    check_out_hour: '00',
    check_out_minutes: '00',
    check_out_recommendation: '',
    check_out_recommendation_error: '',
    check_out_recommendation_locales: {},
    children_locales: {},
    city: '',
    city_error: '',
    client_hotel_identifier: '',
    columns: EMPTY_COLUMNS,
    comfort_rate: 0,
    contact_create: false,
    contact_delete: null,
    contact_edit: null,
    contact_email: '',
    contact_email_error: '',
    contact_first_name: '',
    contact_first_name_error: '',
    contact_job: '',
    contact_job_error: '',
    contact_last_name: '',
    contact_last_name_error: '',
    contact_phone_number: '',
    contact_phone_number_error: '',
    contract_create: false,
    contract_contract: null,
    contract_contract_error: '',
    contract_contracts: [null],
    contract_provider: null,
    contract_providers: [null],
    contract_selected: null,
    count: 0,
    country: null,
    create: false,
    create_name: '',
    create_name_error: '',
    create_search_name: '',
    create_search_city: '',
    create_search_country: null,
    create_search_results: null,
    create_search_selected: -1,
    create_tab: 0,
    custom_pictures: [],
    custom_pictures_order: [],
    early_booking_conditions: [{ ...EMPTY_EARLY_BOOKING_CONDITION }],
    early_booking_create: false,
    early_booking_delete: null,
    early_booking_duplicate: null,
    early_booking_edit: null,
    early_booking_end_date: null,
    early_booking_end_date_error: '',
    early_booking_extra: [],
    early_booking_extra_conflicts: [],
    early_booking_list: [],
    early_booking_sorted_list: [],
    early_booking_criteria: { order_by: 'name', search: '', order: 'asc' },
    early_booking_name_locales: {},
    early_booking_name_locales_error: '',
    early_booking_description_locales: {},
    early_booking_description_locales_error: '',
    early_booking_offer_conflicts: [],
    early_booking_reference: '',
    early_booking_reference_error: '',
    early_booking_start_date: null,
    early_booking_start_date_error: '',
    early_booking_min_days: '',
    early_booking_max_days: '',
    edit: null,
    edit_step: 'INFO',
    editor_pictures: null,
    email: '',
    email_error: '',
    empty_room_price: EMPTY_ROOM_PRICE,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    extra_create: false,
    extra_delete: null,
    extra_duplicate: null,
    extra_edit: null,
    extra_conflicts: [],
    extra_list: [],
    extra_sorted_list: [],
    extra_criteria: { order_by: 'name', search: '', order: 'asc' },
    extra_description: '',
    extra_description_error: '',
    extra_description_locales: {},
    extra_included: false,
    extra_per_day: false,
    extra_per_person: true,
    extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
    extra_price_by: 0,
    extra_maximal_age: '',
    extra_meal_plan: [],
    extra_minimal_age: '',
    extra_minimal_age_error: '',
    extra_minimal_travelers: '',
    extra_name: '',
    extra_name_error: '',
    extra_name_locales: {},
    extra_reference: '',
    extra_reference_error: '',
    extra_transfer: false,
    extra_type: [0],
    family_plan: '',
    family_plan_max: '',
    pricing_type: PricingType.PURCHASE,
    com_calculation_as_percentage: false,
    com_applied_on_ttc: false,
    vat: '',
    global_com: '',
    global_markup: '',
    com_is_percentage: false,
    markup_is_percentage: false,
    stay_duration: [],
    fact_search: null,
    facts: [...EMPTY_FACTS],
    fax: '',
    fax_error: '',
    filter_address: '',
    filter_chain: null,
    filter_country: null,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_destination: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_name: '',
    filter_tags: [],
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_status: 'ALL',
    giata_pictures: [],
    google_id: '',
    google_id_error: '',
    info_locales: {},
    house_number: '',
    house_number_error: '',
    is_teen_adult_in_monoparental: false,
    is_protected_description: false,
    latitude: '',
    latitude_error: '',
    limit: 50,
    list: null,
    longitude: '',
    longitude_error: '',
    video_link: '',
    manual_accommodation: null,
    manual_accommodation_duplicate: null,
    manual_accommodation_duplicate_accommodation_to: null,
    manual_accommodation_duplicate_contract: null,
    manual_accommodation_duplicate_contract_target: null,
    manual_accommodation_duplicate_contract_to: null,
    manual_accommodation_duplicate_multiple: false,
    manual_accommodation_duplicate_multiple_cancellation_rule_modal: false,
    manual_accommodation_duplicate_multiple_early_booking_modal: false,
    manual_accommodation_duplicate_multiple_extra_modal: false,
    manual_accommodation_duplicate_multiple_items: [],
    manual_accommodation_duplicate_multiple_offer_modal: false,
    manual_accommodation_duplicate_multiple_yield_modal: false,
    manual_accommodation_duplicate_price: '',
    manual_accommodation_duplicate_price_change: 1,
    manual_accommodation_duplicate_price_change_type: 0,
    manual_accommodation_duplicate_price_type: 'FIX',
    manual_accommodation_duplicate_step: 0,
    manual_accommodation_duplicate_type: null,
    max_age_baby: '',
    max_age_baby_error: '',
    max_age_child: '',
    max_age_child_error: '',
    max_age_teen: '',
    max_age_teen_error: '',
    name: '',
    name_error: '',
    client_hotel_identifier_error: '',
    offer_by: 0,
    offer_interval_age_offers: EMPTY_INTERVAL_AGE_OFFERS,
    offer_create: false,
    offer_applied_for: 'ADULT',
    offer_applied_for_max: '',
    offer_applied_for_min: 0,
    offer_applied_for_min_error: '',
    offer_booked_night: false,
    offer_booked_night_number: 0,
    offer_booked_room: false,
    offer_booked_room_number: 0,
    offer_check_out_dates: false,
    offer_check_out_end_date: null,
    offer_check_out_start_date: null,
    offer_check_out_start_date_error: '',
    offer_child: false,
    offer_child_number: 0,
    offer_combine: [],
    offer_combine_accommodation: [],
    offer_combine_accommodation_chain: [],
    offer_delete: null,
    offer_description_locales: {},
    offer_duplicate: null,
    offer_edit: null,
    offer_end_date: null,
    offer_end_date_error: '',
    offer_extra: null,
    offer_extra_error: '',
    offer_extra_number: '',
    offer_extra_number_error: '',
    offer_extras: [],
    offer_extras_error: '',
    offer_list: [],
    offer_sorted_list: [],
    offer_criteria: { order_by: 'name', search: '', order: 'asc' },
    offer_name_locales: {},
    offer_name_locales_error: '',
    offer_night: '',
    offer_night_error: '',
    offer_night_type: 'cheap',
    offer_on_extra_applied: null,
    offer_on_extra_applied_error: '',
    offer_is_on_extra_applied: false,
    offer_is_honeymoon: false,
    offer_reduction: '',
    offer_reduction_error: '',
    offer_reduction_type: 'FIX',
    offer_reference: '',
    offer_start_date: null,
    offer_start_date_error: '',
    offer_sub_room: false,
    offer_trip_end_date: null,
    offer_trip_end_date_error: '',
    offer_trip_start_date: null,
    offer_trip_start_date_error: '',
    offer_type: 0,
    order: 'asc',
    order_by: '-created_date',
    page: 0,
    picture_create: false,
    phone_number: '',
    phone_number_error: '',
    preferences: [],
    providers: [],
    recommendation: 0,
    refresh: false,
    restoration_locales: {},
    restoration_rate: 0,
    room_bedding: [{ ...EMPTY_ROOM_BEDDING }],
    room_bedding_error: '',
    rooms_black_out: EMPTY_ROOM_BLACK_OUT,
    rooms_black_out_create: false,
    rooms_un_black_out_create: false,
    room_black_outs: [],
    room_black_outs_duplicate: null,
    room_black_outs_room: null,
    room_black_outs_target: null,
    room_cancellation_rules: [],
    room_category: '',
    room_category_error: '',
    room_create: false,
    room_delete: null,
    room_duplicate: null,
    room_desc_locales: {},
    room_description_locales: {},
    room_early_bookings: [],
    room_edit: null,
    room_equipments: [],
    room_extras: [],
    room_list: [],
    room_sorted_list: [],
    room_criteria: { order_by: 'name', search: '', order: 'asc' },
    room_locked_duration: 0,
    room_name_locales: {},
    room_main: true,
    room_max_adults: 0,
    room_max_children: 0,
    room_max_nights: 0,
    room_max_travelers: 0,
    room_min_adults: 0,
    room_min_children: 0,
    room_min_nights: 0,
    room_min_stay_periods: [],
    room_min_travelers: 0,
    room_min_travelers_error: '',
    room_offers: [],
    room_pictures: [],
    room_pictures_order: false,
    room_price: null,
    room_price_duplicate: null,
    room_price_duplicate_target: null,
    room_price_scroll_id: null,
    room_prices: [],
    is_room_price_empty: true,
    room_prohibited_compositions: [],
    room_size: '',
    room_yields: [],
    running: false,
    search: '',
    situation_locales: {},
    situation_rate: 0,
    stars: 0,
    stocks_closed_sale_end_date: null,
    stocks_closed_sale_room_id: null,
    stocks_closed_sale_start_date: null,
    stocks_closed_sale_hover_date: null,
    stocks_current_day: null,
    stocks_current_month: null,
    stocks_state: null,
    stocks_display_mode: 0,
    stocks_edit_mode: null,
    stocks_stock_room_id: null,
    stocks_stock_price: null,
    stocks_stock_index: null,
    stocks_stock_value: '',
    superior_stars: false,
    display_for_trip_plan: true,
    display_for_package: false,
    taxes_fees: '',
    taxes_meal: '',
    taxes_meal_id: null,
    taxes_meal_vat: '',
    taxes_resort: '',
    taxes_resort_id: null,
    taxes_resort_vat: '',
    taxes_touristic: '',
    taxes_touristic_vat: '',
    type: 16,
    website: '',
    website_error: '',
    yield_create: false,
    yield_delete: null,
    yield_duplicate: null,
    yield_edit: null,
    yield_end_date: null,
    yield_end_date_error: '',
    yield_list: [],
    yield_reference: '',
    yield_reference_error: '',
    yield_sorted_list: [],
    yield_criteria: { order_by: 'name', search: '', order: 'asc' },
    yield_name_locales: {},
    yield_rules: [{ ...EMPTY_YELD_RULE }],
    yield_start_date: null,
    yield_start_date_error: '',
    zip_code: '',
    zip_code_error: '',
    lock: false,
    publish_on_web: false,
    destination_name: '',
    zoom: 4,
    stock_room_product_info: null,
    stock_room_products: null,
    stock_room_products_loading: false,
    accommodation_tags: []
};

const updateExtraProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const prices = [...state.extra_prices];
    const price = { ...prices[index] };
    price[key] = value;
    price[`${key}_error`] = '';
    prices[index] = price;
    return {
        ...state,
        extra_prices: prices
    };
};

const updateCancellationRuleConditionProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const conditions = [...state.cancellation_rule_conditions];
    const condition = { ...conditions[index] };
    condition[key] = value;
    conditions[index] = condition;
    return { ...state, cancellation_rule_conditions: conditions };
};

const updateEarlyBookingConditionProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: keyof EarlyBookingRule
) => {
    const conditions = [...state.early_booking_conditions];
    const condition = { ...conditions[index] };
    if (key === 'type') {
        condition.days = '';
        condition.days_error = '';
        condition.max_date = null;
        condition.max_date_error = '';
    }
    if (key === 'deduction_type') {
        condition.deduction = '';
        condition.deduction_error = '';
    }
    if (key === 'deduction') {
        condition.deduction_error = '';
    }
    if (key === 'days') {
        condition.days_error = '';
    }
    if (key === 'max_date') {
        condition.max_date_error = '';
    }
    condition[key] = value;
    conditions[index] = condition;
    return { ...state, early_booking_conditions: conditions };
};

const updateYieldRuleProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const rules = [...state.yield_rules];
    const rule = { ...rules[index] };
    rule[key] = value;
    rules[index] = rule;
    return { ...state, yield_rules: rules };
};

const updateRoomMinStayPeriodProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const room_min_stay_periods = [...state.room_min_stay_periods];
    const room_min_stay_period = { ...room_min_stay_periods[index] };
    room_min_stay_period[key] = value;
    room_min_stay_periods[index] = room_min_stay_period;
    return { ...state, room_min_stay_periods: room_min_stay_periods };
};

const updateRoomBlackOutProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const room_black_outs = [...state.room_black_outs];
    const room_black_out = { ...room_black_outs[index] };
    room_black_out[key] = value;
    room_black_outs[index] = room_black_out;
    return { ...state, room_black_outs: room_black_outs };
};

const updateRoomPriceProperty = (
    value: any,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const pricing_type = state.manual_accommodation?.contract_parameters?.pricing_type;
    const com_is_percentage = state.manual_accommodation?.contract_parameters?.com_is_percentage;
    const markup_is_percentage = state.manual_accommodation?.contract_parameters?.markup_is_percentage;
    let is_selling_ttc = false;
    const room_prices = [...state.room_prices];
    const price = { ...room_prices[index] };
    const _key = key.replace(/com_|markup_|selling_/g, '');
    price[`${key}_price_error`] = '';
    price[`${_key}_price_error`] = '';
    price[`${key}_error`] = '';
    price[`${_key}_error`] = '';
    if (value === "" && !(key.includes("com_") || key.includes("markup_"))) {
        const _key = key.replace(/com_|markup_|selling_/g, '');
        price[key] = "";
        price[_key] = "";
    }
    else {
        if (pricing_type?.includes('TTC')) {
            is_selling_ttc = true;
        }
        price[key] = value;
        if (pricing_type?.includes('PURCHASE') && (pricing_type?.includes('COM') || pricing_type?.includes('MARKUP') || pricing_type?.includes('DETAILED'))) {
            const _key = key.replace(/com_|markup_|selling_/g, '');
            if (!key.includes('selling')) {
                const com = Number(com_is_percentage ? (Number(price[_key]) * Number(price[`com_${_key}`]) / 100) : Number(price[`com_${_key}`]));
                const markup = Number(markup_is_percentage ? ((Number(price[_key]) + com) * Number(price[`markup_${_key}`]) / 100) : Number(price[`markup_${_key}`]));
                price[`selling_${_key}`] = Number(price[_key])
                    + com
                    + markup;
                price[`selling_${_key}_error`] = '';
            } else {
                if (Number(price[_key]) === 0) {
                    price[`${key}_error`] = 'shared.no-purchase-price';

                }
                else {
                    const com = com_is_percentage ? Number(price[_key]) * Number(price[`com_${_key}`]) / 100 : Number(price[`com_${_key}`]);
                    const markup = markup_is_percentage ? ((Number(price[`selling_${_key}`]) - Number(price[_key]) - com) * 100) / (Number(price[_key]) + com) : Number(price[`selling_${_key}`]) - Number(price[_key]) - com;
                    price[`markup_${_key}`] = markup < 0 ? 0 : markup;
                    if (markup < 0) {
                        price[`${key}_error`] = 'shared.selling-at-a-loss';
                    } else {
                        price[`${key}_error`] = '';
                    }
                }
            }
        }
        if (pricing_type?.includes('SELLING') && (pricing_type?.includes('COM') || pricing_type?.includes('MARKUP') || pricing_type?.includes('DETAILED'))) {
            if (key.includes('selling') || key.includes('com') || key.includes('markup')) {
                const _key = key.replace(/com_|markup_|selling_/g, '');
                if (is_selling_ttc) {
                    if (state.com_applied_on_ttc) {
                        const selling_price_ttc = Number(price[`selling_${_key}`]);
                        const selling_price_ht = Number(price[`selling_${_key}`]) * 100 / (100 + Number(state.vat));
                        if (state.com_calculation_as_percentage) {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    // A = (VHT - (VTTC - (VTTC / (1 + COM / 100)) * (1 - MARKUP /100)
                                    price[_key] = (selling_price_ht - (selling_price_ttc * Number(price[`com_${_key}`]) / 100)) * (1 - Number(price[`markup_${_key}`]) / 100);
                                } else {
                                    // A = (VHT - (VTTC - (VTTC / (1 + COM / 100)) / (1+MARKUP /100)
                                    price[_key] = (selling_price_ht - (selling_price_ttc * Number(price[`com_${_key}`]) / 100)) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                price[_key] = selling_price_ht - Number(price[`markup_${_key}`]) - ((selling_price_ttc - Number(price[`markup_${_key}`])) * Number(price[`com_${_key}`]) / 100);
                            }
                        } else if (com_is_percentage) {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    // A = (VHT - (VTTC - (VTTC / (1 + COM / 100)) * (1 - MARKUP /100)
                                    price[_key] = (selling_price_ht - (selling_price_ttc - (selling_price_ttc / (1 + Number(price[`com_${_key}`]) / 100)))) * (1 - Number(price[`markup_${_key}`]) / 100);
                                } else {
                                    // A = (VHT - (VTTC - (VTTC / (1 + COM / 100)) / (1 + MARKUP /100)
                                    price[_key] = (selling_price_ht - (selling_price_ttc - (selling_price_ttc / (1 + Number(price[`com_${_key}`]) / 100)))) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                price[_key] = selling_price_ht - Number(price[`markup_${_key}`]) - (selling_price_ttc - (selling_price_ttc / (1 + Number(price[`com_${_key}`]) / 100)));
                            }
                        } else {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    // A = (V - COM) * (1 - MARKUP/100)
                                    price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) * (1 - Number(price[`markup_${_key}`]) / 100);
                                }
                                else {
                                    // A = (V - COM) / (1 + MARKUP/100)
                                    price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                // A = (V - COM - MARKUP/100)
                                price[_key] = selling_price_ht - Number(price[`com_${_key}`]) - Number(price[`markup_${_key}`]);
                            }
                        }
                    } else {
                        const selling_price = Number(price[`selling_${_key}`]) * 100 / (100 + Number(state.vat));
                        if (state.com_calculation_as_percentage) {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    // A = VHT / (1 + COM / 100)*(1 - MARKUP /100)
                                    price[_key] = (selling_price - (selling_price * Number(price[`com_${_key}`]) / 100)) * (1 - Number(price[`markup_${_key}`]) / 100);
                                } else {
                                    // A = VHT / (1 + COM / 100)/(1+ MARKUP /100)
                                    price[_key] = (selling_price - (selling_price * Number(price[`com_${_key}`]) / 100)) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                price[_key] = (selling_price - Number(price[`markup_${_key}`]) - ((selling_price - Number(price[`markup_${_key}`])) * Number(price[`com_${_key}`]) / 100));
                                // price[_key] = (selling_price - Number(price[`markup_${_key}`]) - (selling_price - selling_price * Number(price[`com_${_key}`]) / 100))
                            }
                        } else if (com_is_percentage) {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    price[_key] = selling_price / (1 + Number(price[`com_${_key}`]) / 100) * (1 - Number(price[`markup_${_key}`]) / 100);
                                } else {
                                    price[_key] = selling_price / (1 + Number(price[`com_${_key}`]) / 100) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                price[_key] = (selling_price - Number(price[`markup_${_key}`])) / (1 + Number(price[`com_${_key}`]) / 100);
                            }
                        } else {
                            if (markup_is_percentage) {
                                if (state.margin_master_markup) {
                                    price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) * (1 - Number(price[`markup_${_key}`]) / 100);
                                } else {
                                    price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) / (1 + Number(price[`markup_${_key}`]) / 100);
                                }
                            } else {
                                price[_key] = selling_price - Number(price[`com_${_key}`]) - Number(price[`markup_${_key}`]);
                            }
                        }
                    }
                } else {
                    if (state.com_calculation_as_percentage) {
                        if (markup_is_percentage) {
                            if (state.margin_master_markup) {
                                price[_key] = (Number(price[`selling_${_key}`]) - (Number(price[`selling_${_key}`]) * Number(price[`com_${_key}`]) / 100)) * (1 - Number(price[`markup_${_key}`]) / 100);
                            } else {
                                price[_key] = (Number(price[`selling_${_key}`]) - (Number(price[`selling_${_key}`]) * Number(price[`com_${_key}`]) / 100)) / (1 + Number(price[`markup_${_key}`]) / 100);
                            }
                        } else {
                            price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`markup_${_key}`])) - ((Number(price[`selling_${_key}`]) - Number(price[`markup_${_key}`])) * Number(price[`com_${_key}`]) / 100);
                        }
                    } else if (com_is_percentage) {
                        if (markup_is_percentage) {
                            if (state.margin_master_markup) {
                                price[_key] = Number(price[`selling_${_key}`]) / (1 + Number(price[`com_${_key}`]) / 100) * (1 - Number(price[`markup_${_key}`]) / 100);
                            } else {
                                price[_key] = Number(price[`selling_${_key}`]) / (1 + Number(price[`com_${_key}`]) / 100) / (1 + Number(price[`markup_${_key}`]) / 100);
                            }
                        } else {
                            price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`markup_${_key}`])) / (1 + Number(price[`com_${_key}`]) / 100);
                        }
                    } else {
                        if (markup_is_percentage) {
                            if (state.margin_master_markup) {
                                price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) * (1 - Number(price[`markup_${_key}`]) / 100);
                            } else {
                                price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`])) / (1 + Number(price[`markup_${_key}`]) / 100);
                            }
                            // price[_key] = (Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`]) - Number(price[`com_${_key}`]) * Number(price[`markup_${_key}`]) / 100) * 100 / (100 + Number(price[`markup_${_key}`]))
                        } else {
                            price[_key] = Number(price[`selling_${_key}`]) - Number(price[`com_${_key}`]) - Number(price[`markup_${_key}`]);
                        }
                    }
                }
            }
        }
        for (const prop in price) {
            if (typeof price[prop] === 'number') {
                price[prop] = Number(price[prop].toFixed(2));
            }
        }
    }

    room_prices[index] = price;
    return { ...state, room_prices: room_prices };
};

const updateRoomPricePeriodProperty = (
    value: any,
    price_index: number,
    index: number,
    state: ManualAccommodationState,
    key: string
) => {
    const room_prices = [...state.room_prices];
    const price = { ...room_prices[price_index] };
    const periods = [...price.periods];
    const period = { ...periods[index] };
    period[key] = value;
    periods[index] = period;
    price.periods = periods;
    room_prices[price_index] = price;
    return { ...state, room_prices: room_prices };
};

const addCancellationRuleConditionReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_conditions: [
        ...state.cancellation_rule_conditions,
        { ...EMPTY_CANCELLATION_CONDITION }
    ]
});

const addEarlyBookingConditionReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    early_booking_conditions: [
        ...state.early_booking_conditions,
        EMPTY_EARLY_BOOKING_CONDITION
    ]
});

const addExtraIntervalAgePricesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price_index: number }>
): ManualAccommodationState => {
    const extra_prices = cloneDeep(state.extra_prices);
    extra_prices[action.payload.price_index] = {
        ...extra_prices[action.payload.price_index],
        interval_age_prices: [
            ...extra_prices[action.payload.price_index].interval_age_prices,
            ...EMPTY_INTERVAL_AGE_OFFERS
        ]
    };
    return { ...state, extra_prices };
};

const addExtraPriceReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    extra_prices: [...state.extra_prices, EMPTY_EXTRA_PRICE]
});

const addOnContractsReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    contract_contracts: [...state.contract_contracts, null],
    contract_providers: [...state.contract_providers, null]
});

const addPreferencesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Preferences[]>
): ManualAccommodationState => ({
    ...state,
    preferences: [...state.preferences, ...action.payload]
});
const addRoomBeddingReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_bedding: [...state.room_bedding, { ...EMPTY_ROOM_BEDDING }]
});

const addRoomBlackOutReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_black_outs: [...state.room_black_outs, { ...EMPTY_ROOM_BLACK_OUT }]
});

const addRoomMinStayPeriodReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_min_stay_periods: [
        ...state.room_min_stay_periods,
        { ...EMPTY_ROOM_MIN_STAY_PERIOD }
    ]
});

const addRoomPriceReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_price_scroll_id: `manual-accommodation-room-price-${[...state.room_prices, { ...state.empty_room_price }].length - 1}`,
    room_prices: [...state.room_prices, { ...state.empty_room_price }]
});

const addRoomPricePeriodReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => {
    const room_prices = [...state.room_prices];
    const price = { ...room_prices[action.payload] };
    const periods = [...price.periods];
    periods.push({ ...EMPTY_ROOM_PRICE_PERIOD });
    price.periods = periods;
    room_prices[action.payload] = price;
    return { ...state, room_prices: room_prices };
};

const addRoomProhibitedCompositionReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_prohibited_compositions: [
        ...state.room_prohibited_compositions,
        [0, 0]
    ]
});

const addYieldRuleReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    yield_rules: [...state.yield_rules, { ...EMPTY_YELD_RULE }]
});

const deleteCancellationRuleConditionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_conditions: state.cancellation_rule_conditions.filter(
        (element, index) => index !== action.payload
    )
});

const deleteCustomPictureReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    custom_pictures: state.custom_pictures.filter(
        (element, index) => index !== action.payload
    )
});

const deleteEarlyBookingConditionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    early_booking_conditions: state.early_booking_conditions.filter(
        (element, index) => index !== action.payload
    )
});

const deleteExtraIntervalAgePricesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; price_index: number }>
): ManualAccommodationState => {
    const extra_prices = cloneDeep(state.extra_prices);
    extra_prices[action.payload.price_index] = {
        ...extra_prices[action.payload.price_index],
        interval_age_prices: extra_prices[
            action.payload.price_index
        ].interval_age_prices.filter(
            (element, index) => index !== action.payload.index
        )
    };
    return { ...state, extra_prices };
};

const deleteExtraPriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    extra_prices: state.extra_prices.filter(
        (element, index) => index !== action.payload
    )
});

const deleteOnContractsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    contract_contracts: state.contract_contracts.filter(
        (element, index) => index !== action.payload
    ),
    contract_providers: state.contract_providers.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomBeddingReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_bedding: state.room_bedding.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomBlackOutReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_black_outs: state.room_black_outs.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomMinStayPeriodReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_min_stay_periods: state.room_min_stay_periods.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomPictureReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_pictures: state.room_pictures.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomPriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_prices: state.room_prices.filter(
        (element, index) => index !== action.payload
    )
});

const deleteRoomPricePeriodReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price_index: number; period_index: number }>
): ManualAccommodationState => {
    const { price_index, period_index } = action.payload;
    const room_prices = [...state.room_prices];
    const price = { ...room_prices[price_index] };
    const periods = [...price.periods];
    periods.splice(period_index, 1);
    price.periods = periods;
    room_prices[price_index] = price;
    return { ...state, room_prices: room_prices };
};

const deleteRoomProhibitedCompositionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_prohibited_compositions: state.room_prohibited_compositions.filter(
        (element, index) => index !== action.payload
    )
});

const deleteYieldRuleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    yield_rules: state.yield_rules.filter(
        (element, index) => index !== action.payload
    )
});

const moveDownColumnReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    columns: state.columns?.map((column, index) =>
        index === action.payload + 1
            ? { ...state.columns[action.payload], last_updated: true }
            : index === action.payload
                ? { ...state.columns[action.payload + 1], last_updated: false }
                : { ...column, last_updated: false }
    )
});

const moveUpColumnReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    columns: state.columns?.map((column, index) =>
        index === action.payload - 1
            ? { ...state.columns[action.payload], last_updated: true }
            : index === action.payload
                ? { ...state.columns[action.payload - 1], last_updated: false }
                : { ...column, last_updated: false }
    )
});

const refreshCancellationRuleSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    cancellation_rule_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.cancellation_rule_list,
            state.cancellation_rule_criteria.search || null,
            action.payload
        ),
        state.cancellation_rule_criteria.order || 'asc',
        state.cancellation_rule_criteria.order_by || '',
        action.payload
    )
});

const refreshEarlyBookingSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    early_booking_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.early_booking_list,
            state.early_booking_criteria.search || null,
            action.payload
        ),
        state.early_booking_criteria.order || 'asc',
        state.early_booking_criteria.order_by || '',
        action.payload
    )
});

const refreshExtraSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    extra_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.extra_list,
            state.extra_criteria.search || null,
            action.payload
        ),
        state.extra_criteria.order || 'asc',
        state.extra_criteria.order_by || '',
        action.payload
    )
});

const refreshOfferSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    offer_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.offer_list,
            state.offer_criteria.search || null,
            action.payload
        ),
        state.offer_criteria.order || 'asc',
        state.offer_criteria.order_by || '',
        action.payload
    )
});

const refreshRoomSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    room_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.room_list,
            state.room_criteria.search || null,
            action.payload
        ),
        state.room_criteria.order || 'asc',
        state.room_criteria.order_by || '',
        action.payload
    )
});

const refreshYieldSortedListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | null>
) => ({
    ...state,
    yield_sorted_list: sortAccommodationArray(
        searchOnLocalizationNameAndReference(
            state.yield_list,
            state.yield_criteria.search || null,
            action.payload
        ),
        state.yield_criteria.order || 'asc',
        state.yield_criteria.order_by || '',
        action.payload
    )
});

const resetContractsReducer = (state: ManualAccommodationState) => ({
    ...state,
    contract_contracts: [null],
    contract_providers: [null]
});

const resetCreateReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    create: false,
    create_name: '',
    create_search_city: '',
    create_search_country: null,
    create_search_name: '',
    create_search_results: null,
    create_search_selected: -1,
    create_tab: 0
});

const resetFiltersReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    filter_address: '',
    filter_chain: null,
    filter_country: null,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_destination: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_name: '',
    filter_tags: [],
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_status: 'ALL'
});

const resetManualAccommodationReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    address: '',
    address_error: '',
    booking_type: 0,
    chain: null,
    check_in_hour: '00',
    check_in_minutes: '00',
    check_in_recommendation: '',
    check_in_recommendation_error: '',
    check_in_recommendation_locales: {},
    check_out_hour: '00',
    check_out_minutes: '00',
    check_out_recommendation: '',
    check_out_recommendation_error: '',
    check_out_recommendation_locales: {},
    city: '',
    city_error: '',
    client_hotel_identifier: '',
    is_protected_description: false,
    comfort_rate: 0,
    contact_create: false,
    contact_delete: null,
    contact_edit: null,
    contact_email: '',
    contact_email_error: '',
    contact_first_name: '',
    contact_first_name_error: '',
    contact_job: '',
    contact_job_error: '',
    contact_last_name: '',
    contact_last_name_error: '',
    contact_phone_number: '',
    contact_phone_number_error: '',
    contract_create: false,
    contract_contract: null,
    contract_contract_error: '',
    contract_provider: null,
    contract_selected: null,
    country: null,
    create_name: '',
    create_name_error: '',
    create_search_name: '',
    create_search_city: '',
    create_search_country: null,
    create_search_results: null,
    create_search_selected: -1,
    create_tab: 0,
    edit: null,
    edit_step: 'INFO',
    email: '',
    email_error: '',
    extra_create: false,
    extra_delete: null,
    extra_duplicate: null,
    extra_edit: null,
    extra_conflicts: [],
    extra_list: [],
    extra_sorted_list: [],
    extra_criteria: {
        order_by: 'name',
        search: null,
        order: 'asc'
    },
    extra_description: '',
    extra_description_error: '',
    extra_description_locales: {},
    extra_included: false,
    extra_per_day: false,
    extra_per_person: true,
    extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
    extra_maximal_age: '',
    extra_meal_plan: [],
    extra_minimal_age: '',
    extra_minimal_age_error: '',
    extra_minimal_travelers: '',
    extra_name: '',
    extra_name_error: '',
    extra_name_locales: {},
    extra_reference: '',
    extra_reference_error: '',
    extra_transfer: false,
    extra_type: [0],
    family_plan: '',
    family_plan_max: '',
    pricing_type: PricingType.PURCHASE,
    com_calculation_as_percentage: false,
    global_com: '',
    global_markup: '',
    com_is_percentage: false,
    markup_is_percentage: false,
    fax: '',
    fax_error: '',
    google_id: '',
    google_id_error: '',
    house_number: '',
    house_number_error: '',
    latitude: '',
    latitude_error: '',
    longitude: '',
    longitude_error: '',
    video_link: '',
    manual_accommodation: null,
    max_age_baby: '',
    max_age_baby_error: '',
    min_age_adult: '',
    name: '',
    name_error: '',
    client_hotel_identifier_error: '',
    phone_number: '',
    phone_number_error: '',
    providers: [],
    recommendation: 0,
    restoration_rate: 0,
    room_create: false,
    room_delete: null,
    room_edit: null,
    room_list: [],
    situation_rate: 0,
    stars: 0,
    superior_stars: false,
    display_for_trip_plan: true,
    display_for_package: false,
    type: 16,
    website: '',
    website_error: '',
    zip_code: '',
    zip_code_error: '',
    lock: false,
    publish_on_web: false,
    destination_name: '',
    zoom: 4
});

const setContractParametersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ContractParameters>
): ManualAccommodationState => ({
    ...state,
    max_age_baby:
        action.payload.max_age_baby !== null ? action.payload.max_age_baby : '',
    max_age_child:
        action.payload.max_age_child !== null
            ? action.payload.max_age_child
            : '',
    max_age_teen:
        action.payload.max_age_teen !== null ? action.payload.max_age_teen : '',
    booking_type: action.payload.booking_type,
    is_teen_adult_in_monoparental: action.payload.is_teen_adult_in_monoparental,
    family_plan:
        action.payload.family_plan !== null ? action.payload.family_plan : '',
    family_plan_max:
        action.payload.family_plan_max !== null
            ? action.payload.family_plan_max
            : '',
    pricing_type: action.payload.pricing_type || PricingType.PURCHASE,
    com_calculation_as_percentage: action.payload.com_calculation_as_percentage,
    com_applied_on_ttc: !!action.payload.com_applied_on_ttc,
    global_com: action.payload.global_com || '',
    global_markup: action.payload.global_markup || '',
    com_is_percentage: !!action.payload.com_is_percentage,
    markup_is_percentage: !!action.payload.markup_is_percentage,
    manual_accommodation: state.manual_accommodation
        ? {
            ...state.manual_accommodation,
            contract_parameters: state.manual_accommodation
                ?.contract_parameters
                ? ({
                    ...state.manual_accommodation?.contract_parameters,
                    max_age_baby:
                        action.payload.max_age_baby !== null
                            ? action.payload.max_age_baby
                            : '',
                    max_age_child:
                        action.payload.max_age_child !== null
                            ? action.payload.max_age_child
                            : '',
                    max_age_teen:
                        action.payload.max_age_teen !== null
                            ? action.payload.max_age_teen
                            : '',
                    booking_type: action.payload.booking_type,
                    is_teen_adult_in_monoparental:
                        action.payload.is_teen_adult_in_monoparental,
                    family_plan:
                        action.payload.family_plan !== null
                            ? action.payload.family_plan
                            : '',
                    family_plan_max:
                        action.payload.family_plan_max !== null
                            ? action.payload.family_plan_max
                            : '',
                    pricing_type: action.payload.pricing_type || PricingType.PURCHASE,
                    com_calculation_as_percentage: action.payload.com_calculation_as_percentage,
                    com_applied_on_ttc: !!action.payload.com_applied_on_ttc,
                    global_com: action.payload.global_com || '',
                    global_markup: action.payload.global_markup || '',
                    com_is_percentage: !!action.payload.com_is_percentage,
                    markup_is_percentage: !!action.payload.markup_is_percentage,
                } as ContractParameters)
                : ({
                    max_age_baby:
                        action.payload.max_age_baby !== null
                            ? action.payload.max_age_baby
                            : '',
                    max_age_child:
                        action.payload.max_age_child !== null
                            ? action.payload.max_age_child
                            : '',
                    max_age_teen:
                        action.payload.max_age_teen !== null
                            ? action.payload.max_age_teen
                            : '',
                    booking_type: action.payload.booking_type,
                    is_teen_adult_in_monoparental:
                        action.payload.is_teen_adult_in_monoparental,
                    family_plan:
                        action.payload.family_plan !== null
                            ? action.payload.family_plan
                            : '',
                    family_plan_max:
                        action.payload.family_plan_max !== null
                            ? action.payload.family_plan_max
                            : '',
                    pricing_type: action.payload.pricing_type || PricingType.PURCHASE,
                    com_calculation_as_percentage: action.payload.com_calculation_as_percentage,
                    com_applied_on_ttc: !!action.payload.com_applied_on_ttc,
                    global_com: action.payload.global_com || '',
                    global_markup: action.payload.global_markup || '',
                    com_is_percentage: !!action.payload.com_is_percentage,
                    markup_is_percentage: !!action.payload.markup_is_percentage,
                } as ContractParameters)
        }
        : null
});

const setEditReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<
        | null
        | 'INFO'
        | 'COMPLEMENTARY_INFO'
        | 'SITUATION'
        | 'RESTORATION'
        | 'ROOMS_DESCRIPTION'
        | 'CHILDREN_INFORMATIONS'
        | 'EQUIPMENTS'
        | 'LOCATION'
        | 'PICTURES'
        | 'PICTURES_ORDER'
        | 'VIDEO'
        | 'TAG'
        | 'FILES'
    >
): ManualAccommodationState => {
    if (state.manual_accommodation !== null) {
        let check_in_hour = '00';
        let check_in_minutes = '00';
        if (state.manual_accommodation.check_in !== null) {
            const check_in = state.manual_accommodation.check_in.split(':');
            if (check_in[0] !== undefined && check_in[0].length === 2) {
                check_in_hour = check_in[0];
            }
            if (check_in[1] !== undefined && check_in[1].length === 2) {
                check_in_minutes = check_in[1];
            }
        }
        let check_out_hour = '00';
        let check_out_minutes = '00';
        if (state.manual_accommodation.check_out !== null) {
            const check_out = state.manual_accommodation.check_out.split(':');
            if (check_out[0] !== undefined && check_out[0].length === 2) {
                check_out_hour = check_out[0];
            }
            if (check_out[1] !== undefined && check_out[1].length === 2) {
                check_out_minutes = check_out[1];
            }
        }
        const providers = [...state.manual_accommodation.providers].map(
            (provider) => {
                const res: Contract[] = [];
                const contracts = state?.manual_accommodation?.contracts
                    ? [...state?.manual_accommodation?.contracts]
                    : [];
                for (let i = 0; i < contracts.length; i++) {
                    if (provider.id === contracts[i]?.provider?.id) {
                        res.push({ ...contracts[i] });
                    }
                }
                return { ...provider, contracts: res };
            }
        );
        const check_in_recommendation_locales: {} & {
            [index: string]: any;
        } = {};
        const check_out_recommendation_locales: {} & {
            [index: string]: any;
        } = {};
        const situation_locales: {} & { [index: string]: any } = {};
        const restoration_locales: {} & { [index: string]: any } = {};
        const children_locales: {} & { [index: string]: any } = {};
        const room_desc_locales: {} & { [index: string]: any } = {};
        const info_locales: {} & { [index: string]: any } = {};
        let has_french = false;
        state.manual_accommodation.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            check_in_recommendation_locales[locale.locale] =
                locale.check_in_recommendation;
            check_out_recommendation_locales[locale.locale] =
                locale.check_out_recommendation;
            situation_locales[locale.locale] = locale.situation;
            restoration_locales[locale.locale] = locale.restoration;
            children_locales[locale.locale] = locale.children;
            room_desc_locales[locale.locale] = locale.room_desc;
            info_locales[locale.locale] = locale.info;
        });
        if (!has_french) {
            check_in_recommendation_locales['1'] =
                state.manual_accommodation.check_in_recommendation !== null
                    ? state.manual_accommodation.check_in_recommendation
                    : '';
            check_out_recommendation_locales['1'] =
                state.manual_accommodation.check_out_recommendation !== null
                    ? state.manual_accommodation.check_out_recommendation
                    : '';
            situation_locales['1'] =
                state.manual_accommodation.situation !== null
                    ? state.manual_accommodation.situation
                    : '';
            restoration_locales['1'] =
                state.manual_accommodation.restoration !== null
                    ? state.manual_accommodation.restoration
                    : '';
            children_locales['1'] =
                state.manual_accommodation.children !== null
                    ? state.manual_accommodation.children
                    : '';
            room_desc_locales['1'] =
                state.manual_accommodation.room_desc !== null
                    ? state.manual_accommodation.room_desc
                    : '';
            info_locales['1'] = '';
        }
        const custom_pictures: Picture[] = [];
        const giata_pictures: (Picture & { activated: boolean })[] = [];
        if (state.manual_accommodation.custom_pictures !== null) {
            state.manual_accommodation.custom_pictures.map((custom_picture) => {
                let activated = false;
                if (state.manual_accommodation?.pictures !== null) {
                    state.manual_accommodation?.pictures.map((picture) => {
                        if (custom_picture.id === picture.id) {
                            activated = true;
                        }
                    });
                }
                if (!activated) {
                    custom_pictures.push({ ...custom_picture });
                }
            });
        }
        if (state.manual_accommodation.pictures !== null) {
            state.manual_accommodation.pictures.map((picture) => {
                let activated = false;
                if (state.manual_accommodation?.custom_pictures !== null) {
                    state.manual_accommodation?.custom_pictures.map(
                        (custom_picture) => {
                            if (custom_picture.id === picture.id) {
                                activated = true;
                            }
                        }
                    );
                }
                giata_pictures.push({
                    ...picture,
                    activated: activated
                });
            });
        }
        const edit_facts = [...EMPTY_FACTS].map((fact) => ({
            ...fact,
            value_bool: !!state.manual_accommodation?.facts.some(
                (e) => e.codename === fact.codename
            )
        }));
        return {
            ...state,
            edit: action.payload,
            address:
                state.manual_accommodation.street !== null
                    ? state.manual_accommodation.street
                    : '',
            address_error: '',
            address_search: null,
            best_offer_limit: state.manual_accommodation?.best_offer_limit || 0,
            best_offer_order:
                state.manual_accommodation?.best_offer_order ||
                'BEST_REDUCTION',
            booking_type: state.manual_accommodation.booking_type,
            children_locales: children_locales,
            city:
                state.manual_accommodation.city_name !== null
                    ? state.manual_accommodation.city_name
                    : '',
            city_error: '',
            country: state.manual_accommodation.country,
            chain: state.manual_accommodation.chain,
            check_in_hour: check_in_hour,
            check_in_minutes: check_in_minutes,
            check_in_recommendation:
                state.manual_accommodation.check_in_recommendation,
            check_in_recommendation_locales: check_in_recommendation_locales,
            check_out_hour: check_out_hour,
            check_out_minutes: check_out_minutes,
            check_out_recommendation:
                state.manual_accommodation.check_out_recommendation,
            check_out_recommendation_locales: check_out_recommendation_locales,
            custom_pictures: custom_pictures,
            custom_pictures_order:
                state.manual_accommodation.custom_pictures !== null
                    ? [...state.manual_accommodation.custom_pictures]
                    : [],
            comfort_rate:
                state.manual_accommodation.comfort !== null
                    ? state.manual_accommodation.comfort
                    : 0,
            email:
                state.manual_accommodation.email !== null
                    ? state.manual_accommodation.email
                    : '',
            email_error: '',
            facts: edit_facts,
            family_plan:
                state.manual_accommodation.family_plan !== null
                    ? state.manual_accommodation.family_plan
                    : '',
            family_plan_max:
                state.manual_accommodation.family_plan_max !== null
                    ? state.manual_accommodation.family_plan_max
                    : '',
            fax:
                state.manual_accommodation.fax !== null
                    ? state.manual_accommodation.fax
                    : '',
            fax_error: '',
            giata_pictures: giata_pictures,
            google_id:
                state.manual_accommodation.google_id !== null
                    ? state.manual_accommodation.google_id
                    : '',
            google_id_error: '',
            house_number:
                state.manual_accommodation.house_number !== null
                    ? state.manual_accommodation.house_number
                    : '',
            house_number_error: '',
            info_locales: info_locales,
            latitude:
                state.manual_accommodation.latitude !== null
                    ? state.manual_accommodation.latitude
                    : '',
            latitude_error: '',
            longitude:
                state.manual_accommodation.longitude !== null
                    ? state.manual_accommodation.longitude
                    : '',
            longitude_error: '',
            video_link: state.manual_accommodation.video_link || '',
            max_age_baby:
                state.manual_accommodation.max_age_baby !== null
                    ? state.manual_accommodation.max_age_baby
                    : '',
            min_age_adult:
                state.manual_accommodation.min_age_adult !== null
                    ? state.manual_accommodation.min_age_adult
                    : '',
            name:
                state.manual_accommodation.name !== null
                    ? state.manual_accommodation.name
                    : '',
            client_hotel_identifier:
                state.manual_accommodation.client_hotel_identifier || '',
            is_protected_description:
                !!state.manual_accommodation.is_protected_description,
            name_error: '',
            client_hotel_identifier_error: '',
            phone_number:
                state.manual_accommodation.phone_number !== null
                    ? state.manual_accommodation.phone_number
                    : '',
            phone_number_error: '',
            providers: providers,
            recommendation:
                !state.manual_accommodation.blacklist &&
                    !state.manual_accommodation.favorite
                    ? 0
                    : state.manual_accommodation.favorite
                        ? 1
                        : 2,
            restoration_locales: restoration_locales,
            restoration_rate:
                state.manual_accommodation.restaurant !== null
                    ? state.manual_accommodation.restaurant
                    : 0,
            room_desc_locales: room_desc_locales,
            situation_locales: situation_locales,
            situation_rate:
                state.manual_accommodation.situation_select !== null
                    ? state.manual_accommodation.situation_select
                    : 0,
            stars:
                state.manual_accommodation.stars !== null
                    ? state.manual_accommodation.stars
                    : 0,
            superior_stars: state.manual_accommodation.hotel_sup,
            display_for_trip_plan: state.manual_accommodation.display_for_trip_plan,
            display_for_package: state.manual_accommodation.display_for_package,
            type: state.manual_accommodation.type,
            website:
                state.manual_accommodation.website !== null
                    ? state.manual_accommodation.website
                    : '',
            website_error: '',
            zip_code:
                state.manual_accommodation.zip_code !== null
                    ? state.manual_accommodation.zip_code
                    : '',
            lock: !!state.manual_accommodation.lock,
            publish_on_web: !!state.manual_accommodation.publish_on_web,
            zip_code_error: '',
            destination_name: state.manual_accommodation.destination_name !== null
                ? state.manual_accommodation.destination_name
                : '',
        };
    }
    return state;
};

const setCancellationRuleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | CancellationRule>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            cancellation_rule_create: true,
            cancellation_rule_edit: null,
            cancellation_rule_name: '',
            cancellation_rule_name_locales: {},
            cancellation_rule_name_error: '',
            cancellation_rule_reference: '',
            cancellation_rule_reference_error: '',
            cancellation_rule_start_date: null,
            cancellation_rule_start_date_error: '',
            cancellation_rule_end_date: null,
            cancellation_rule_end_date_error: '',
            cancellation_rule_free: 0,
            cancellation_rule_conditions: [{ ...EMPTY_CANCELLATION_CONDITION }]
        };
    } else if (action.payload === false) {
        return {
            ...state,
            cancellation_rule_create: false,
            cancellation_rule_edit: null,
            cancellation_rule_name: '',
            cancellation_rule_name_locales: {},
            cancellation_rule_name_error: '',
            cancellation_rule_reference: '',
            cancellation_rule_reference_error: '',
            cancellation_rule_start_date: null,
            cancellation_rule_start_date_error: '',
            cancellation_rule_end_date: null,
            cancellation_rule_end_date_error: '',
            cancellation_rule_free: 0,
            cancellation_rule_conditions: [{ ...EMPTY_CANCELLATION_CONDITION }]
        };
    } else if (state.cancellation_rule_create) {
        const cancellation_rule_list = [...state.cancellation_rule_list];
        cancellation_rule_list.push(action.payload);
        return {
            ...state,
            cancellation_rule_create: false,
            cancellation_rule_edit: null,
            cancellation_rule_list: cancellation_rule_list,
            cancellation_rule_name: '',
            cancellation_rule_name_locales: {},
            cancellation_rule_name_error: '',
            cancellation_rule_reference: '',
            cancellation_rule_reference_error: '',
            cancellation_rule_start_date: null,
            cancellation_rule_start_date_error: '',
            cancellation_rule_end_date: null,
            cancellation_rule_end_date_error: '',
            cancellation_rule_free: 0,
            cancellation_rule_conditions: [{ ...EMPTY_CANCELLATION_CONDITION }]
        };
    } else if (state.cancellation_rule_edit) {
        const cancellation_rule_list = [...state.cancellation_rule_list];
        for (let i = 0; i < cancellation_rule_list.length; i++) {
            if (
                cancellation_rule_list[i].id === state.cancellation_rule_edit.id
            ) {
                cancellation_rule_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            cancellation_rule_create: false,
            cancellation_rule_edit: null,
            cancellation_rule_list: cancellation_rule_list,
            cancellation_rule_name: '',
            cancellation_rule_name_locales: {},
            cancellation_rule_name_error: '',
            cancellation_rule_reference: '',
            cancellation_rule_reference_error: '',
            cancellation_rule_start_date: null,
            cancellation_rule_start_date_error: '',
            cancellation_rule_end_date: null,
            cancellation_rule_end_date_error: '',
            cancellation_rule_free: 0,
            cancellation_rule_conditions: [{ ...EMPTY_CANCELLATION_CONDITION }]
        };
    } else {
        const cancellation_rule_name_locales: {} & {
            [index: string]: any;
        } = {};
        let has_french = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            cancellation_rule_name_locales[locale.locale] = locale.name;
        });
        if (!has_french) {
            cancellation_rule_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
        }
        return {
            ...state,
            cancellation_rule_create: false,
            cancellation_rule_edit: action.payload,
            cancellation_rule_name:
                action.payload.name !== null ? action.payload.name : '',
            cancellation_rule_name_locales: cancellation_rule_name_locales,
            cancellation_rule_name_error: '',
            cancellation_rule_reference:
                action.payload.reference !== null
                    ? action.payload.reference
                    : '',
            cancellation_rule_reference_error: '',
            cancellation_rule_start_date:
                action.payload.start_date !== null
                    ? new Date(action.payload.start_date)
                    : null,
            cancellation_rule_start_date_error: '',
            cancellation_rule_end_date:
                action.payload.end_date !== null
                    ? new Date(action.payload.end_date)
                    : null,
            cancellation_rule_end_date_error: '',
            cancellation_rule_free: action.payload.free_cancel,
            cancellation_rule_conditions: action.payload.rules
        };
    }
};

const setCancellationRuleDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | CancellationRule>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_delete: action.payload === false ? null : action.payload,
    cancellation_rule_list:
        action.payload === null && state.cancellation_rule_delete !== null
            ? state.cancellation_rule_list.filter(
                (element, index) =>
                    element.id !==
                    (state.cancellation_rule_delete as CancellationRule)?.id
            )
            : state.cancellation_rule_list
});

const setCancellationRuleCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_criteria: action.payload
});

const setCancellationRuleDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | CancellationRule>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_duplicate:
        state.manual_accommodation_duplicate_type === 'different_different' ||
            (state.manual_accommodation_duplicate_type === 'same_different' &&
                state.manual_accommodation_duplicate_contract_to?.id !==
                state.contract_selected?.id) ||
            (state.cancellation_rule_duplicate !== null && action.payload !== null)
            ? null
            : action.payload,
    cancellation_rule_list:
        state.cancellation_rule_duplicate !== null && action.payload !== null
            ? [...state.cancellation_rule_list, action.payload]
            : state.cancellation_rule_list
});

const setCancellationRuleListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<CancellationRule[]>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_list: action.payload
});

const setContactDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Contact | null>
): ManualAccommodationState => ({
    ...state,
    contact_delete: action.payload
});

const setEditStepReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<
        | 'INFO'
        | 'CONTRACTS'
        | 'CONTACTS'
        | 'ROOMS'
        | 'PRICES_STOCKS'
        | 'STOCKS_STATE'
        | 'ROOM_BLACK_OUTS'
        | 'CONTRACT_PARAMETERS'
        | 'EXTRAS'
        | 'CANCELLATION_RULES'
        | 'OFFERS'
        | 'YIELDS'
        | 'EARLY_BOOKINGS'
        | 'TAXES'
        | 'SIMULATIONS'
    >
): ManualAccommodationState => ({
    ...state,
    edit_step: action.payload
});

const setErrorsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Error[]>
): ManualAccommodationState => {
    const errors = action.payload;
    const new_state: ManualAccommodationState = { ...state };
    errors.map((error) => {
        if (error.subkey !== undefined && error.subkey_index !== undefined) {
            if (
                error.subsubkey !== undefined &&
                error.subsubkey_index !== undefined
            ) {
                const room_prices = [...new_state.room_prices];
                const price = { ...room_prices[error.subkey_index] };
                const periods = [...price.periods];
                const period = { ...periods[error.subsubkey_index] };
                period[error.subsubkey] = error.error;
                periods[error.subsubkey_index] = period;
                price.periods = periods;
                room_prices[error.subkey_index] = price;
                new_state.room_prices = room_prices;
            } else {
                const sub_property = [...new_state[error.key]];
                const property = {
                    ...sub_property[error.subkey_index]
                };
                property[error.subkey] = error.error;
                sub_property[error.subkey_index] = property;
                new_state[error.key] = sub_property;
            }
        } else {
            new_state[error.key] = error.error;
        }
    });
    return { ...new_state };
};


const setExportTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'CSV' | 'PDF'>
): ManualAccommodationState => ({
    ...state,
    export_type: action.payload
});

const setEarlyBookingReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | EarlyBooking>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            early_booking_create: true,
            early_booking_edit: null,
            early_booking_conditions: [{ ...EMPTY_EARLY_BOOKING_CONDITION }],
            early_booking_name_locales: {},
            early_booking_name_locales_error: '',
            early_booking_description_locales: {},
            early_booking_description_locales_error: '',
            early_booking_end_date: null,
            early_booking_end_date_error: '',
            early_booking_extra: [],
            early_booking_extra_conflicts: [],
            early_booking_offer_conflicts: [],
            early_booking_reference: '',
            early_booking_reference_error: '',
            early_booking_min_days: '',
            early_booking_max_days: '',
            early_booking_start_date: null,
            early_booking_start_date_error: ''
        };
    } else if (action.payload === false) {
        return {
            ...state,
            early_booking_create: false,
            early_booking_edit: null,
            early_booking_conditions: [{ ...EMPTY_EARLY_BOOKING_CONDITION }],
            early_booking_name_locales: {},
            early_booking_name_locales_error: '',
            early_booking_description_locales: {},
            early_booking_description_locales_error: '',
            early_booking_end_date: null,
            early_booking_end_date_error: '',
            early_booking_extra: [],
            early_booking_extra_conflicts: [],
            early_booking_offer_conflicts: [],
            early_booking_reference: '',
            early_booking_reference_error: '',
            early_booking_min_days: '',
            early_booking_max_days: '',
            early_booking_start_date: null,
            early_booking_start_date_error: ''
        };
    } else if (state.early_booking_create) {
        const early_booking_list = [...state.early_booking_list];
        early_booking_list.push(action.payload);
        return {
            ...state,
            early_booking_create: false,
            early_booking_edit: null,
            early_booking_list: early_booking_list,
            early_booking_conditions: [{ ...EMPTY_EARLY_BOOKING_CONDITION }],
            early_booking_name_locales: {},
            early_booking_name_locales_error: '',
            early_booking_description_locales: {},
            early_booking_description_locales_error: '',
            early_booking_end_date: null,
            early_booking_end_date_error: '',
            early_booking_extra: [],
            early_booking_extra_conflicts: [],
            early_booking_offer_conflicts: [],
            early_booking_reference: '',
            early_booking_reference_error: '',
            early_booking_min_days: '',
            early_booking_max_days: '',
            early_booking_start_date: null,
            early_booking_start_date_error: ''
        };
    } else if (state.early_booking_edit) {
        const early_booking_list = [...state.early_booking_list];
        for (let i = 0; i < early_booking_list.length; i++) {
            if (early_booking_list[i].id === state.early_booking_edit.id) {
                early_booking_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            early_booking_create: false,
            early_booking_edit: null,
            early_booking_list: early_booking_list,
            early_booking_conditions: [{ ...EMPTY_EARLY_BOOKING_CONDITION }],
            early_booking_name_locales: {},
            early_booking_name_locales_error: '',
            early_booking_description_locales: {},
            early_booking_description_locales_error: '',
            early_booking_end_date: null,
            early_booking_end_date_error: '',
            early_booking_extra: [],
            early_booking_reference: '',
            early_booking_reference_error: '',
            early_booking_min_days: '',
            early_booking_max_days: '',
            early_booking_start_date: null,
            early_booking_start_date_error: ''
        };
    } else {
        const early_booking_name_locales: {} & {
            [index: string]: any;
        } = {};
        const early_booking_description_locales: {} & {
            [index: string]: any;
        } = {};
        let has_french = false;
        let has_french_early_booking_description_locales = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            early_booking_name_locales[locale.locale] = locale.name;
            early_booking_description_locales[locale.locale] = locale.description;
        });
        if (!has_french) {
            early_booking_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
        }
        if (!has_french) {
            early_booking_description_locales['1'] =
                action.payload.description ? action.payload.description : '';
        }
        const early_booking_conditions: EarlyBookingRule[] = [];
        action.payload.rules.map((rule) => {
            early_booking_conditions.push({
                deduction: rule.value,
                deduction_error: '',
                deduction_type: rule.price_type,
                days: rule.nb_days !== null ? rule.nb_days : '',
                days_error: '',
                max_date: rule.limit_date ? new Date(rule.limit_date) : null,
                max_date_error: '',
                type: rule.limit_type
            });
        });
        const early_booking_extra_conflicts: Extra[] = [];
        state.extra_list.map((extra) => {
            if (
                (
                    (action.payload as EarlyBooking).extra_conflicts as number[]
                ).includes(extra.id)
            ) {
                early_booking_extra_conflicts.push({ ...extra });
            }
        });
        const early_booking_offer_conflicts: Offer[] = [];
        state.offer_list.map((offer) => {
            if (
                (
                    (action.payload as EarlyBooking).offer_conflicts as number[]
                ).includes(offer.id)
            ) {
                early_booking_offer_conflicts.push({ ...offer });
            }
        });
        return {
            ...state,
            early_booking_create: false,
            early_booking_edit: action.payload,
            early_booking_conditions: early_booking_conditions,
            early_booking_name_locales: early_booking_name_locales,
            early_booking_name_locales_error: '',
            early_booking_description_locales: early_booking_description_locales,
            early_booking_description_locales_error: '',
            early_booking_reference: action.payload.reference,
            early_booking_reference_error: '',
            early_booking_min_days: action.payload.min_days || '',
            early_booking_max_days: action.payload.max_days || '',
            early_booking_end_date:
                action.payload.end_date !== null
                    ? new Date(action.payload.end_date)
                    : null,
            early_booking_end_date_error: '',
            early_booking_extra: action.payload.extra_rules,
            early_booking_extra_conflicts: early_booking_extra_conflicts,
            early_booking_offer_conflicts: early_booking_offer_conflicts,
            early_booking_start_date:
                action.payload.start_date !== null
                    ? new Date(action.payload.start_date)
                    : null,
            early_booking_start_date_error: ''
        };
    }
};

const setEarlyBookingCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    early_booking_criteria: action.payload
});

const setEarlyBookingDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | EarlyBooking>
): ManualAccommodationState => ({
    ...state,
    early_booking_delete: action.payload === false ? null : action.payload,
    early_booking_list:
        action.payload === null && state.early_booking_delete !== null
            ? state.early_booking_list.filter(
                (element, index) =>
                    element.id !==
                    (state.early_booking_delete as EarlyBooking)?.id
            )
            : state.early_booking_list,
    room_list: action.payload === null && state.early_booking_delete !== null
        ? state.room_list.map((room) => ({ ...room, early_booking: room.early_booking.filter((early) => early.id !== (state.early_booking_delete as EarlyBooking)?.id) }))
        : state.room_list
});

const setEarlyBookingDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | EarlyBooking>
): ManualAccommodationState => ({
    ...state,
    early_booking_duplicate:
        state.manual_accommodation_duplicate_type === 'different_different' ||
            (state.manual_accommodation_duplicate_type === 'same_different' &&
                state.manual_accommodation_duplicate_contract_to?.id !==
                state.contract_selected?.id) ||
            (state.early_booking_duplicate !== null && action.payload !== null)
            ? null
            : action.payload,
    early_booking_list:
        state.early_booking_duplicate !== null && action.payload !== null
            ? [...state.early_booking_list, action.payload]
            : state.early_booking_list
});

const setEarlyBookingListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<EarlyBooking[]>
): ManualAccommodationState => ({
    ...state,
    early_booking_list: action.payload
});

const setExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | Extra>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            extra_create: true,
            extra_edit: null,
            extra_conflicts: [],
            extra_description: '',
            extra_description_error: '',
            extra_included: false,
            extra_per_day: false,
            extra_per_person: false,
            extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
            extra_maximal_age: '',
            extra_meal_plan: [],
            extra_minimal_age: '',
            extra_minimal_age_error: '',
            extra_minimal_travelers: '',
            extra_name: '',
            extra_name_error: '',
            extra_reference: '',
            extra_reference_error: '',
            extra_transfer: false,
            extra_type: [0],
            extra_name_locales: {},
            extra_description_locales: {}
        };
    } else if (action.payload === false) {
        return {
            ...state,
            extra_create: false,
            extra_edit: null,
            extra_conflicts: [],
            extra_description: '',
            extra_description_error: '',
            extra_included: false,
            extra_per_day: false,
            extra_per_person: false,
            extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
            extra_maximal_age: '',
            extra_meal_plan: [],
            extra_minimal_age: '',
            extra_minimal_age_error: '',
            extra_minimal_travelers: '',
            extra_name: '',
            extra_name_error: '',
            extra_reference: '',
            extra_reference_error: '',
            extra_transfer: false,
            extra_type: [0],
            extra_name_locales: {},
            extra_description_locales: {}
        };
    } else if (state.extra_create) {
        const extra_list = [...state.extra_list];
        extra_list.push(action.payload);
        return {
            ...state,
            extra_create: false,
            extra_edit: null,
            extra_conflicts: [],
            extra_description: '',
            extra_description_error: '',
            extra_included: false,
            extra_list: extra_list,
            extra_per_day: false,
            extra_per_person: false,
            extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
            extra_maximal_age: '',
            extra_meal_plan: [],
            extra_minimal_age: '',
            extra_minimal_age_error: '',
            extra_minimal_travelers: '',
            extra_name: '',
            extra_name_error: '',
            extra_reference: '',
            extra_reference_error: '',
            extra_transfer: false,
            extra_type: [0],
            extra_name_locales: {},
            extra_description_locales: {}
        };
    } else if (state.extra_edit) {
        const extra_list = [...state.extra_list];
        for (let i = 0; i < extra_list.length; i++) {
            if (extra_list[i].id === state.extra_edit.id) {
                extra_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            extra_create: false,
            extra_edit: null,
            extra_conflicts: [],
            extra_description: '',
            extra_description_error: '',
            extra_included: false,
            extra_list: extra_list,
            extra_per_day: false,
            extra_per_person: false,
            extra_prices: [{ ...EMPTY_EXTRA_PRICE }],
            extra_maximal_age: '',
            extra_meal_plan: [],
            extra_minimal_age: '',
            extra_minimal_age_error: '',
            extra_minimal_travelers: '',
            extra_name: '',
            extra_name_error: '',
            extra_reference: '',
            extra_reference_error: '',
            extra_transfer: false,
            extra_type: [0]
        };
    } else {
        const prices: ExtraPrice[] = [];
        const meal_plan: number[] = [];
        if (action.payload.breakfast) {
            meal_plan.push(0);
        }
        if (action.payload.lunch) {
            meal_plan.push(1);
        }
        if (action.payload.dinner) {
            meal_plan.push(2);
        }
        if (action.payload.drinks) {
            meal_plan.push(3);
        }
        let extra_price_by: 0 | 1 = 0;
        action.payload.prices.map((price) => {
            prices.push({
                end_date:
                    price.end_date !== null ? new Date(price.end_date) : null,
                end_date_error: '',
                interval_age_prices:
                    !price.interval_age_prices ||
                        price.interval_age_prices.length < 1
                        ? EMPTY_INTERVAL_AGE_OFFERS
                        : ((extra_price_by = 1), price.interval_age_prices),
                price: price.price !== null ? price.price : '',
                price_error: '',
                price_adult:
                    price.price_adult !== null ? price.price_adult : '',
                price_adult_error: '',
                price_child:
                    price.price_child !== null ? price.price_child : '',
                price_child_error: '',
                price_infant:
                    price.price_infant !== null ? price.price_infant : '',
                price_infant_error: '',
                price_teen: price.price_teen !== null ? price.price_teen : '',
                price_teen_error: '',
                price_type: price.price_type,
                start_date:
                    price.start_date !== null
                        ? new Date(price.start_date)
                        : null,
                start_date_error: ''
            });
        });
        const extra_name_locales: {} & { [index: string]: any } = {};
        const extra_description_locales: {} & { [index: string]: any } = {};
        let has_french = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            extra_name_locales[locale.locale] = locale.name;
            extra_description_locales[locale.locale] = locale.description;
        });
        if (!has_french) {
            extra_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
            extra_description_locales['1'] =
                action.payload.description !== null
                    ? action.payload.description
                    : '';
        }
        return {
            ...state,
            extra_create: false,
            extra_edit: action.payload,
            extra_conflicts: action.payload.conflicts,
            extra_description:
                action.payload.description !== null
                    ? action.payload.description
                    : '',
            extra_description_error: '',
            extra_description_locales: extra_description_locales,
            extra_included: action.payload.included,
            extra_per_day: action.payload.per_day,
            extra_per_person: action.payload.per_person,
            extra_price_by: extra_price_by,
            extra_prices:
                prices.length === 0 ? [{ ...EMPTY_EXTRA_PRICE }] : prices,
            extra_maximal_age:
                action.payload.maximal_age !== null
                    ? action.payload.maximal_age
                    : '',
            extra_meal_plan: meal_plan,
            extra_minimal_age:
                action.payload.minimal_age !== null
                    ? action.payload.minimal_age
                    : '',
            extra_minimal_age_error: '',
            extra_minimal_travelers:
                action.payload.minimal_travelers !== null
                    ? action.payload.minimal_travelers
                    : '',
            extra_name: action.payload.name !== null ? action.payload.name : '',
            extra_name_error: '',
            extra_name_locales: extra_name_locales,
            extra_reference:
                action.payload.reference !== null
                    ? action.payload.reference
                    : '',
            extra_reference_error: '',
            extra_transfer: action.payload.transfer,
            extra_type: action.payload.type
        };
    }
};

const setExtraCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    extra_criteria: action.payload
});

const setExtraDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | Extra>
): ManualAccommodationState => ({
    ...state,
    extra_list:
        action.payload === null && state.extra_delete !== null
            ? state.extra_list.filter(
                (element) =>
                    element.id !== (state.extra_delete as Extra)?.id
            )
            : state.extra_list,
    offer_list: action.payload === null && state.extra_delete !== null ?
        state.offer_list.map(offer => ({
            ...offer,
            extra: offer.extra?.id === state.extra_delete?.id ? null : offer.extra,
            extra_rules: offer.extra_rules?.filter(
                (element) =>
                    element.id !== (state.extra_delete as Extra)?.id
            ) || null
        }))
        : state.offer_list,
    room_list: action.payload === null && state.extra_delete !== null ?
        state.room_list.map(room => ({
            ...room,
            extra_rules: room.extra_rules?.filter(
                (element) =>
                    element.id !== (state.extra_delete as Extra)?.id
            ) || null
        }))
        : state.room_list,
    extra_delete: action.payload === false ? null : action.payload
});

const setExtraDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Extra>
): ManualAccommodationState => ({
    ...state,
    extra_duplicate:
        state.manual_accommodation_duplicate_type === 'different_different' ||
            (state.manual_accommodation_duplicate_type === 'same_different' &&
                state.manual_accommodation_duplicate_contract_to?.id !==
                state.contract_selected?.id) ||
            (state.extra_duplicate !== null && action.payload !== null)
            ? null
            : action.payload,
    extra_list:
        state.extra_duplicate !== null && action.payload !== null && (state.manual_accommodation_duplicate_type === 'same_same' || state.manual_accommodation_duplicate_contract_to?.id ===
            state.contract_selected?.id)
            ? [...state.extra_list, action.payload]
            : state.extra_list
});

const setExtraIntervalAgePricesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        index: number;
        interval: IntervalAgeOffer;
        price_index: number;
    }>
): ManualAccommodationState => {
    const extra_prices = cloneDeep(state.extra_prices);
    extra_prices[action.payload.price_index] = {
        ...extra_prices[action.payload.price_index],
        interval_age_prices: extra_prices[
            action.payload.price_index
        ].interval_age_prices.map((interval, index) =>
            index === action.payload.index ? action.payload.interval : interval
        )
    };
    return { ...state, extra_prices };
};

const setExtraListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra[]>
): ManualAccommodationState => ({
    ...state,
    extra_list: action.payload
});

const setLimitReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    list: null,
    limit: action.payload
});

const setListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<List>
): ManualAccommodationState => ({
    ...state,
    count: action.payload.count,
    list: action.payload.results,
    running: false
});

const setManualAccommodationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodation>
): ManualAccommodationState => {
    let check_in_hour = '00';
    let check_in_minutes = '00';
    if (action.payload.check_in !== null) {
        const check_in = action.payload.check_in.split(':');
        if (check_in[0] !== undefined && check_in[0].length === 2) {
            check_in_hour = check_in[0];
        }
        if (check_in[1] !== undefined && check_in[1].length === 2) {
            check_in_minutes = check_in[1];
        }
    }
    let check_out_hour = '00';
    let check_out_minutes = '00';
    if (action.payload.check_out !== null) {
        const check_out = action.payload.check_out.split(':');
        if (check_out[0] !== undefined && check_out[0].length === 2) {
            check_out_hour = check_out[0];
        }
        if (check_out[1] !== undefined && check_out[1].length === 2) {
            check_out_minutes = check_out[1];
        }
    }
    const providers = [...action.payload.providers];
    providers.map((provider) => {
        provider.contracts = [];
        const contracts = [...action.payload.contracts];
        for (let i = 0; i < contracts.length; i++) {
            if (provider.id === contracts[i]?.provider?.id) {
                provider.contracts.push({ ...contracts[i] });
            }
        }
    });
    const check_in_recommendation_locales: {} & {
        [index: string]: any;
    } = {};
    const check_out_recommendation_locales: {} & {
        [index: string]: any;
    } = {};
    const situation_locales: {} & { [index: string]: any } = {};
    const restoration_locales: {} & { [index: string]: any } = {};
    const children_locales: {} & { [index: string]: any } = {};
    const room_desc_locales: {} & { [index: string]: any } = {};
    const info_locales: {} & { [index: string]: any } = {};
    let has_french = false;
    action.payload.localization.map((locale) => {
        if (locale.locale === 1) {
            has_french = true;
        }
        check_in_recommendation_locales[locale.locale] =
            locale.check_in_recommendation;
        check_out_recommendation_locales[locale.locale] =
            locale.check_out_recommendation;
        situation_locales[locale.locale] = locale.situation;
        restoration_locales[locale.locale] = locale.restoration;
        children_locales[locale.locale] = locale.children;
        room_desc_locales[locale.locale] = locale.room_desc;
        info_locales[locale.locale] = locale.info;
    });
    if (!has_french) {
        check_in_recommendation_locales['1'] =
            action.payload.check_in_recommendation !== null
                ? action.payload.check_in_recommendation
                : '';
        check_out_recommendation_locales['1'] =
            action.payload.check_out_recommendation !== null
                ? action.payload.check_out_recommendation
                : '';
        situation_locales['1'] =
            action.payload.situation !== null ? action.payload.situation : '';
        restoration_locales['1'] =
            action.payload.restoration !== null
                ? action.payload.restoration
                : '';
        children_locales['1'] =
            action.payload.children !== null ? action.payload.children : '';
        room_desc_locales['1'] =
            action.payload.room_desc !== null ? action.payload.room_desc : '';
        info_locales['1'] = '';
    }
    const tags = action.payload.tags.length > 0 ? ((
        typeof action.payload.tags[0] === 'number') ?
        (action.payload.tags as unknown as number[]).map((tag: number) => state.accommodation_tags[tag]) : action.payload.tags) : [];
    return {
        ...state,
        // edit: null,
        best_offer_limit: action.payload.best_offer_limit || 0,
        best_offer_order: action.payload.best_offer_order || 'BEST_REDUCTION',
        chain: action.payload.chain,
        check_in_hour: check_in_hour,
        check_in_minutes: check_in_minutes,
        check_in_recommendation: action.payload.check_in_recommendation,
        check_in_recommendation_locales: check_in_recommendation_locales,
        check_out_recommendation_locales: check_out_recommendation_locales,
        check_out_hour: check_out_hour,
        check_out_minutes: check_out_minutes,
        check_out_recommendation: action.payload.check_out_recommendation,
        children_locales: children_locales,
        contact_create: false,
        contact_delete: null,
        contact_edit: null,
        contact_email: '',
        contact_email_error: '',
        contact_first_name: '',
        contact_first_name_error: '',
        contact_job: '',
        contact_job_error: '',
        contact_last_name: '',
        contact_last_name_error: '',
        contact_phone_number: '',
        contact_phone_number_error: '',
        comfort_rate:
            action.payload.comfort !== null ? action.payload.comfort : 0,
        contract_contract: null,
        contract_contract_error: '',
        contract_create: false,
        contract_provider: null,
        email: action.payload.email !== null ? action.payload.email : '',
        email_error: '',
        family_plan:
            action.payload.family_plan !== null
                ? action.payload.family_plan
                : '',
        family_plan_max:
            action.payload.family_plan_max !== null
                ? action.payload.family_plan_max
                : '',
        fax: action.payload.fax !== null ? action.payload.fax : '',
        fax_error: '',
        info_locales: info_locales,
        max_age_baby:
            action.payload.max_age_baby !== null
                ? action.payload.max_age_baby
                : '',
        min_age_adult:
            action.payload.min_age_adult !== null
                ? action.payload.min_age_adult
                : '',
        name: action.payload.name !== null ? action.payload.name : '',
        client_hotel_identifier: action.payload.client_hotel_identifier || '',
        is_protected_description: !!action.payload.is_protected_description,
        name_error: '',
        client_hotel_identifier_error: '',
        phone_number:
            action.payload.phone_number !== null
                ? action.payload.phone_number
                : '',
        phone_number_error: '',
        providers: providers,
        recommendation:
            !action.payload.blacklist && !action.payload.favorite
                ? 0
                : action.payload.favorite
                    ? 1
                    : 2,
        restoration_locales: restoration_locales,
        restoration_rate:
            action.payload.restaurant !== null ? action.payload.restaurant : 0,
        room_desc_locales: room_desc_locales,
        situation_locales: situation_locales,
        situation_rate:
            action.payload.situation_select !== null
                ? action.payload.situation_select
                : 0,
        stars: action.payload.stars !== null ? action.payload.stars : 0,
        superior_stars: action.payload.hotel_sup,
        display_for_trip_plan: action.payload.display_for_trip_plan,
        display_for_package: action.payload.display_for_package,
        type: action.payload.type,
        website: action.payload.website !== null ? action.payload.website : '',
        website_error: '',
        stay_duration: action.payload.stay_duration?.map((minimal_stay) => ({ ...minimal_stay, start_date: new Date(minimal_stay?.start_date || ''), end_date: new Date(minimal_stay?.end_date || '') })) || [],
        manual_accommodation: {
            ...action.payload,
            tags
        }
    };
};

const setManualAccommodationDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        manual_accommodation: null | ManualAccommodation;
        contract: null | Contract;
    }>
) => ({
    ...state,
    manual_accommodation_duplicate: action.payload.manual_accommodation,
    manual_accommodation_duplicate_contract: action.payload.contract,
    manual_accommodation_duplicate_contract_target: null,
    create_search_results: null,
    create_name: '',
    create_search_selected: -1,
    create_search_city: '',
    create_search_country: null,
    create_search_name: ''
});

const setManualAccommodationContactsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Contact | boolean>
): ManualAccommodationState =>
    action.payload === true
        ? {
            ...state,
            contact_create: true,
            contact_edit: null,
            contact_email: '',
            contact_email_error: '',
            contact_first_name: '',
            contact_first_name_error: '',
            contact_job: '',
            contact_job_error: '',
            contact_last_name: '',
            contact_last_name_error: '',
            contact_phone_number: '',
            contact_phone_number_error: ''
        }
        : action.payload === false
            ? {
                ...state,
                contact_create: false,
                contact_edit: null,
                contact_email: '',
                contact_email_error: '',
                contact_first_name: '',
                contact_first_name_error: '',
                contact_job: '',
                contact_job_error: '',
                contact_last_name: '',
                contact_last_name_error: '',
                contact_phone_number: '',
                contact_phone_number_error: ''
            }
            : {
                ...state,
                contact_create: false,
                contact_edit: action.payload,
                contact_email:
                    action.payload.email !== null ? action.payload.email : '',
                contact_email_error: '',
                contact_first_name:
                    action.payload.first_name !== null
                        ? action.payload.first_name
                        : '',
                contact_first_name_error: '',
                contact_job:
                    action.payload.job !== null ? action.payload.job : '',
                contact_job_error: '',
                contact_last_name:
                    action.payload.last_name !== null
                        ? action.payload.last_name
                        : '',
                contact_last_name_error: '',
                contact_phone_number:
                    action.payload.phone_number !== null
                        ? action.payload.phone_number
                        : '',
                contact_phone_number_error: ''
            };

const setManualAccommodationDuplicateStepReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
) => ({
    ...state,
    manual_accommodation_duplicate_step: action.payload
});

const setManualAccommodationDuplicateTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<
        null | 'same_same' | 'same_different' | 'different_different'
    >
) => ({
    ...state,
    manual_accommodation_duplicate_type: action.payload
});

const setManualAccommodationDuplicateAccommodationToReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<
        null | (ManualAccommodation & { [index: string]: any })
    >
): ManualAccommodationState => {
    const payload = cloneDeep(action.payload);
    if (payload && payload?.id !== state.manual_accommodation?.id) {
        const providers = [...(payload.providers ?? [])];
        providers.map((provider) => {
            provider.contracts = [];
            const contracts = [...(payload.contracts ?? [])];
            for (let i = 0; i < contracts.length; i++) {
                if (provider.id === (contracts ?? [])[i]?.provider?.id) {
                    provider.contracts.push({ ...contracts[i] });
                }
            }
        });
        payload.providers = providers;
    }
    return {
        ...state,
        manual_accommodation_duplicate_accommodation_to: payload
    };
};

const setManualAccommodationDuplicateContractToReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Contract>
) => ({
    ...state,
    manual_accommodation_duplicate_contract_to: action.payload
});

const setManualAccommodationMealTaxesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<MealTaxes>
) => ({
    ...state,
    taxes_meal:
        action.payload.service_charge_tax !== null
            ? action.payload.service_charge_tax
            : '',
    taxes_meal_id: action.payload.id,
    taxes_meal_vat: action.payload.vat !== null ? action.payload.vat : ''
});

const setManualAccommodationTaxesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Taxes>
) => ({
    ...state,
    taxes_fees:
        action.payload.service_charge_tax !== null
            ? action.payload.service_charge_tax
            : '',
    taxes_resort:
        action.payload.resort_and_conservation_fee !== null
            ? action.payload.resort_and_conservation_fee
            : '',
    taxes_resort_id: action.payload.id,
    taxes_resort_vat:
        action.payload.vat_on_resort_and_conservation_fee !== null
            ? action.payload.vat_on_resort_and_conservation_fee
            : '',
    taxes_touristic:
        action.payload.touristic_tax !== null
            ? action.payload.touristic_tax
            : '',
    taxes_touristic_vat: action.payload.vat !== null ? action.payload.vat : ''
});

const setManualAccommodationDuplicateMultipleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple: action.payload
});

const setManualAccommodationDuplicateMultipleItemsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number[]>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_items: action.payload
});

const setManualAccommodationDuplicateMultipleExtraModalReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_extra_modal: action.payload
});

const setManualAccommodationDuplicateMultipleCancellationRuleModalReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_cancellation_rule_modal:
        action.payload
});

const setManualAccommodationDuplicateMultipleEarlyBookingModalReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_early_booking_modal: action.payload
});

const setManualAccommodationDuplicateMultipleOfferModalReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_offer_modal: action.payload
});

const setManualAccommodationDuplicateMultipleYieldModalReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_multiple_yield_modal: action.payload
});

const setOrderByReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    list: null,
    order_by: action.payload,
    order:
        state.order_by === action.payload && state.order === 'asc'
            ? 'desc'
            : 'asc'
});

const setOfferReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | Offer>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            offer_create: true,
            offer_edit: null,
            offer_applied_for: 'ADULT',
            offer_applied_for_max: '',
            offer_applied_for_min: 0,
            offer_applied_for_min_error: '',
            offer_booked_night: false,
            offer_booked_night_number: 0,
            offer_booked_room: false,
            offer_booked_room_number: 0,
            offer_child: false,
            offer_child_number: 0,
            offer_combine: [],
            offer_combine_accommodation: [],
            offer_combine_accommodation_chain: [],
            offer_delete: null,
            offer_description_locales: {},
            offer_duplicate: null,
            offer_end_date: null,
            offer_end_date_error: '',
            offer_extra: null,
            offer_extra_error: '',
            offer_extra_number: '',
            offer_extra_number_error: '',
            offer_extras: [],
            offer_extras_error: '',
            offer_name_locales: {},
            offer_name_locales_error: '',
            offer_night: '',
            offer_night_error: '',
            offer_night_type: 'cheap',
            offer_reduction: '',
            offer_reduction_error: '',
            offer_reduction_type: 'FIX',
            offer_reference: '',
            offer_start_date: null,
            offer_start_date_error: '',
            offer_sub_room: false,
            offer_trip_end_date: null,
            offer_trip_end_date_error: '',
            offer_trip_start_date: null,
            offer_trip_start_date_error: '',
            offer_type: 0,
            offer_by: 0,
            offer_interval_age_offers: EMPTY_INTERVAL_AGE_OFFERS
        };
    } else if (action.payload === false) {
        return {
            ...state,
            offer_create: false,
            offer_edit: null,
            offer_applied_for: 'ADULT',
            offer_applied_for_max: '',
            offer_applied_for_min: 0,
            offer_applied_for_min_error: '',
            offer_booked_night: false,
            offer_booked_night_number: 0,
            offer_booked_room: false,
            offer_booked_room_number: 0,
            offer_check_out_dates: false,
            offer_check_out_start_date: null,
            offer_check_out_start_date_error: '',
            offer_check_out_end_date: null,
            offer_child: false,
            offer_child_number: 0,
            offer_combine: [],
            offer_combine_accommodation: [],
            offer_combine_accommodation_chain: [],
            offer_delete: null,
            offer_description_locales: {},
            offer_duplicate: null,
            offer_end_date: null,
            offer_end_date_error: '',
            offer_extra: null,
            offer_extra_error: '',
            offer_extra_number: '',
            offer_extra_number_error: '',
            offer_extras: [],
            offer_extras_error: '',
            offer_name_locales: {},
            offer_name_locales_error: '',
            offer_night: '',
            offer_night_error: '',
            offer_night_type: 'cheap',
            offer_on_extra_applied: null,
            offer_on_extra_applied_error: '',
            offer_is_on_extra_applied: false,
            offer_is_honeymoon: false,
            offer_reduction: '',
            offer_reduction_error: '',
            offer_reduction_type: 'FIX',
            offer_reference: '',
            offer_start_date: null,
            offer_start_date_error: '',
            offer_sub_room: false,
            offer_trip_end_date: null,
            offer_trip_end_date_error: '',
            offer_trip_start_date: null,
            offer_trip_start_date_error: '',
            offer_type: 0,
            offer_by: 0,
            offer_interval_age_offers: EMPTY_INTERVAL_AGE_OFFERS
        };
    } else if (state.offer_create) {
        const offer_list = [...state.offer_list];
        offer_list.push(action.payload);
        return {
            ...state,
            offer_create: false,
            offer_edit: null,
            offer_list: offer_list,
            offer_applied_for: 'ADULT',
            offer_applied_for_max: '',
            offer_applied_for_min: 0,
            offer_applied_for_min_error: '',
            offer_booked_night: false,
            offer_booked_night_number: 0,
            offer_booked_room: false,
            offer_booked_room_number: 0,
            offer_check_out_dates: false,
            offer_check_out_start_date: null,
            offer_check_out_start_date_error: '',
            offer_check_out_end_date: null,
            offer_child: false,
            offer_child_number: 0,
            offer_combine: [],
            offer_combine_accommodation: [],
            offer_combine_accommodation_chain: [],
            offer_delete: null,
            offer_description_locales: {},
            offer_duplicate: null,
            offer_end_date: null,
            offer_end_date_error: '',
            offer_extra: null,
            offer_extra_error: '',
            offer_extra_number: '',
            offer_extra_number_error: '',
            offer_extras: [],
            offer_extras_error: '',
            offer_name_locales: {},
            offer_name_locales_error: '',
            offer_night: '',
            offer_night_error: '',
            offer_night_type: 'cheap',
            offer_on_extra_applied: null,
            offer_on_extra_applied_error: '',
            offer_is_on_extra_applied: false,
            offer_is_honeymoon: false,
            offer_reduction: '',
            offer_reduction_error: '',
            offer_reduction_type: 'FIX',
            offer_reference: '',
            offer_start_date: null,
            offer_start_date_error: '',
            offer_sub_room: false,
            offer_trip_end_date: null,
            offer_trip_end_date_error: '',
            offer_trip_start_date: null,
            offer_trip_start_date_error: '',
            offer_type: 0,
            offer_by: 0,
            offer_interval_age_offers: EMPTY_INTERVAL_AGE_OFFERS
        };
    } else if (state.offer_edit) {
        const offer_list = [...state.offer_list];
        for (let i = 0; i < offer_list.length; i++) {
            if (offer_list[i].id === state.offer_edit.id) {
                offer_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            offer_create: false,
            offer_edit: null,
            offer_list: offer_list,
            offer_applied_for: 'ADULT',
            offer_applied_for_max: '',
            offer_applied_for_min: 0,
            offer_applied_for_min_error: '',
            offer_booked_night: false,
            offer_booked_night_number: 0,
            offer_booked_room: false,
            offer_booked_room_number: 0,
            offer_check_out_dates: false,
            offer_check_out_start_date: null,
            offer_check_out_start_date_error: '',
            offer_check_out_end_date: null,
            offer_child: false,
            offer_child_number: 0,
            offer_combine: [],
            offer_combine_accommodation: [],
            offer_combine_accommodation_chain: [],
            offer_delete: null,
            offer_description_locales: {},
            offer_duplicate: null,
            offer_end_date: null,
            offer_end_date_error: '',
            offer_extra: null,
            offer_extra_error: '',
            offer_extra_number: '',
            offer_extra_number_error: '',
            offer_extras: [],
            offer_extras_error: '',
            offer_name_locales: {},
            offer_name_locales_error: '',
            offer_night: '',
            offer_night_error: '',
            offer_night_type: 'cheap',
            offer_on_extra_applied: null,
            offer_on_extra_applied_error: '',
            offer_is_on_extra_applied: false,
            offer_is_honeymoon: false,
            offer_reduction: '',
            offer_reduction_error: '',
            offer_reduction_type: 'FIX',
            offer_reference: '',
            offer_start_date: null,
            offer_start_date_error: '',
            offer_sub_room: false,
            offer_trip_end_date: null,
            offer_trip_end_date_error: '',
            offer_trip_start_date: null,
            offer_trip_start_date_error: '',
            offer_type: 0,
            offer_by: 0,
            offer_interval_age_offers: EMPTY_INTERVAL_AGE_OFFERS
        };
    } else {
        const offer_name_locales: {} & { [index: string]: any } = {};
        const offer_description_locales: {} & { [index: string]: any } = {};
        let has_french = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            offer_name_locales[locale.locale] = locale.name;
            offer_description_locales[locale.locale] = locale.description;
        });
        if (!has_french) {
            offer_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
            offer_description_locales['1'] =
                action.payload.description !== null
                    ? action.payload.description
                    : '';
        }
        return {
            ...state,
            offer_create: false,
            offer_edit: action.payload,
            offer_applied_for: action.payload.applied_for,
            offer_applied_for_max:
                action.payload.applied_max !== null
                    ? action.payload.applied_max
                    : '',
            offer_applied_for_min:
                action.payload.applied_min !== null
                    ? action.payload.applied_min
                    : '',
            offer_applied_for_min_error: '',
            offer_booked_night: action.payload.minimal_stay !== null,
            offer_booked_night_number:
                action.payload.minimal_stay !== null
                    ? action.payload.minimal_stay
                    : '',
            offer_booked_room: action.payload.nb_room !== null,
            offer_booked_room_number:
                action.payload.nb_room !== null ? action.payload.nb_room : '',
            offer_check_out_dates: !!action.payload.check_out_start_date,
            offer_check_out_start_date:
                action.payload.check_out_start_date !== null
                    ? new Date(action.payload.check_out_start_date)
                    : null,
            offer_check_out_start_date_error: '',
            offer_check_out_end_date:
                action.payload.check_out_end_date !== null
                    ? new Date(action.payload.check_out_end_date)
                    : null,
            offer_child: action.payload.minimal_children !== null,
            offer_child_number:
                action.payload.minimal_children !== null
                    ? action.payload.minimal_children
                    : '',
            offer_combine: action.payload.combine,
            offer_combine_accommodation: action.payload.valid_hotel,
            offer_combine_accommodation_chain: action.payload.valid_chain,
            offer_description_locales: offer_description_locales,
            offer_end_date:
                action.payload.end_date !== null
                    ? new Date(action.payload.end_date)
                    : null,
            offer_end_date_error: '',
            offer_extra: action.payload.extra,
            offer_extra_error: '',
            offer_extra_number_error: '',
            offer_is_on_extra_applied: !!action.payload.on_extra_applied,
            offer_on_extra_applied: action.payload.on_extra_applied,
            offer_on_extra_applied_error: '',
            offer_is_honeymoon: action.payload.is_honeymoon,
            offer_extra_number:
                action.payload.nb_extra !== null ? action.payload.nb_extra : '',
            offer_extras:
                action.payload.extra_rules !== null
                    ? action.payload.extra_rules
                    : [],
            offer_extras_error: '',
            offer_name_locales: offer_name_locales,
            offer_name_locales_error: '',
            offer_night:
                action.payload.nb_night !== null ? action.payload.nb_night : '',
            offer_night_error: '',
            offer_night_type:
                action.payload.night_price !== null
                    ? action.payload.night_price
                    : 'cheap',
            offer_reduction:
                action.payload.value !== null ? action.payload.value : '',
            offer_reduction_type: action.payload.type,
            offer_reduction_error: '',
            offer_reference:
                action.payload.reference !== null
                    ? action.payload.reference
                    : '',
            offer_start_date:
                action.payload.start_date !== null
                    ? new Date(action.payload.start_date)
                    : null,
            offer_start_date_error: '',
            offer_sub_room: action.payload.sub_room,
            offer_trip_end_date:
                action.payload.promotion_end_date !== null
                    ? new Date(action.payload.promotion_end_date)
                    : null,
            offer_trip_end_date_error: '',
            offer_trip_start_date:
                action.payload.promotion_start_date !== null
                    ? new Date(action.payload.promotion_start_date)
                    : null,
            offer_trip_start_date_error: '',
            offer_type: action.payload.reduction_type,
            offer_by: action.payload.interval_age_offers.length > 0 ? 1 : 0,
            offer_interval_age_offers:
                !action.payload.interval_age_offers ||
                    action.payload.interval_age_offers.length < 1
                    ? EMPTY_INTERVAL_AGE_OFFERS
                    : action.payload.interval_age_offers
        };
    }
};

const setOfferCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    offer_criteria: action.payload
});

const setOfferDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | Offer>
): ManualAccommodationState => ({
    ...state,
    offer_delete: action.payload === false ? null : action.payload,
    offer_list:
        action.payload === null && state.offer_delete !== null
            ? state.offer_list.filter(
                (element, index) =>
                    element.id !== (state.offer_delete as Offer)?.id
            )
            : state.offer_list
});

const setOfferDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Offer>
): ManualAccommodationState => ({
    ...state,
    offer_duplicate:
        state.manual_accommodation_duplicate_type === 'different_different' ||
            (state.manual_accommodation_duplicate_type === 'same_different' &&
                state.manual_accommodation_duplicate_contract_to?.id !==
                state.contract_selected?.id) ||
            (state.offer_duplicate !== null && action.payload !== null)
            ? null
            : action.payload,
    offer_list:
        state.offer_duplicate !== null && action.payload !== null
            ? [...state.offer_list, action.payload]
            : state.offer_list
});

const setOfferListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Offer[]>
): ManualAccommodationState => ({
    ...state,
    offer_list: action.payload
});

const setOfferIntervalAgeOffersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; interval: IntervalAgeOffer }>
): ManualAccommodationState => ({
    ...state,
    offer_interval_age_offers: state.offer_interval_age_offers.map(
        (interval, index) =>
            index === action.payload.index ? action.payload.interval : interval
    )
});

const addOfferIntervalAgeOffersReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    offer_interval_age_offers: [
        ...state.offer_interval_age_offers,
        ...EMPTY_INTERVAL_AGE_OFFERS
    ]
});

const deleteOfferIntervalAgeOffersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    offer_interval_age_offers: state.offer_interval_age_offers.filter(
        (element, index) => index !== action.payload
    )
});

const setOfferIsHoneymoonReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_is_honeymoon: action.payload
});

const setOfferIsOnExtraAppliedReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_is_on_extra_applied: action.payload,
    offer_on_extra_applied: null,
    offer_on_extra_applied_error: '',
});

const setOfferOnExtraAppliedReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra | null>
): ManualAccommodationState => ({
    ...state,
    offer_on_extra_applied_error: '',
    offer_on_extra_applied: action.payload
});

const setPageReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    list: null,
    page: action.payload
});

const setSearchReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    list: null,
    page: 0,
    search: action.payload
});

const setPreferencesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Preferences[]>
): ManualAccommodationState => {
    const preferences = action.payload;
    const new_columns: Column[] = [];
    let new_order_by = '-created_date';
    let new_order: 'asc' | 'desc' = 'asc';
    const field_name: string[] = [];
    const manual_accommodation_preferences: Preferences[] = [];
    preferences.map((preference) => {
        if (preference.module === 'MANUAL_ACCOMMODATION') {
            if (!field_name.includes(preference.field_name)) {
                switch (preference.field_name) {
                    case 'NAME':
                        new_columns.push({
                            label: 'name',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'name',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'name'
                                : new_order_by;
                        break;
                    case 'CLIENT_HOTEL_IDENTIFIER':
                        new_columns.push({
                            label: 'client_hotel_identifier',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'client_hotel_identifier',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'client_hotel_identifier'
                                : new_order_by;
                        break;
                    case 'ADDRESS':
                        new_columns.push({
                            label: 'address',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'street',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'street'
                                : new_order_by;
                        break;
                    case 'DESTINATION':
                        new_columns.push({
                            label: 'destination',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'destination_name',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'destination_name'
                                : new_order_by;
                        break;
                    case 'COUNTRY':
                        new_columns.push({
                            label: 'country',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'country',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'country'
                                : new_order_by;
                        break;
                    case 'CHAIN':
                        new_columns.push({
                            label: 'hotel-chain',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'chain__name',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'chain__name'
                                : new_order_by;
                        break;
                    case 'VISIBILITY':
                        new_columns.push({
                            label: 'online-to',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: '',
                            fixed_width: true,
                            last_updated: false
                        });
                        break;
                    case 'AGENCY_VISIBILITY':
                        new_columns.push({
                            label: 'online-agency',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: '',
                            fixed_width: true,
                            last_updated: false
                        });
                        break;
                    case 'PUBLISHED_ON_WEB':
                        new_columns.push({ label: 'website-publication', field_name: preference.field_name, checked: preference.active, order_by: 'publish_on_web', fixed_width: true, last_updated: false });
                        break;
                    case 'LOCK':
                        new_columns.push({ label: 'reviewed-by-marketing', field_name: preference.field_name, checked: preference.active, order_by: 'lock', fixed_width: true, last_updated: false });
                        break;

                    case 'CREATED_DATE':
                        new_columns.push({
                            label: 'created-date',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'created_date',
                            fixed_width: true,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'created_date'
                                : new_order_by;
                        break;
                    case 'CREATED_BY':
                        new_columns.push({
                            label: 'created-by',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'creator__username',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'creator__username'
                                : new_order_by;
                        break;
                    case 'MODIFIED_DATE':
                        new_columns.push({
                            label: 'modified-date',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'modified_date',
                            fixed_width: true,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'modified_date'
                                : new_order_by;
                        break;
                    case 'MODIFIED_BY':
                        new_columns.push({
                            label: 'modified-by',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'last_author__username',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'last_author__username'
                                : new_order_by;
                        break;
                    case 'TAGS':
                        new_columns.push({
                            label: 'modified-by',
                            field_name: preference.field_name,
                            checked: preference.active,
                            order_by: 'last_author__username',
                            fixed_width: false,
                            last_updated: false
                        });
                        new_order_by =
                            preference.ordering_asc !== null
                                ? 'last_author__username'
                                : new_order_by;
                        break;
                    default:
                        break;
                }
                manual_accommodation_preferences.push(preference);
                field_name.push(preference.field_name);
                new_order =
                    preference.ordering_asc !== null
                        ? preference.ordering_asc
                            ? 'asc'
                            : 'desc'
                        : new_order;
            }
        }
    });
    if (new_columns.length !== state.columns.length) {
        state.columns.map((column) => {
            let added = false;
            for (let i = 0; i < new_columns.length; i++) {
                if (new_columns[i].label === column.label) {
                    added = true;
                    break;
                }
            }
            if (!added) {
                new_columns.push({ ...column });
            }
        });
    }
    return {
        ...state,
        preferences: manual_accommodation_preferences,
        columns: new_columns,
        order_by: new_order_by,
        order: new_order
    };
};

const setRoomReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Room | boolean>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            room_create: true,
            room_edit: null,
            room_bedding: [{ ...EMPTY_ROOM_BEDDING }],
            room_cancellation_rules: [],
            room_category: '',
            room_delete: null,
            room_duplicate: null,
            room_desc_locales: {},
            room_description_locales: {},
            room_early_bookings: [],
            room_equipments: [],
            room_extras: [],
            room_locked_duration: 0,
            room_name_locales: {},
            room_main: true,
            room_max_adults: 0,
            room_max_children: 0,
            room_max_nights: 0,
            room_max_travelers: 0,
            room_min_adults: 0,
            room_min_children: 0,
            room_min_nights: 0,
            room_min_stay_periods: [],
            room_min_travelers: 0,
            room_offers: [],
            room_pictures: [],
            room_prohibited_compositions: [],
            room_size: '',
            room_yields: []
        };
    } else if (action.payload === false) {
        return {
            ...state,
            room_create: false,
            room_edit: null,
            room_bedding: [{ ...EMPTY_ROOM_BEDDING }],
            room_cancellation_rules: [],
            room_category: '',
            room_delete: null,
            room_duplicate: null,
            room_description_locales: {},
            room_early_bookings: [],
            room_equipments: [],
            room_extras: [],
            room_locked_duration: 0,
            room_name_locales: {},
            room_main: true,
            room_max_adults: 0,
            room_max_children: 0,
            room_max_nights: 0,
            room_max_travelers: 0,
            room_min_adults: 0,
            room_min_children: 0,
            room_min_nights: 0,
            room_min_stay_periods: [],
            room_min_travelers: 0,
            room_offers: [],
            room_pictures: [],
            room_prohibited_compositions: [],
            room_size: '',
            room_yields: []
        };
    } else if (state.room_create) {
        const room_list = [...state.room_list];
        room_list.push(action.payload);
        return {
            ...state,
            room_edit: null,
            room_create: false,
            room_list: room_list,
            room_bedding: [{ ...EMPTY_ROOM_BEDDING }],
            room_cancellation_rules: [],
            room_category: '',
            room_delete: null,
            room_duplicate: null,
            room_description_locales: {},
            room_early_bookings: [],
            room_equipments: [],
            room_extras: [],
            room_locked_duration: 0,
            room_name_locales: {},
            room_main: true,
            room_max_adults: 0,
            room_max_children: 0,
            room_max_nights: 0,
            room_max_travelers: 0,
            room_min_adults: 0,
            room_min_children: 0,
            room_min_nights: 0,
            room_min_stay_periods: [],
            room_min_travelers: 0,
            room_offers: [],
            room_pictures: [],
            room_prohibited_compositions: [],
            room_size: '',
            room_yields: []
        };
    } else if (state.room_edit) {
        const room_list = [...state.room_list];
        for (let i = 0; i < room_list.length; i++) {
            if (room_list[i].id === action.payload.id) {
                room_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            room_edit: null,
            room_create: false,
            room_list: room_list,
            room_bedding: [{ ...EMPTY_ROOM_BEDDING }],
            room_cancellation_rules: [],
            room_category: '',
            room_delete: null,
            room_duplicate: null,
            room_description_locales: {},
            room_early_bookings: [],
            room_equipments: [],
            room_extras: [],
            room_locked_duration: 0,
            room_name_locales: {},
            room_main: true,
            room_max_adults: 0,
            room_max_children: 0,
            room_max_nights: 0,
            room_max_travelers: 0,
            room_min_adults: 0,
            room_min_children: 0,
            room_min_nights: 0,
            room_min_stay_periods: [],
            room_min_travelers: 0,
            room_offers: [],
            room_pictures: [],
            room_prohibited_compositions: [],
            room_size: '',
            room_yields: []
        };
    } else {
        const room_name_locales: {} & { [index: string]: any } = {};
        const room_description_locales: {} & { [index: string]: any } = {};
        let has_french = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            room_name_locales[locale.locale] = locale.name;
            room_description_locales[locale.locale] = locale.description;
        });
        if (!has_french) {
            room_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
            room_description_locales['1'] =
                action.payload.description !== null
                    ? action.payload.description
                    : '';
        }
        const room_min_stay_periods: RoomStayPeriod[] = [];
        action.payload.minimal_stay.map((stay) => {
            room_min_stay_periods.push({
                end_date:
                    stay.end_date === null ? null : new Date(stay.end_date),
                end_date_error: '',
                nights: stay.nb_days,
                nights_error: '',
                start_date:
                    stay.start_date === null ? null : new Date(stay.start_date),
                start_date_error: ''
            });
        });
        const room_bedding: RoomBedding[] = [];
        action.payload.beds.map((bed) => {
            const bedding = { ...EMPTY_ROOM_BEDDING };
            for (const [key, count] of Object.entries(bedding)) {
                bed.map((bed_type) => {
                    if (bed_type === key) {
                        bedding[key]++;
                    }
                });
            }
            room_bedding.push(bedding);
        });
        return {
            ...state,
            room_create: false,
            room_edit: action.payload,
            room_bedding: room_bedding,
            room_cancellation_rules:
                action.payload.cancellation_rules !== null
                    ? action.payload.cancellation_rules
                    : [],
            room_category:
                action.payload.type !== null ? action.payload.type : '',
            room_delete: null,
            room_duplicate: null,
            room_description_locales: room_description_locales,
            room_early_bookings:
                action.payload.early_booking !== null
                    ? action.payload.early_booking
                    : [],
            room_equipments: [],
            room_extras:
                action.payload.extra_rules !== null
                    ? action.payload.extra_rules
                    : [],
            room_locked_duration: action.payload.check_out_days,
            room_name_locales: room_name_locales,
            room_main: action.payload.sub_room ? action.payload.parent : true,
            room_max_adults: action.payload.composition[0],
            room_max_children: action.payload.composition[1],
            room_max_nights: action.payload.max_days,
            room_max_travelers: action.payload.traveler_max,
            room_min_adults: action.payload.minimal_composition[0],
            room_min_children: action.payload.minimal_composition[1],
            room_min_nights: action.payload.min_days,
            room_min_stay_periods: room_min_stay_periods,
            room_min_travelers: action.payload.traveler_min,
            room_offers:
                action.payload.offers !== null ? action.payload.offers : [],
            room_pictures:
                action.payload.pictures !== null ? action.payload.pictures : [],
            room_prohibited_compositions:
                action.payload.forbidden_compositions !== null
                    ? action.payload.forbidden_compositions
                    : [],
            room_size: action.payload.size,
            room_yields:
                action.payload.yield_management !== null
                    ? action.payload.yield_management
                    : []
        };
    }
};

const setRoomsBlackOutReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | BlackOut>
): ManualAccommodationState =>
    action.payload === true
        ? {
            ...state,
            rooms_black_out: EMPTY_ROOM_BLACK_OUT,
            rooms_black_out_create: true
        }
        : {
            ...state,
            rooms_black_out: action.payload
                ? action.payload
                : EMPTY_ROOM_BLACK_OUT,
            rooms_black_out_create: action.payload ? true : false
        };

const setRoomsUnBlackOutReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | BlackOut>
): ManualAccommodationState =>
    action.payload === true
        ? {
            ...state,
            rooms_black_out: EMPTY_ROOM_BLACK_OUT,
            rooms_un_black_out_create: true
        }
        : {
            ...state,
            rooms_black_out: action.payload
                ? action.payload
                : EMPTY_ROOM_BLACK_OUT,
            rooms_un_black_out_create: action.payload ? true : false
        };

const setRoomBlackOutsDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Room>
): ManualAccommodationState => {
    if (action.payload === null) {
        return {
            ...state,
            room_black_outs_duplicate: null,
            room_black_outs_target: null
        };
    } else if (
        state.room_black_outs_duplicate !== null &&
        state.room_black_outs_target !== null
    ) {
        const room_list = [...state.room_list];
        for (let i = 0; i < room_list.length; i++) {
            if (room_list[i].id === action.payload.id) {
                room_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            room_list: room_list,
            room_black_outs_duplicate: null,
            room_black_outs_target: null
        };
    } else {
        return { ...state, room_black_outs_duplicate: action.payload };
    }
};

const setRoomBlackOutsRoomReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Room | null>
): ManualAccommodationState => {
    if (action.payload === null) {
        return {
            ...state,
            room_black_outs: [],
            room_black_outs_room: null,
            room_black_outs_target: null
        };
    } else if (
        state.room_black_outs_room !== null ||
        state.rooms_black_out.start_date
    ) {
        const room_list = [...state.room_list];
        for (let i = 0; i < room_list.length; i++) {
            if (room_list[i].id === action.payload.id) {
                room_list[i] = action.payload;
                break;
            }
        }

        return {
            ...state,
            room_list: room_list,
            stocks_state: state.stocks_state
                ? {
                    ...state.stocks_state,
                    rooms: state.stocks_state?.rooms.map((room, index) => ({
                        ...room,
                        ...room_list[index]
                    }))
                }
                : null,
            room_black_outs: [],
            room_black_outs_room: null,
            room_black_outs_target: null
        };
    } else {
        const room_black_outs: BlackOut[] = [];
        action.payload.black_out.map((black_out) => {
            room_black_outs.push({
                end_date:
                    black_out.end_date === null
                        ? null
                        : new Date(black_out.end_date),
                end_date_error: '',
                start_date:
                    black_out.start_date === null
                        ? !black_out.date
                            ? null
                            : new Date(black_out.date)
                        : new Date(black_out.start_date),
                start_date_error: ''
            });
        });
        return {
            ...state,
            room_black_outs: room_black_outs,
            room_black_outs_room: action.payload
        };
    }
};

const setRoomCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    room_criteria: action.payload
});

const setRoomDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | Room>
): ManualAccommodationState => ({
    ...state,
    room_delete: action.payload === false ? null : action.payload,
    room_list:
        action.payload === null && state.room_delete !== null
            ? state.room_list.filter(
                (element, index) => element.id !== state.room_delete?.id
            )
            : state.room_list
});

const setRoomDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Room>
): ManualAccommodationState => ({
    ...state,
    room_duplicate:
        state.room_duplicate !== null && action.payload !== null
            ? null
            : action.payload,
    room_list:
        state.room_duplicate !== null && action.payload !== null
            ? [...state.room_list, action.payload]
            : state.room_list
});

const setRoomListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Room[]>
): ManualAccommodationState => ({
    ...state,
    room_list: action.payload
});

const setRoomPriceScrollIdReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | null>
) => ({
    ...state,
    room_price_scroll_id: action.payload
});

const setEmptyRoomPriceReducer = (state: ManualAccommodationState, action: PayloadAction<RoomPrice>): ManualAccommodationState => ({
    ...state, empty_room_price: action.payload
});

const setIsRoomPricesEmptyReducer = (state: ManualAccommodationState, action: PayloadAction<boolean>): ManualAccommodationState => ({
    ...state, is_room_price_empty: action.payload
});

const setRoomPricesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Room | Price[] | false>
) => {
    if (state.contract_selected === null) {
        return state;
    } else {
        if (action.payload === false) {
            return {
                ...state,
                room_price: null,
                room_prices: [{ ...state.empty_room_price }],
            };
        } else if (state.room_price !== null) {
            const room_list = [...state.room_list];
            // const prices = {...action.payload};
            const prices = (cloneDeep(action.payload) as Room).prices || action.payload as Price[];
            state.room_price.prices.map((price) => {
                if (
                    price.contract !== null &&
                    price.contract !== state.contract_selected?.id
                ) {
                    prices.push(price);
                }
            });
            const room_price = { ...state.room_price };
            room_price.prices = prices;
            for (let i = 0; i < room_list.length; i++) {
                if (room_list[i].id === room_price.id) {
                    room_list[i] = room_price;
                }
            }
            return {
                ...state,
                room_list: room_list,
                room_price: null,
                room_prices: [{ ...state.empty_room_price }],
            };
        } else {
            const room_prices: RoomPrice[] = [];
            (action.payload as Room).prices.map((price) => {
                if (
                    price.contract !== null &&
                    price.contract === state.contract_selected?.id
                ) {
                    let added = false;
                    room_prices.map((current_price) => {
                        const is_same_selling_prices =
                            (price.double_selling_price === null
                                ? ""
                                : price.double_selling_price) ===
                            current_price.selling_double &&
                            (price.selling_extra_adult === null
                                ? ""
                                : price.selling_extra_adult) ===
                            current_price.selling_extra_adult &&
                            (price.selling_extra_baby === null
                                ? ""
                                : price.selling_extra_baby) ===
                            current_price.selling_extra_baby &&
                            (price.selling_extra_child === null
                                ? ""
                                : price.selling_extra_child) ===
                            current_price.selling_extra_infant &&
                            (price.selling_extra_teen === null
                                ? ""
                                : price.selling_extra_teen) ===
                            current_price.selling_extra_teen &&
                            (price.monoparent_selling_price === null
                                ? ""
                                : price.monoparent_selling_price) ===
                            current_price.selling_mono &&
                            (price.quad_selling_price === null
                                ? ""
                                : price.quad_selling_price) ===
                            current_price.selling_quad &&
                            (price.selling_single_price === null
                                ? ""
                                : price.selling_single_price) ===
                            current_price.selling_single &&
                            (price.triple_selling_price === null
                                ? ""
                                : price.triple_selling_price) ===
                            current_price.selling_triple;

                        const is_same_purchase_prices =
                            (price.double_price === null
                                ? ""
                                : price.double_price) ===
                            current_price.double &&
                            (price.extra_adult === null
                                ? ""
                                : price.extra_adult) ===
                            current_price.extra_adult &&
                            (price.extra_baby === null
                                ? ""
                                : price.extra_baby) ===
                            current_price.extra_baby &&
                            (price.extra_child === null
                                ? ""
                                : price.extra_child) ===
                            current_price.extra_infant &&
                            (price.extra_teen === null
                                ? ""
                                : price.extra_teen) ===
                            current_price.extra_teen &&
                            (price.monoparent_price === null
                                ? ""
                                : price.monoparent_price) ===
                            current_price.mono &&
                            (price.quad_price === null
                                ? ""
                                : price.quad_price) === current_price.quad &&
                            (price.single_price === null
                                ? ""
                                : price.single_price) ===
                            current_price.single &&
                            (price.triple_price === null
                                ? ""
                                : price.triple_price) === current_price.triple;

                        const is_same_com_prices =
                            (price.double_com_price === null
                                ? ""
                                : price.double_com_price) ===
                            current_price.com_double &&
                            (price.com_extra_adult === null
                                ? ""
                                : price.com_extra_adult) ===
                            current_price.com_extra_adult &&
                            (price.com_extra_baby === null
                                ? ""
                                : price.com_extra_baby) ===
                            current_price.com_extra_baby &&
                            (price.com_extra_child === null
                                ? ""
                                : price.com_extra_child) ===
                            current_price.com_extra_infant &&
                            (price.com_extra_teen === null
                                ? ""
                                : price.com_extra_teen) ===
                            current_price.com_extra_teen &&
                            (price.monoparent_com_price === null
                                ? ""
                                : price.monoparent_com_price) ===
                            current_price.com_mono &&
                            (price.quad_com_price === null
                                ? ""
                                : price.quad_com_price) === current_price.com_quad &&
                            (price.com_single_price === null
                                ? ""
                                : price.com_single_price) ===
                            current_price.com_single &&
                            (price.triple_com_price === null
                                ? ""
                                : price.triple_com_price) === current_price.com_triple;

                        const is_same_markup_prices =
                            (price.double_markup_price === null
                                ? ""
                                : price.double_markup_price) ===
                            current_price.markup_double &&
                            (price.markup_extra_adult === null
                                ? ""
                                : price.markup_extra_adult) ===
                            current_price.markup_extra_adult &&
                            (price.markup_extra_baby === null
                                ? ""
                                : price.markup_extra_baby) ===
                            current_price.markup_extra_baby &&
                            (price.markup_extra_child === null
                                ? ""
                                : price.markup_extra_child) ===
                            current_price.markup_extra_infant &&
                            (price.markup_extra_teen === null
                                ? ""
                                : price.markup_extra_teen) ===
                            current_price.markup_extra_teen &&
                            (price.monoparent_markup_price === null
                                ? ""
                                : price.monoparent_markup_price) ===
                            current_price.markup_mono &&
                            (price.quad_markup_price === null
                                ? ""
                                : price.quad_markup_price) === current_price.markup_quad &&
                            (price.markup_single_price === null
                                ? ""
                                : price.markup_single_price) ===
                            current_price.markup_single &&
                            (price.triple_markup_price === null
                                ? ""
                                : price.triple_markup_price) === current_price.markup_triple;

                        if (
                            price.behavior_monoparent_adult ===
                            current_price.mono_adult &&
                            price.behavior_monoparent_child ===
                            current_price.mono_child &&
                            (
                                ((state.pricing_type === PricingType.PURCHASE) && is_same_purchase_prices) ||
                                ((state.pricing_type === PricingType.PURCHASE_COM) && is_same_purchase_prices && is_same_com_prices) ||
                                ((state.pricing_type === PricingType.PURCHASE_MARKUP) && is_same_purchase_prices && is_same_markup_prices) ||
                                ((state.pricing_type === PricingType.PURCHASE_DETAILED) && is_same_purchase_prices && is_same_com_prices && is_same_markup_prices) ||
                                ((state.pricing_type === PricingType.SELLING || state.pricing_type === PricingType.SELLING_TTC) && is_same_selling_prices) ||
                                ((state.pricing_type === PricingType.SELLING_COM || state.pricing_type === PricingType.SELLING_COM_TTC) && is_same_selling_prices && is_same_com_prices) ||
                                ((state.pricing_type === PricingType.SELLING_MARKUP || state.pricing_type === PricingType.SELLING_MARKUP_TTC) && is_same_selling_prices && is_same_markup_prices) ||
                                ((state.pricing_type === PricingType.SELLING_DETAILED || state.pricing_type === PricingType.SELLING_DETAILED_TTC) && is_same_purchase_prices && is_same_com_prices && is_same_markup_prices)
                            )
                        ) {
                            current_price.periods.push({
                                days: price.days !== null ? price.days : [],
                                days_error: '',
                                end_date:
                                    price.end_date !== null
                                        ? new Date(price.end_date)
                                        : null,
                                end_date_error: '',
                                id: price.id,
                                start_date:
                                    price.start_date !== null
                                        ? new Date(price.start_date)
                                        : null,
                                start_date_error: ''
                            });
                            added = true;
                        }
                    });
                    if (!added) {
                        room_prices.push({
                            periods: [
                                {
                                    days: price.days !== null ? price.days : [],
                                    days_error: '',
                                    end_date:
                                        price.end_date !== null
                                            ? new Date(price.end_date)
                                            : null,
                                    end_date_error: '',
                                    id: price.id,
                                    start_date:
                                        price.start_date !== null
                                            ? new Date(price.start_date)
                                            : null,
                                    start_date_error: ''
                                }
                            ],
                            id: price.id,
                            stock:
                                price.stocks !== null &&
                                    price.stocks.length > 0 &&
                                    !isNaN(price.stocks[0])
                                    ? price.stocks[0]
                                    : 0,
                            stock_error: '',
                            restitution_days:
                                price.restitution_days !== null
                                    ? parseInt(price.restitution_days as string)
                                    : 0,

                            // Purchase
                            single:
                                price.single_price !== null
                                    ? price.single_price
                                    : "",
                            single_price_error: '',
                            double:
                                price.double_price !== null
                                    ? price.double_price
                                    : "",

                            triple:
                                price.triple_price !== null
                                    ? price.triple_price
                                    : "",
                            quad:
                                price.quad_price !== null
                                    ? price.quad_price
                                    : "",
                            mono:
                                price.monoparent_price !== null
                                    ? price.monoparent_price
                                    : "",
                            extra_adult:
                                price.extra_adult !== null
                                    ? price.extra_adult
                                    : "",
                            extra_infant:
                                price.extra_child !== null
                                    ? price.extra_child
                                    : "",
                            extra_baby:
                                price.extra_baby !== null
                                    ? price.extra_baby
                                    : "",
                            extra_teen:
                                price.extra_teen !== null
                                    ? price.extra_teen
                                    : "",
                            price_adult: price.price_adult !== null
                                ? price.price_adult
                                : "",
                            price_teen: price.price_teen !== null
                                ? price.price_teen
                                : "",
                            price_baby: price.price_baby !== null
                                ? price.price_baby
                                : "",
                            price_child: price.price_child !== null
                                ? price.price_child
                                : "",

                            // sale
                            selling_single:
                                price.selling_single_price !== null
                                    ? price.selling_single_price
                                    : "",
                            selling_single_error: '',
                            selling_double:
                                price.double_selling_price !== null
                                    ? price.double_selling_price
                                    : "",
                            selling_double_error: '',
                            selling_triple:
                                price.triple_selling_price !== null
                                    ? price.triple_selling_price
                                    : "",
                            selling_triple_error: '',
                            selling_quad:
                                price.quad_selling_price !== null
                                    ? price.quad_selling_price
                                    : "",
                            selling_quad_error: '',
                            selling_mono:
                                price.monoparent_selling_price !== null
                                    ? price.monoparent_selling_price
                                    : "",
                            selling_mono_error: '',
                            selling_extra_adult:
                                price.selling_extra_adult !== null
                                    ? price.selling_extra_adult
                                    : "",
                            selling_extra_adult_error: '',
                            selling_extra_infant:
                                price.selling_extra_child !== null
                                    ? price.selling_extra_child
                                    : "",
                            selling_extra_infant_error: '',
                            selling_extra_baby:
                                price.selling_extra_baby !== null
                                    ? price.selling_extra_baby
                                    : "",
                            selling_extra_baby_error: '',
                            selling_extra_teen:
                                price.selling_extra_teen !== null
                                    ? price.selling_extra_teen
                                    : "",
                            selling_extra_teen_error: '',
                            selling_price_adult: price.selling_price_adult !== null
                                ? price.selling_price_adult
                                : "",
                            selling_price_adult_error: '',
                            selling_price_teen: price.selling_price_teen !== null
                                ? price.selling_price_teen
                                : "",
                            selling_price_teen_error: '',
                            selling_price_baby: price.selling_price_baby !== null
                                ? price.selling_price_baby
                                : "",
                            selling_price_baby_error: '',
                            selling_price_child: price.selling_price_child !== null
                                ? price.selling_price_child
                                : "",
                            selling_price_child_error: '',

                            // com
                            com_single:
                                price.com_single_price !== null
                                    ? price.com_single_price
                                    : "",
                            com_single_price_error: '',
                            com_double:
                                price.double_com_price !== null
                                    ? price.double_com_price
                                    : "",
                            com_triple:
                                price.triple_com_price !== null
                                    ? price.triple_com_price
                                    : "",
                            com_quad:
                                price.quad_com_price !== null
                                    ? price.quad_com_price
                                    : "",
                            com_mono:
                                price.monoparent_com_price !== null
                                    ? price.monoparent_com_price
                                    : "",
                            com_extra_adult:
                                price.com_extra_adult !== null
                                    ? price.com_extra_adult
                                    : "",
                            com_extra_infant:
                                price.com_extra_child !== null
                                    ? price.com_extra_child
                                    : "",
                            com_extra_baby:
                                price.com_extra_baby !== null
                                    ? price.com_extra_baby
                                    : "",
                            com_extra_teen:
                                price.com_extra_teen !== null
                                    ? price.com_extra_teen
                                    : "",
                            com_price_adult: price.com_price_adult !== null
                                ? price.com_price_adult
                                : "",
                            com_price_teen: price.com_price_teen !== null
                                ? price.com_price_teen
                                : "",
                            com_price_baby: price.com_price_baby !== null
                                ? price.com_price_baby
                                : "",
                            com_price_child: price.com_price_child !== null
                                ? price.com_price_child
                                : "",
                            // markup
                            markup_single:
                                price.markup_single_price !== null
                                    ? price.markup_single_price
                                    : "",
                            markup_single_price_error: '',
                            markup_double:
                                price.double_markup_price !== null
                                    ? price.double_markup_price
                                    : "",
                            markup_triple:
                                price.triple_markup_price !== null
                                    ? price.triple_markup_price
                                    : "",
                            markup_quad:
                                price.quad_markup_price !== null
                                    ? price.quad_markup_price
                                    : "",
                            markup_mono:
                                price.monoparent_markup_price !== null
                                    ? price.monoparent_markup_price
                                    : "",
                            markup_extra_adult:
                                price.markup_extra_adult !== null
                                    ? price.markup_extra_adult
                                    : "",
                            markup_extra_infant:
                                price.markup_extra_child !== null
                                    ? price.markup_extra_child
                                    : "",
                            markup_extra_baby:
                                price.markup_extra_baby !== null
                                    ? price.markup_extra_baby
                                    : "",
                            markup_extra_teen:
                                price.markup_extra_teen !== null
                                    ? price.markup_extra_teen
                                    : "",
                            markup_price_adult: price.markup_price_adult !== null
                                ? price.markup_price_adult
                                : "",
                            markup_price_teen: price.markup_price_teen !== null
                                ? price.markup_price_teen
                                : "",
                            markup_price_baby: price.markup_price_baby !== null
                                ? price.markup_price_baby
                                : "",
                            markup_price_child: price.markup_price_child !== null
                                ? price.markup_price_child
                                : "",

                            mono_adult: price.behavior_monoparent_adult,
                            mono_child: price.behavior_monoparent_child
                        });
                    }
                }
            });
            if (room_prices.length === 0) {
                room_prices.push({ ...state.empty_room_price });
            }
            return {
                ...state,
                room_price: action.payload as Room,
                room_prices: room_prices,
            };
        }
    }
};

const setRoomPricesDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Price[] | null>
) => {
    if (action.payload === null) {
        return {
            ...state,
            room_price_duplicate: null,
            room_price_duplicate_target: null
        };
    } else if (
        state.room_price_duplicate !== null &&
        state.room_price_duplicate_target !== null
    ) {
        const room_list = [...state.room_list];
        // const prices = action.payload;
        const prices = cloneDeep(action.payload);
        state.room_price_duplicate_target.prices.map((price) => {
            if (
                price.contract !== null &&
                price.contract !== state.contract_selected?.id
            ) {
                prices.push(price);
            }
        });
        const room_price_duplicate_target = {
            ...state.room_price_duplicate_target
        };
        room_price_duplicate_target.prices = prices;
        for (let i = 0; i < room_list.length; i++) {
            if (room_list[i].id === room_price_duplicate_target.id) {
                room_list[i] = room_price_duplicate_target;
            }
        }
        return {
            ...state,
            room_list: room_list,
            room_price_duplicate: null,
            room_price_duplicate_target: null
        };
    } else {
        return { ...state, room_price_duplicate: action.payload };
    }
};

const setRunningReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    running: action.payload
});

const toggleCreateReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    create: !state.create
});

const setYieldReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean | Yield>
): ManualAccommodationState => {
    if (action.payload === true) {
        return {
            ...state,
            yield_create: true,
            yield_edit: null,
            yield_end_date: null,
            yield_name_locales: {},
            yield_rules: [{ ...EMPTY_YELD_RULE }],
            yield_reference: '',
            yield_reference_error: '',
            yield_start_date: null
        };
    } else if (action.payload === false) {
        return {
            ...state,
            yield_create: false,
            yield_edit: null,
            yield_end_date: null,
            yield_name_locales: {},
            yield_rules: [{ ...EMPTY_YELD_RULE }],
            yield_reference: '',
            yield_reference_error: '',
            yield_start_date: null
        };
    } else if (state.yield_create) {
        const yield_list = [...state.yield_list];
        yield_list.push(action.payload);
        return {
            ...state,
            yield_create: false,
            yield_edit: null,
            yield_end_date: null,
            yield_list: yield_list,
            yield_name_locales: {},
            yield_rules: [{ ...EMPTY_YELD_RULE }],
            yield_reference: '',
            yield_reference_error: '',
            yield_start_date: null
        };
    } else if (state.yield_edit) {
        const yield_list = [...state.yield_list];
        for (let i = 0; i < yield_list.length; i++) {
            if (yield_list[i].id === state.yield_edit.id) {
                yield_list[i] = action.payload;
                break;
            }
        }
        return {
            ...state,
            yield_create: false,
            yield_edit: null,
            yield_end_date: null,
            yield_list: yield_list,
            yield_name_locales: {},
            yield_rules: [{ ...EMPTY_YELD_RULE }],
            yield_reference: '',
            yield_reference_error: '',
            yield_start_date: null
        };
    } else {
        const yield_name_locales: {} & { [index: string]: any } = {};
        let has_french = false;
        action.payload.localization.map((locale) => {
            if (locale.locale === 1) {
                has_french = true;
            }
            yield_name_locales[locale.locale] = locale.name;
        });
        if (!has_french) {
            yield_name_locales['1'] =
                action.payload.name !== null ? action.payload.name : '';
        }
        const rules: YieldRule[] = [];
        action.payload.rules.map((rule) => {
            rules.push({
                amount_type: rule.price_type,
                amount: rule.value,
                days: rule.nb_days,
                type: rule.yield_type
            });
        });
        return {
            ...state,
            yield_create: false,
            yield_edit: action.payload,
            yield_end_date:
                action.payload.end_date !== null
                    ? new Date(action.payload.end_date)
                    : null,
            yield_name_locales: yield_name_locales,
            yield_reference: action.payload.reference,
            yield_reference_error: '',
            yield_rules: rules,
            yield_start_date:
                action.payload.start_date !== null
                    ? new Date(action.payload.start_date)
                    : null
        };
    }
};

const setYieldCriteriaReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationListCriteria>
): ManualAccommodationState => ({
    ...state,
    yield_criteria: action.payload
});

const setYieldDeleteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<false | null | Yield>
): ManualAccommodationState => ({
    ...state,
    yield_delete: action.payload === false ? null : action.payload,
    yield_list:
        action.payload === null && state.yield_delete !== null
            ? state.yield_list.filter(
                (element, index) =>
                    element.id !== (state.yield_delete as Yield)?.id
            )
            : state.yield_list
});

const setYieldDuplicateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Yield>
): ManualAccommodationState => ({
    ...state,
    yield_duplicate:
        state.manual_accommodation_duplicate_type === 'different_different' ||
            (state.manual_accommodation_duplicate_type === 'same_different' &&
                state.manual_accommodation_duplicate_contract_to?.id !==
                state.contract_selected?.id) ||
            (state.yield_duplicate !== null && action.payload !== null)
            ? null
            : action.payload,
    yield_list:
        state.yield_duplicate !== null && action.payload !== null
            ? [...state.yield_list, action.payload]
            : state.yield_list
});

const setYieldListReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Yield[]>
): ManualAccommodationState => ({
    ...state,
    yield_list: action.payload
});

const setYieldReferenceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    yield_reference: action.payload
});

const toggleColumnReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
) => ({
    ...state,
    columns: state.columns?.map((col, index) =>
        index === action.payload ? { ...col, checked: !col.checked } : col
    )
});

const toggleComCalculationAsPercentageReducer = (state: ManualAccommodationState): ManualAccommodationState => ({
    ...state, com_calculation_as_percentage: !state.com_calculation_as_percentage
});

const toggleContractCreateReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    contract_create: !state.contract_create,
    contract_contract: null,
    contract_contract_error: '',
    contract_provider: null
});

const toggleEditReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state
});

const toggleExportReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    export_list: action.payload
});

const toggleExportFullReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    export_full: !state.export_full
});

const toggleExtraConflictReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra>
): ManualAccommodationState => {
    const extra_conflicts: Extra[] = [...state.extra_conflicts];
    let has_extra = false;
    for (let i = 0; i < extra_conflicts.length; i++) {
        if (extra_conflicts[i].id === action.payload.id) {
            extra_conflicts.splice(i, 1);
            has_extra = true;
            break;
        }
    }
    if (!has_extra) {
        extra_conflicts.push(action.payload);
    }
    return { ...state, extra_conflicts: extra_conflicts };
};

const toggleExtraIncludedReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    extra_included: !state.extra_included
});

const toggleExtraTransferReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    extra_transfer: !state.extra_transfer
});

const toggleFactReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    facts: state.facts.map((fact, index) =>
        index === action.payload
            ? { ...fact, value_bool: !fact.value_bool }
            : fact
    )
});

const toggleFiltersReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    filter_open: !state.filter_open
});

const toggleOfferCombineReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Offer>
): ManualAccommodationState => {
    const offer_combine: Offer[] = [...state.offer_combine];
    let has_offer = false;
    for (let i = 0; i < offer_combine.length; i++) {
        if (offer_combine[i].id === action.payload.id) {
            offer_combine.splice(i, 1);
            has_offer = true;
            break;
        }
    }
    if (!has_offer) {
        offer_combine.push(action.payload);
    }
    return { ...state, offer_combine: offer_combine };
};

const toggleOfferSubRoomReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    offer_sub_room: !state.offer_sub_room
});

const togglePictureActivatedReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    giata_pictures: state.giata_pictures.map((giata_picture, index) =>
        action.payload === index
            ? { ...giata_picture, activated: !giata_picture.activated }
            : giata_picture
    )
});

const toggleRefreshReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    refresh: !state.refresh
});

const toggleRoomCancellationRuleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<CancellationRule>
): ManualAccommodationState => {
    const room_cancellation_rules: CancellationRule[] = [
        ...state.room_cancellation_rules
    ];
    let has_cancellation_rule = false;
    for (let i = 0; i < room_cancellation_rules.length; i++) {
        if (room_cancellation_rules[i].id === action.payload.id) {
            room_cancellation_rules.splice(i, 1);
            has_cancellation_rule = true;
            break;
        }
    }
    if (!has_cancellation_rule) {
        room_cancellation_rules.push(action.payload);
    }
    return {
        ...state,
        room_cancellation_rules: room_cancellation_rules
    };
};

const toggleRoomEarlyBookingReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<EarlyBooking>
): ManualAccommodationState => {
    const room_early_bookings: EarlyBooking[] = [...state.room_early_bookings];
    let has_early_booking = false;
    for (let i = 0; i < room_early_bookings.length; i++) {
        if (room_early_bookings[i].id === action.payload.id) {
            room_early_bookings.splice(i, 1);
            has_early_booking = true;
            break;
        }
    }
    if (!has_early_booking) {
        room_early_bookings.push(action.payload);
    }
    return { ...state, room_early_bookings: room_early_bookings };
};

const toggleRoomExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra>
): ManualAccommodationState => {
    const room_extras: Extra[] = [...state.room_extras];
    let has_extra = false;
    for (let i = 0; i < room_extras.length; i++) {
        if (room_extras[i].id === action.payload.id) {
            room_extras.splice(i, 1);
            has_extra = true;
            break;
        }
    }
    if (!has_extra) {
        room_extras.push(action.payload);
    }
    return { ...state, room_extras: room_extras };
};

const toggleRoomOfferReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Offer>
): ManualAccommodationState => {
    const room_offers: Offer[] = [...state.room_offers];
    let has_offer = false;
    for (let i = 0; i < room_offers.length; i++) {
        if (room_offers[i].id === action.payload.id) {
            room_offers.splice(i, 1);
            has_offer = true;
            break;
        }
    }
    if (!has_offer) {
        room_offers.push(action.payload);
    }
    return { ...state, room_offers: room_offers };
};

const toggleRoomPicturesOrderReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    room_pictures_order: !state.room_pictures_order
});

const toggleRoomYieldReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Yield>
): ManualAccommodationState => {
    const room_yields: Yield[] = [...state.room_yields];
    let has_yields = false;
    for (let i = 0; i < room_yields.length; i++) {
        if (room_yields[i].id === action.payload.id) {
            room_yields.splice(i, 1);
            has_yields = true;
            break;
        }
    }
    if (!has_yields) {
        room_yields.push(action.payload);
    }
    return { ...state, room_yields: room_yields };
};

const toggleStocksViewModeReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    stocks_display_mode: state.stocks_display_mode === 0 ? 1 : 0
});

const toggleSuperiorStarsReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    superior_stars: !state.superior_stars
});

const toggleDisplayForTripPlanReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    display_for_trip_plan: !state.display_for_trip_plan
});
const toggleDisplayForPackageReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    display_for_package: !state.display_for_package
});

const toggleTeenAdultMonoparentalReducer = (
    state: ManualAccommodationState
): ManualAccommodationState => ({
    ...state,
    is_teen_adult_in_monoparental: !state.is_teen_adult_in_monoparental
});

const updateAddressReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    address: action.payload
});

const updateAddressSearchReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<any>
): ManualAccommodationState => ({
    ...state,
    address_search: action.payload,
    google_id: action?.payload?.place_id || ''
});

const updateContactEmailReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    contact_email: action.payload
});

const updateContactFirstNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    contact_first_name: action.payload
});

const updateContactJobReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    contact_job: action.payload
});

const updateContactLastNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    contact_last_name: action.payload
});

const updateContactPhoneNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    contact_phone_number: action.payload
});

const updateContractPricingTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | PricingType>
): ManualAccommodationState => ({
    ...state,
    pricing_type: action.payload || PricingType.PURCHASE,
});

const updateContractGlobalComReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    global_com: action.payload
});

const updateContractGlobalMarkupReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    global_markup: action.payload
});

const updateContractComIsPercentageReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    com_is_percentage: action.payload
});

const updateComAppliedOnTTCReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state, com_applied_on_ttc: action.payload,
});

const updateContractMarkupIsPercentageReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    markup_is_percentage: action.payload
});


const addStayDurationReducer = (
    state: ManualAccommodationState,
): ManualAccommodationState => ({
    ...state,
    stay_duration: [...state.stay_duration, EMPTY_STAY_DURATION]
});

const deleteStayDurationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    stay_duration: state.stay_duration.filter((_, index) => index !== action.payload)
});

const updateStayDurationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number, value: StayDuration }>
): ManualAccommodationState => ({
    ...state,
    stay_duration: state.stay_duration.map((minimal_stay, index) => index === action.payload.index ? action.payload.value : minimal_stay)
});


const updateContractReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Contract | null>
): ManualAccommodationState => ({
    ...state,
    contract_contract: action.payload,
    contract_contract_error: ''
});

const updateContractsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ contract: null | Contract; index: number }>
): ManualAccommodationState => ({
    ...state,
    contract_contracts: state.contract_contracts.map(
        (contact_contract, index) =>
            index === action.payload.index
                ? action.payload.contract
                : contact_contract
    )
});

const updateContractSelectedReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Contract | null>
): ManualAccommodationState => ({
    ...state,
    contract_selected: action.payload,
    extra_list: []
});

const updateBestOfferLimitReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    best_offer_limit: action.payload
});

const updateBestOfferOrderReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'BEST_REDUCTION' | 'WORST_REDUCTION'>
): ManualAccommodationState => ({
    ...state,
    best_offer_order: action.payload
});

const updateBookingTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    booking_type: action.payload
});

const updateCancellationRuleDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateCancellationRuleConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'nb_days'
    );

const updateCancellationRuleEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_end_date: action.payload,
    cancellation_rule_end_date_error: ''
});

const updateCancellationRuleFreeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_free: action.payload
});

const updateCancellationRuleNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const cancellation_rule_name_locales = {
        ...state.cancellation_rule_name_locales
    };
    cancellation_rule_name_locales[id] = value;
    return { ...state, cancellation_rule_name_locales };
};

const updateCancellationRulePriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateCancellationRuleConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'value'
    );

const updateCancellationRulePriceTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateCancellationRuleConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_type'
    );

const updateCancellationRuleReferenceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_reference: action.payload
});

const updateCancellationRuleStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    cancellation_rule_start_date: action.payload,
    cancellation_rule_end_date: null,
    cancellation_rule_start_date_error: '',
    cancellation_rule_end_date_error: ''
});

const updateCancellationRuleTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateCancellationRuleConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'cancellation_type'
    );

const updateCatalogPriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | false>
): ManualAccommodationState => ({
    ...state,
    catalog_price: action.payload
});

const updateChainReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodationChain | null>
): ManualAccommodationState => ({
    ...state,
    chain: action.payload
});

const updateCheckInHourReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    check_in_hour: action.payload
});

const updateCheckInMinutesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    check_in_minutes: action.payload
});

const updateCheckInRecommendationLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const check_in_recommendation_locales = {
        ...state.check_in_recommendation_locales
    };
    check_in_recommendation_locales[id] = value;
    return {
        ...state,
        check_in_recommendation_locales: check_in_recommendation_locales
    };
};

const updateCheckOutHourReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    check_out_hour: action.payload
});

const updateCheckOutMinutesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    check_out_minutes: action.payload
});

const updateCheckOutRecommendationLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const check_out_recommendation_locales = {
        ...state.check_out_recommendation_locales
    };
    check_out_recommendation_locales[id] = value;
    return {
        ...state,
        check_out_recommendation_locales: check_out_recommendation_locales
    };
};

const updateChildrenLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const children_locales = { ...state.children_locales };
    children_locales[id] = value;
    return { ...state, children_locales: children_locales };
};

const updateCityReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    city: action.payload
});

const updateClientHotelIdentifierReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    client_hotel_identifier: action.payload
});

const updateIsProtectedDescriptionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    is_protected_description: action.payload
});

const updateComfortRateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    comfort_rate: action.payload
});

const updateCountryReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<IataCountry | null>
): ManualAccommodationState => ({
    ...state,
    country: action.payload
});

const updateCreateNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    create_name: action.payload
});

const updateCreateSearchCityReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    create_search_city: action.payload
});

const updateCreateSearchCountryReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<IataCountry | null>
): ManualAccommodationState => ({
    ...state,
    create_search_country: action.payload
});

const updateCreateSearchNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    create_search_name: action.payload
});

const updateCreateSearchResultsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<GiataAccommodation[] | null>
): ManualAccommodationState => ({
    ...state,
    create_search_results: action.payload,
    create_search_selected:
        action.payload !== null && action.payload.length > 0
            ? action.payload[0].id
            : -1
});

const updateCreateSearchSelectedReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    create_search_selected: action.payload
});

const updateCreateTabReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    create_tab: action.payload
});

const updateDuplicateContractTargetContractReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Contract | null>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_contract_target: action.payload
});

const updateDuplicatePriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_price: action.payload
});

const updateDuplicatePriceChangeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_price_change: action.payload,
    manual_accommodation_duplicate_price_change_type: 0,
    manual_accommodation_duplicate_price: '',
    manual_accommodation_duplicate_price_type: 'FIX'
});

const updateDuplicatePriceChangeTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_price_change_type: action.payload
});

const updateDuplicatePriceTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'FIX' | 'PER'>
): ManualAccommodationState => ({
    ...state,
    manual_accommodation_duplicate_price_type: action.payload
});

const updateEarlyBookingDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'days'
    );

const updateEarlyBookingDaysErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'days_error'
    );

const updateEarlyBookingDeductionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'deduction'
    );
const updateEarlyBookingDeductionErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'deduction_error'
    );

const updateEarlyBookingDeductionTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'deduction_type'
    );

const updateEarlyBookingEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    early_booking_end_date: action.payload,
    early_booking_end_date_error: ''
});

const updateEarlyBookingExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra[]>
): ManualAccommodationState => ({
    ...state,
    early_booking_extra: action.payload
});

const updateEarlyBookingExtraConflictsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra[]>
): ManualAccommodationState => ({
    ...state,
    early_booking_extra_conflicts: action.payload
});

const updateEarlyBookingMaxDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: Date | null; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'max_date'
    );
const updateEarlyBookingMaxDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: Date | null; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'max_date_error'
    );

const updateEarlyBookingNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string; id: number }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const early_booking_name_locales = { ...state.early_booking_name_locales };
    early_booking_name_locales[id] = value;
    return {
        ...state, early_booking_name_locales,
        early_booking_name_locales_error: ''
    };
};
const updateEarlyBookingDescriptionLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string; id: number }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const early_booking_description_locales = { ...state.early_booking_description_locales };
    early_booking_description_locales[id] = value;
    return {
        ...state, early_booking_description_locales,
        early_booking_description_locales_error: ''
    };
};

const updateEarlyBookingReferenceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    early_booking_reference: action.payload
});


const updateEarlyBookingMinDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number | null>
): ManualAccommodationState => ({
    ...state,
    early_booking_min_days: action.payload
});

const updateEarlyBookingMaxDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number | null>
): ManualAccommodationState => ({
    ...state,
    early_booking_max_days: action.payload
});

const updateEarlyBookingOfferConflictsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Offer[]>
): ManualAccommodationState => ({
    ...state,
    early_booking_offer_conflicts: action.payload
});

const updateEarlyBookingStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    early_booking_start_date: action.payload,
    early_booking_start_date_error: ''
});

const updateEarlyBookingTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateEarlyBookingConditionProperty(
        action.payload.value,
        action.payload.index,
        state,
        'type'
    );

const updateEmailReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    email: action.payload
});

const updateExtraDescriptionLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const extra_description_locales = { ...state.extra_description_locales };
    extra_description_locales[id] = value;
    return { ...state, extra_description_locales };
};

const updateExtraMaximalAgeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    extra_maximal_age: action.payload
});

const updateExtraMealPlanReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number[]>
): ManualAccommodationState => ({
    ...state,
    extra_meal_plan: action.payload
});

const updateExtraMinimalAgeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    extra_minimal_age: action.payload
});

const updateExtraMinimalTravelersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    extra_minimal_travelers: action.payload
});

const updateExtraNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const extra_name_locales = { ...state.extra_name_locales };
    extra_name_locales[id] = value;
    return { ...state, extra_name_locales };
};

const updateExtraPerDayReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    extra_per_day: action.payload
});

const updateExtraPerPersonReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    extra_per_person: action.payload,
    extra_prices: [{ ...EMPTY_EXTRA_PRICE }]
});

const updateExtraPriceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price'
    );

const updateExtraPriceAdultReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_adult'
    );

const updateExtraPriceByReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    extra_price_by: action.payload
});

const updateExtraPriceChildReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_child'
    );

const updateExtraPriceEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: Date | null; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'end_date'
    );

const updateExtraPriceInfantReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_infant'
    );

const updateExtraPriceTeenReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_teen'
    );

const updateExtraPriceStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: Date | null; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'start_date'
    );

const updateExtraPriceTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: string | number; index: number }>
): ManualAccommodationState =>
    updateExtraProperty(
        action.payload.value,
        action.payload.index,
        state,
        'price_type'
    );

const updateExtraReferenceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    extra_reference: action.payload
});

const updateExtraTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number[]>
): ManualAccommodationState => ({
    ...state,
    extra_type: action.payload,
    extra_minimal_travelers: action.payload.includes(3)
        ? state.extra_minimal_travelers
        : '',
    extra_maximal_age: action.payload.includes(1)
        ? state.extra_maximal_age
        : '',
    extra_minimal_age: action.payload.includes(1)
        ? state.extra_minimal_age
        : '',
    extra_prices:
        (state.extra_type.includes(0) && action.payload.includes(0)) ||
            (state.extra_type.includes(1) && action.payload.includes(1))
            ? state.extra_prices
            : [{ ...EMPTY_EXTRA_PRICE }]
});

const updateFactSearchReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Fact | null>
): ManualAccommodationState => ({
    ...state,
    fact_search: action.payload
});

const updateFamilyPlanReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    family_plan: action.payload
});

const updateFamilyPlanMaxReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | string>
): ManualAccommodationState => ({
    ...state,
    family_plan_max: action.payload
});

const updateFaxReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    fax: action.payload
});

const updateFilterAddressReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    filter_address: action.payload
});
const updateFilterChainReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodationChain | null>
): ManualAccommodationState => ({
    ...state,
    filter_chain: action.payload
});

const updateFilterCountryReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<IataCountry | null>
): ManualAccommodationState => ({
    ...state,
    filter_country: action.payload
});
const updateFilterCreatedDateGteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    filter_created_date_gte: action.payload
});
const updateFilterCreatedDateLteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    filter_created_date_lte: action.payload
});
const updateFilterCreatorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    filter_creator: action.payload
});
const updateFilterDestinationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    filter_destination: action.payload
});
const updateFilterLastAuthorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    filter_last_author: action.payload
});
const updateFilterModifiedDateGteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    filter_modified_date_gte: action.payload
});
const updateFilterModifiedDateLteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    filter_modified_date_lte: action.payload
});
const updateFilterNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    filter_name: action.payload
});
const updateFilterTagReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationTag[]>
): ManualAccommodationState => ({
    ...state,
    filter_tags: action.payload
});
const updateFilterPeriodReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'CREATION' | 'MODIFICATION'>
): ManualAccommodationState => ({
    ...state,
    filter_period: action.payload,
    filter_period_type: 0,
    filter_created_date_gte:
        action.payload !== 'CREATION' ? null : state.filter_created_date_gte,
    filter_created_date_lte:
        action.payload !== 'CREATION' ? null : state.filter_created_date_lte,
    filter_modified_date_gte:
        action.payload !== 'MODIFICATION'
            ? null
            : state.filter_modified_date_gte,
    filter_modified_date_lte:
        action.payload !== 'MODIFICATION'
            ? null
            : state.filter_modified_date_lte
});
const updateFilterPeriodTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    filter_period_type: action.payload
});
const updateFilterStatusReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'ALL' | 'OFFLINE' | 'ONLINE_TO' | 'ONLINE_AGENCY'>
): ManualAccommodationState => ({
    ...state,
    filter_status: action.payload
});
const updateFilterTagsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<AccommodationTag[]>
): ManualAccommodationState => ({
    ...state,
    filter_tags: action.payload
});

const updateGlobalParametersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        best_offer_limit: number;
        best_offer_order: 'BEST_REDUCTION' | 'WORST_REDUCTION';
    } | null>
): ManualAccommodationState => ({
    ...state,
    best_offer_limit: !action.payload
        ? state.manual_accommodation?.best_offer_limit || 0
        : action.payload.best_offer_limit,
    best_offer_order: !action.payload
        ? state.manual_accommodation?.best_offer_order || 'BEST_REDUCTION'
        : action.payload.best_offer_order,
    stay_duration: !action.payload && state.manual_accommodation ? state.manual_accommodation.stay_duration.map((stay) => ({ ...stay, start_date: new Date(stay?.start_date || ''), end_date: new Date(stay?.end_date || '') })) || [] : state.stay_duration.map((stay) => ({ ...stay, start_date: new Date(stay?.start_date || ''), end_date: new Date(stay?.end_date || '') })) || []
});

const updateGoogleIdReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    google_id: action.payload
});

const updateHouseNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    house_number: action.payload
});

const updateLatitudeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | string>
): ManualAccommodationState => ({
    ...state,
    latitude: action.payload
});

const updateListItemVisibilityReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodation>
): ManualAccommodationState => ({
    ...state,
    list: state.list
        ? state.list?.map((item) =>
            item.id === action.payload.id
                ? { ...item, visibility: action.payload.visibility }
                : item
        )
        : state.list
});

const updateListItemLockReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodation>
): ManualAccommodationState => ({
    ...state,
    list: state.list
        ? state.list?.map((item) =>
            item.id === action.payload.id
                ? { ...item, lock: action.payload.lock }
                : item
        )
        : state.list
});

const updateListItemPublishOnWebReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodation>
): ManualAccommodationState => ({
    ...state,
    list: state.list
        ? state.list?.map((item) =>
            item.id === action.payload.id
                ? { ...item, publish_on_web: action.payload.publish_on_web }
                : item
        )
        : state.list
});

const updateLongitudeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    longitude: action.payload
});
const updateVideoLinkReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    video_link: action.payload
});

const updateMaxAgeBabyReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    max_age_baby: action.payload
});

const updateMaxAgeChildReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    max_age_child: action.payload
});

const updateMaxAgeTeenReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    max_age_teen: action.payload
});

const updateNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    name: action.payload
});

const updateOfferAppliedForReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'ADULT' | 'CHILD' | 'ALL' | 'TEEN'>
): ManualAccommodationState => ({
    ...state,
    offer_applied_for: action.payload,
    offer_applied_for_max: '',
    offer_applied_for_min: 0,
    offer_applied_for_min_error: '',
});

const updateOfferAppliedForMaxReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_applied_for_max: action.payload
});

const updateOfferAppliedForMinReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_applied_for_min_error: '',
    offer_applied_for_min: action.payload
});

const updateOfferAppliedForMinErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_applied_for_min_error: action.payload,
});


const updateOfferBookedNightReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_booked_night: action.payload,
    offer_booked_night_number: ''
});

const updateOfferBookedNightNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_booked_night_number: action.payload
});

const updateOfferBookedRoomReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_booked_room: action.payload,
    offer_booked_room_number: ''
});

const updateOfferBookedRoomNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | string>
): ManualAccommodationState => ({
    ...state,
    offer_booked_room_number: action.payload
});

const updateOfferByReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1>
): ManualAccommodationState => ({
    ...state,
    offer_by: action.payload
});

const updateOfferCheckOutDatesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_check_out_dates: action.payload,
    offer_check_out_start_date: null,
    offer_check_out_end_date: null
});

const updateOfferCheckOutEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_check_out_end_date: action.payload
});

const updateOfferCheckOutStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_check_out_start_date: action.payload
});

const updateOfferCheckOutStartDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_check_out_start_date_error: action.payload
});

const updateOfferChildReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    offer_child: action.payload,
    offer_child_number: ''
});

const updateOfferChildNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number | string>
): ManualAccommodationState => ({
    ...state,
    offer_child_number: action.payload
});

const updateOfferCombineAccommodationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodation[]>
): ManualAccommodationState => ({
    ...state,
    offer_combine_accommodation: action.payload
});

const updateOfferCombineAccommodationChainReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<ManualAccommodationChain[]>
): ManualAccommodationState => ({
    ...state,
    offer_combine_accommodation_chain: action.payload
});

const updateOfferDescriptionLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const offer_description_locales = { ...state.offer_description_locales };
    offer_description_locales[id] = value;
    return { ...state, offer_description_locales };
};

const updateOfferEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_end_date_error: '',
    offer_end_date: action.payload
});

const updateOfferEndDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_end_date_error: action.payload
});

const updateOfferExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra | null>
): ManualAccommodationState => ({
    ...state,
    offer_extra: action.payload,
    offer_extra_error: ''
});

const updateOfferExtraErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_extra_error: action.payload
});


const updateOfferExtrasReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra[]>
): ManualAccommodationState => ({
    ...state,
    offer_extras_error: '',
    offer_extras: action.payload
});

const updateOfferExtrasErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_extras_error: action.payload,
});


const updateOfferExtraNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_extra_number_error: '',
    offer_extra_number: action.payload
});

const updateOfferExtraNumberErrorReducer = (state: ManualAccommodationState, action: PayloadAction<string>): ManualAccommodationState => ({
    ...state,
    offer_extra_number_error: action.payload,
});

const updateOfferNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const offer_name_locales = { ...state.offer_name_locales };
    offer_name_locales[id] = value;
    return { ...state, offer_name_locales, offer_name_locales_error: '' };
};

const updateOfferNightReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_night_error: '',
    offer_night: action.payload
});

const updateOfferNightTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'cheap' | 'high' | 'median'>
): ManualAccommodationState => ({
    ...state,
    offer_night_type: action.payload
});

const updateOfferReductionReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    offer_reduction_error: '',
    offer_reduction: action.payload
});

const updateOfferReductionErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_reduction_error: action.payload,
});

const updateOfferReductionTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<'FIX' | 'PER'>
): ManualAccommodationState => ({
    ...state,
    offer_reduction_type: action.payload
});

const updateOfferReferenceReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_reference: action.payload
});

const updateOfferStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_start_date_error: '',
    offer_start_date: action.payload
});

const updateOfferStartDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_start_date_error: action.payload,
});

const updateOfferTripEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_trip_end_date_error: '',
    offer_trip_end_date: action.payload
});

const updateOfferTripEndDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    offer_trip_end_date_error: action.payload
});

const updateOfferTripStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    offer_trip_start_date_error: '',
    offer_trip_start_date: action.payload
});

const updateOfferTripStartDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state, offer_trip_start_date_error: action.payload
});

const updateOfferTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1 | 2>
): ManualAccommodationState => ({
    ...state,
    offer_type: action.payload,
    offer_by: action.payload !== 0 ? 0 : state.offer_by
});

const updatePhoneNumberReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    phone_number: action.payload
});

const updatePictureOrderReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ drag_index: number; hover_index: number }>
): ManualAccommodationState => ({
    ...state,
    custom_pictures_order: state.custom_pictures_order.map((item, index) => {
        if (index === action.payload.drag_index) {
            return state.custom_pictures_order[action.payload.hover_index];
        } else if (index === action.payload.hover_index) {
            return state.custom_pictures_order[action.payload.drag_index];
        }
        return item;
    })
});

const updateProviderReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<CustomProvider | null>
): ManualAccommodationState => ({
    ...state,
    contract_provider: action.payload,
    contract_contract: null,
    contract_contract_error: ''
});

const updateProvidersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ provider: null | CustomProvider; index: number }>
): ManualAccommodationState => ({
    ...state,
    contract_providers: state.contract_providers.map(
        (contact_provider, index) =>
            index === action.payload.index
                ? action.payload.provider
                : contact_provider
    ),
    contract_contracts: state.contract_contracts.map(
        (contact_contract, index) =>
            index === action.payload.index ? null : contact_contract
    )
});

const updateTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    type: action.payload
});

const updateRecommendationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<0 | 1 | 2>
): ManualAccommodationState => ({
    ...state,
    recommendation: action.payload
});

const updateRestorationRateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    restoration_rate: action.payload
});

const updateRestorationLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const restoration_locales = { ...state.restoration_locales };
    restoration_locales[id] = value;
    return { ...state, restoration_locales: restoration_locales };
};

const updateRoomBeddingReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: RoomBedding; key: string; index: number }>
): ManualAccommodationState => {
    const { value, key, index } = action.payload;
    const room_bedding = [...state.room_bedding];
    const bedding = { ...room_bedding[index] };
    bedding[key] = value;
    room_bedding[index] = bedding;
    return { ...state, room_bedding: room_bedding };
};

const updateRoomBlackOutsTargetReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Room>
): ManualAccommodationState => ({
    ...state,
    room_black_outs_target: action.payload
});

const updateRoomBlackOutEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ value: null | Date; index: number }>
): ManualAccommodationState =>
    updateRoomBlackOutProperty(
        action.payload.value,
        action.payload.index,
        state,
        'end_date'
    );

const updateRoomBlackOutEndDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string }>
): ManualAccommodationState =>
    updateRoomBlackOutProperty(
        action.payload.value,
        action.payload.index,
        state,
        'end_date_error'
    );

const updateRoomBlackOutStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: Date | null }>
): ManualAccommodationState =>
    updateRoomBlackOutProperty(
        action.payload.value,
        action.payload.index,
        state,
        'start_date'
    );

const updateRoomBlackOutStartDateErrorReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string }>
): ManualAccommodationState =>
    updateRoomBlackOutProperty(
        action.payload.value,
        action.payload.index,
        state,
        'start_date_error'
    );

const updateRoomCancellationRulesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<CancellationRule[]>
): ManualAccommodationState => ({
    ...state,
    room_cancellation_rules: action.payload
});

const updateRoomCategoryReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    room_category: action.payload,
    room_category_error: ''
});

const updateRoomDescLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const room_desc_locales = { ...state.room_desc_locales };
    room_desc_locales[id] = value;
    return { ...state, room_desc_locales: room_desc_locales };
};

const updateRoomDescriptionLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const room_description_locales = {
        ...state.room_description_locales
    };
    room_description_locales[id] = value;
    return {
        ...state,
        room_description_locales: room_description_locales
    };
};

const updateRoomEarlyBookingsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<EarlyBooking[]>
): ManualAccommodationState => ({
    ...state,
    room_early_bookings: action.payload
});

const updateRoomExtrasReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Extra[]>
): ManualAccommodationState => ({
    ...state,
    room_extras: action.payload
});

const updateRoomLockedDurationReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_locked_duration: action.payload
});

const updateRoomNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const room_name_locales = { ...state.room_name_locales };
    room_name_locales[id] = value;
    return { ...state, room_name_locales: room_name_locales };
};

const updateRoomMainReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<true | Room | null>
): ManualAccommodationState => ({
    ...state,
    room_main: action.payload
});

const updateRoomMaxAdultsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_max_adults: action.payload
});

const updateRoomMaxChildrenReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_max_children: action.payload
});

const updateRoomMaxNightsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_max_nights: action.payload
});

const updateRoomMaxTravelersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_max_travelers: action.payload
});

const updateRoomMinAdultsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_min_adults: action.payload
});

const updateRoomMinChildrenReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_min_children: action.payload
});

const updateRoomMinNightsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_min_nights: action.payload
});

const updateRoomMinStayPeriodEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: null | Date }>
): ManualAccommodationState =>
    updateRoomMinStayPeriodProperty(
        action.payload.value,
        action.payload.index,
        state,
        'end_date'
    );

const updateRoomMinStayPeriodStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: null | Date }>
): ManualAccommodationState =>
    updateRoomMinStayPeriodProperty(
        action.payload.value,
        action.payload.index,
        state,
        'start_date'
    );

const updateRoomMinStayPeriodNightsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string | number }>
): ManualAccommodationState =>
    updateRoomMinStayPeriodProperty(
        action.payload.value,
        action.payload.index,
        state,
        'nights'
    );

const updateRoomMinTravelersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    room_min_travelers: action.payload
});

const updateRoomOffersReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Offer[]>
): ManualAccommodationState => ({
    ...state,
    room_offers: action.payload
});

const updateRoomPictureOrderReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ drag_index: number; hover_index: number }>
): ManualAccommodationState => ({
    ...state,
    room_pictures: state.room_pictures.map((item, index) => {
        if (index === action.payload.drag_index) {
            return state.room_pictures[action.payload.hover_index];
        } else if (index === action.payload.hover_index) {
            return state.room_pictures[action.payload.drag_index];
        }
        return item;
    })
});

const updateRoomPriceDoubleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState => updateRoomPriceProperty(
    action.payload.price,
    action.payload.price_index,
    state,
    `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}double${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
);

const updateRoomPriceExtraAdultReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}extra_adult${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceExtraBabyReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}extra_baby${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceExtraInfantReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}extra_infant${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceExtraTeenReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}extra_teen${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceMonoReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}mono${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceMonoAdultReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        type: 'MNPARENT_SINGLE' | 'MNPARENT_MONO' | 'MNPARENT_NORMAL';
        price_index: number;
    }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.type,
        action.payload.price_index,
        state,
        'mono_adult'
    );

const updateRoomPriceMonoChildReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        type:
        | 'CHILD_AS_ADT_ZERO'
        | 'ALL_CHILD_DOUBLE'
        | 'CHILD_AS_ADT_ONE'
        | 'CHILD_AS_ADT_TWO'
        | 'CHILD_AS_ADT_THREE';
        price_index: number;
    }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.type,
        action.payload.price_index,
        state,
        'mono_child'
    );

const updateRoomPricePeriodDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        days: number;
        price_index: number;
        period_index: number;
    }>
): ManualAccommodationState =>
    updateRoomPricePeriodProperty(
        action.payload.days,
        action.payload.price_index,
        action.payload.period_index,
        state,
        'days'
    );

const updateRoomPricePeriodEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        date: null | Date;
        price_index: number;
        period_index: number;
    }>
): ManualAccommodationState =>
    updateRoomPricePeriodProperty(
        action.payload.date,
        action.payload.price_index,
        action.payload.period_index,
        state,
        'end_date'
    );

const updateRoomPricePeriodStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        date: null | Date;
        price_index: number;
        period_index: number;
    }>
): ManualAccommodationState =>
    updateRoomPricePeriodProperty(
        action.payload.date,
        action.payload.price_index,
        action.payload.period_index,
        state,
        'start_date'
    );

const updateRoomPriceQuadReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}quad${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceRestitutionDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ days: number; price_index: number }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.days,
        action.payload.price_index,
        state,
        'restitution_days'
    );

const updateRoomPriceSingleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}single${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPricePerPersonReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType, person_type: PersonType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}price_${action.payload.person_type}${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomPriceStockReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ stock: number; price_index: number }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.stock,
        action.payload.price_index,
        state,
        'stock'
    );

const updateRoomPriceTargetReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | Room>
): ManualAccommodationState => ({
    ...state,
    room_price_duplicate_target: action.payload
});

const updateRoomPriceTripleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ price: string | number; price_index: number, price_to_type: PriceToType }>
): ManualAccommodationState =>
    updateRoomPriceProperty(
        action.payload.price,
        action.payload.price_index,
        state,
        `${action.payload.price_to_type}${action.payload.price_to_type !== '' ? '_' : ''}triple${action.payload.price_to_type.includes('ttc') ? '_ttc' : ''}`
    );

const updateRoomProhibitedCompositionsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        index: number;
        type: 0 | 1;
        value: string | number;
    }>
): ManualAccommodationState => {
    const { index, type, value } = action.payload;
    const prohibited_compositions = [...state.room_prohibited_compositions];
    const composition = [...prohibited_compositions[index]];
    composition[type] = value;
    prohibited_compositions[index] = composition as [
        string | number,
        string | number
    ];
    return {
        ...state,
        room_prohibited_compositions: prohibited_compositions
    };
};

const updateRoomSizeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    room_size: action.payload
});

const updateRoomYieldsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Yield[]>
): ManualAccommodationState => ({
    ...state,
    room_yields: action.payload
});

const updateSituationLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const situation_locales = { ...state.situation_locales };
    situation_locales[id] = value;
    return { ...state, situation_locales: situation_locales };
};

const updateSituationRateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    situation_rate: action.payload
});

const updateStarsReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    stars: action.payload
});

const updateStocksClosedHoverDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    stocks_closed_sale_hover_date: action.payload
});

const updateStocksClosedSaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ room_id: number; date: number }>
): ManualAccommodationState => {
    const { room_id, date } = action.payload;
    if (state.stocks_closed_sale_start_date === null) {
        return {
            ...state,
            stocks_closed_sale_room_id: room_id,
            stocks_closed_sale_start_date: date
        };
    } else if (state.stocks_closed_sale_room_id === room_id) {
        return {
            ...state,
            stocks_closed_sale_room_id: room_id,
            stocks_closed_sale_end_date: date
        };
    }
    return state;
};

const updateStocksCurrentDayReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    stocks_current_day: action.payload
});

const updateStocksCurrentMonthReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    stocks_current_month: action.payload
});

const updateStocksEditModeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<null | 'STOCK' | 'ROOM_BLACK_OUTS'>
): ManualAccommodationState => ({
    ...state,
    stocks_edit_mode:
        state.stocks_edit_mode === action.payload ? null : action.payload,
    stocks_closed_sale_start_date: null,
    stocks_closed_sale_end_date: null,
    stocks_closed_sale_hover_date: null,
    stocks_closed_sale_room_id: null,
    stocks_stock_room_id: null,
    stocks_stock_index: null,
    stocks_stock_value: '',
    stocks_stock_price: null
});

const updateStocksRoomBlackOutReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Room>
): ManualAccommodationState => ({
    ...state,
    room_list: state.room_list.map((room, index) =>
        room.id === action.payload.id ? action.payload : room
    ),
    stocks_state: state.stocks_state
        ? {
            ...state.stocks_state,
            rooms: state.stocks_state.rooms.map((room, index) =>
                room.id === action.payload.id
                    ? { ...room, black_out: action.payload.black_out }
                    : room
            )
        }
        : null,
    stocks_closed_sale_start_date: null,
    stocks_closed_sale_end_date: null,
    stocks_closed_sale_room_id: null,
    stocks_closed_sale_hover_date: null
});

const updateStocksRoomStockReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Price[]>
): ManualAccommodationState => {
    const stocks_state = cloneDeep(state.stocks_state);
    const rooms = stocks_state ? cloneDeep(stocks_state.rooms) : [];

    console.log(stocks_state?.rooms);
    action.payload.forEach(_price => {
        if (stocks_state) {
            stocks_state.rooms = rooms.map((room) => ({
                ...room,
                stocks: room.stocks.map((stock: any) => stock?.price?.id === _price.id ? { ...stock, price: _price } : stock),
                prices: room.prices.map((price: any) => price.id === _price.id ? _price : price)
            }));
        }
    });
    console.log(stocks_state?.rooms);
    return {
        ...state,
        stocks_state: stocks_state,
        stocks_stock_room_id: null,
        stocks_stock_index: null,
        stocks_stock_value: '',
        stocks_stock_price: null
    };
};

const updateRoomStockWithoutCloseReducer = (state: ManualAccommodationState, action: PayloadAction<Price[]>): ManualAccommodationState => {
    const stocks_state = cloneDeep(state.stocks_state);
    const rooms = stocks_state ? cloneDeep(stocks_state.rooms) : [];

    console.log(stocks_state?.rooms);
    action.payload.forEach(_price => {
        if (stocks_state) {
            stocks_state.rooms = rooms.map((room) => ({
                ...room,
                stocks: room.stocks.map((stock: any) => stock?.price?.id === _price.id ? { ...stock, price: _price } : stock),
                prices: room.prices.map((price: any) => price.id === _price.id ? _price : price)
            }));
        }
    });
    console.log(stocks_state?.rooms);

    return {
        ...state,
        stocks_state: stocks_state
    };
};

const updateStocksStateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Stocks[]>
): ManualAccommodationState => {
    if (state.contract_selected !== null) {
        const stock_state = action.payload;
        const start_date = new Date(state.contract_selected.start_date || '');
        const end_date = new Date(state.contract_selected.end_date || '');
        const dates: number[] = [];
        let current_date = start_date;
        while (current_date <= end_date) {
            dates.push(current_date.setHours(0, 0, 0, 0));
            current_date = new Date(
                current_date.setDate(current_date.getDate() + 1)
            );
        }
        const rooms: (Room & {
            stocks: (
                | (Stocks & { price: {} })
                | { id: null; date: number; price: null | {} }
            )[];
        })[] = [];
        state.room_list.map((room) => {
            const room_stock: Room & {
                stocks: (
                    | (Stocks & { price: null | {} })
                    | { id: null; date: number; price: null | {} }
                )[];
            } = { ...room, stocks: [] };
            dates.map((date) => {
                let found = false;
                stock_state.map((stock) => {
                    if (
                        stock.room === room.id &&
                        isSameDay(new Date(date), new Date(stock.date)) && !found && !stock.for_user
                    ) {
                        let stock_price: Price | null = null;
                        room.prices.map((price) => {
                            if (stock.stock === price.id) {
                                stock_price = price;
                            }
                        });
                        (
                            room_stock.stocks as (Stocks & {
                                price: null | {};
                            })[]
                        ).push({
                            ...stock,
                            date: date,
                            price: stock_price
                        });
                        found = true;
                    }
                });
                if (!found) {
                    let price_found = false;
                    room.prices.map((price) => {
                        if (
                            !price_found &&
                            state.contract_selected?.id === price.contract &&
                            date >=
                            new Date(price.start_date).setHours(
                                0,
                                0,
                                0,
                                0
                            ) &&
                            date <=
                            new Date(price.end_date).setHours(0, 0, 0, 0)
                        ) {
                            room_stock.stocks.push({
                                id: null,
                                date: date,
                                price: price
                            });
                            price_found = true;
                        }
                    });
                    if (!price_found) {
                        room_stock.stocks.push({
                            id: null,
                            date: date,
                            price: null
                        });
                    }
                }
            });
            rooms.push(room_stock);
        });
        return {
            ...state,
            stocks_current_day: null,
            stocks_current_month: start_date,
            stocks_state: { dates: dates, rooms: rooms }
        };
    }
    return state;
};

const updateStocksStockReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{
        room_id: null | number;
        stock: null | any;
        index: null | number;
        stock_value: string | number;
    }>
): ManualAccommodationState => ({
    ...state,
    stocks_stock_room_id: action.payload.room_id,
    stocks_stock_price: action.payload.stock,
    stocks_stock_index: action.payload.index,
    stocks_stock_value: action.payload.stock_value,
});

const updateStocksStockValueReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    stocks_stock_value: action.payload
});

const updateTaxesFeesReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_fees: action.payload
});

const updateTaxesMealReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_meal: action.payload
});

const updateTaxesMealVatReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_meal_vat: action.payload
});

const updateTaxesResortReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_resort: action.payload
});

const updateTaxesResortVatReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_resort_vat: action.payload
});

const updateTaxesTouristicReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_touristic: action.payload
});

const updateTaxesTouristicVatReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state,
    taxes_touristic_vat: action.payload
});

const updateVatReducer = (
    state: ManualAccommodationState, action: PayloadAction<string | number>
): ManualAccommodationState => ({
    ...state, vat: action.payload
});

const updateWebsiteReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    website: action.payload
});

const updateYieldEndDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    yield_end_date: action.payload,
    yield_end_date_error: ''
});

const updateYieldNameLocaleReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ id: number; value: string }>
): ManualAccommodationState => {
    const { id, value } = action.payload;
    const yield_name_locales = { ...state.yield_name_locales };
    yield_name_locales[id] = value;
    return { ...state, yield_name_locales };
};

const updateYieldRuleAmountReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string | number }>
): ManualAccommodationState =>
    updateYieldRuleProperty(
        action.payload.value,
        action.payload.index,
        state,
        'amount'
    );

const updateYieldRuleAmountTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string | number }>
): ManualAccommodationState =>
    updateYieldRuleProperty(
        action.payload.value,
        action.payload.index,
        state,
        'amount_type'
    );

const updateYieldRuleDaysReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string | number }>
): ManualAccommodationState =>
    updateYieldRuleProperty(
        action.payload.value,
        action.payload.index,
        state,
        'days'
    );

const updateYieldRuleTypeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<{ index: number; value: string | number }>
): ManualAccommodationState =>
    updateYieldRuleProperty(
        action.payload.value,
        action.payload.index,
        state,
        'type'
    );

const updateYieldStartDateReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<Date | null>
): ManualAccommodationState => ({
    ...state,
    yield_start_date: action.payload,
    yield_start_date_error: ''
});

const updateZipCodeReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    zip_code: action.payload
});

const updateLockReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    lock: action.payload
});

const updatePublishOnWebReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<boolean>
): ManualAccommodationState => ({
    ...state,
    publish_on_web: action.payload
});

const updateDestinationNameReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<string>
): ManualAccommodationState => ({
    ...state,
    destination_name: action.payload
});

const updateZoomReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<number>
): ManualAccommodationState => ({
    ...state,
    zoom: action.payload
});
const initCustomPictureReducer = (
    state: ManualAccommodationState,
): ManualAccommodationState => ({
    ...state,
    custom_pictures: [],
    room_pictures: []
});
const addCustomPicturesExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<any>
): ManualAccommodationState =>
    state.edit === 'SITUATION' || state.edit === 'ROOMS_DESCRIPTION'
        ? { ...state, editor_pictures: [...action.payload] }
        : state.edit_step === 'ROOMS'
            ? {
                ...state,
                room_pictures: [...state.room_pictures, ...action.payload]
            }
            : {
                ...state,
                custom_pictures: [...state.custom_pictures, ...action.payload]
            };

const setPictureExtraReducer = (
    state: ManualAccommodationState,
    action: PayloadAction<any | Picture>
): ManualAccommodationState =>
    action.payload !== false && action.payload !== true
        ? state.edit_step === 'ROOMS'
            ? {
                ...state,
                room_picture: state.room_pictures.map((room_picture, index) =>
                    room_picture.id === (action.payload as Picture).id
                        ? action.payload
                        : room_picture
                )
            }
            : {
                ...state,
                custom_pictures: state.custom_pictures.map(
                    (custom_picture, index) =>
                        custom_picture.id === (action.payload as Picture).id
                            ? (action.payload as Picture)
                            : custom_picture
                )
            }
        : { ...state };

const updateStockRoomProductInfoReducer = (state: ManualAccommodationState, action: PayloadAction<StockRoomProductInfo | null>): ManualAccommodationState => ({
    ...state, stock_room_product_info: action.payload, stock_room_products_loading: false
});

const updateStockRoomProductReducer = (state: ManualAccommodationState, action: PayloadAction<RoomProduct[] | null>): ManualAccommodationState => ({
    ...state,
    stock_room_products: action.payload,
    stock_room_products_loading: false
});

const loadStockRoomProductReducer = (state: ManualAccommodationState): ManualAccommodationState => ({
    ...state, stock_room_products_loading: true
});
const setColumnsReducer = (state: ManualAccommodationState, action: PayloadAction<Column[]>): ManualAccommodationState => ({
    ...state, columns: action.payload
});

const setMarginMasterMarkupReducer = (state: ManualAccommodationState, action: PayloadAction<boolean>): ManualAccommodationState => ({
    ...state, margin_master_markup: action.payload
});
const updateAccommodationTagsReducer = (state: ManualAccommodationState, action: PayloadAction<Record<number, AccommodationTag>>): ManualAccommodationState => ({
    ...state, accommodation_tags: action.payload
});

export const manualAccommodation = createSlice({
    name: 'manualAccommodation',
    initialState,
    reducers: {
        addCancellationRuleCondition: addCancellationRuleConditionReducer,
        addEarlyBookingCondition: addEarlyBookingConditionReducer,
        addExtraIntervalAgePrices: addExtraIntervalAgePricesReducer,
        addExtraPrice: addExtraPriceReducer,
        addOnContracts: addOnContractsReducer,
        addPreferences: addPreferencesReducer,
        addRoomBedding: addRoomBeddingReducer,
        addRoomBlackOut: addRoomBlackOutReducer,
        addRoomMinStayPeriod: addRoomMinStayPeriodReducer,
        addRoomPrice: addRoomPriceReducer,
        addRoomPricePeriod: addRoomPricePeriodReducer,
        addRoomProhibitedComposition: addRoomProhibitedCompositionReducer,
        addYieldRule: addYieldRuleReducer,
        deleteCancellationRuleCondition: deleteCancellationRuleConditionReducer,
        deleteEarlyBookingCondition: deleteEarlyBookingConditionReducer,
        deleteCustomPicture: deleteCustomPictureReducer,
        deleteExtraIntervalAgePrices: deleteExtraIntervalAgePricesReducer,
        deleteExtraPrice: deleteExtraPriceReducer,
        deleteOnContracts: deleteOnContractsReducer,
        deleteRoomBedding: deleteRoomBeddingReducer,
        deleteRoomBlackOut: deleteRoomBlackOutReducer,
        deleteRoomMinStayPeriod: deleteRoomMinStayPeriodReducer,
        deleteRoomPrice: deleteRoomPriceReducer,
        deleteRoomPricePeriod: deleteRoomPricePeriodReducer,
        deleteRoomProhibitedComposition: deleteRoomProhibitedCompositionReducer,
        deleteRoomPicture: deleteRoomPictureReducer,
        deleteYieldRule: deleteYieldRuleReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        refreshCancellationRuleSortedList:
            refreshCancellationRuleSortedListReducer,
        refreshEarlyBookingSortedList: refreshEarlyBookingSortedListReducer,
        refreshExtraSortedList: refreshExtraSortedListReducer,
        refreshOfferSortedList: refreshOfferSortedListReducer,
        refreshRoomSortedList: refreshRoomSortedListReducer,
        refreshYieldSortedList: refreshYieldSortedListReducer,
        resetContracts: resetContractsReducer,
        resetCreate: resetCreateReducer,
        resetFilters: resetFiltersReducer,
        resetManualAccommodation: resetManualAccommodationReducer,
        setCancellationRule: setCancellationRuleReducer,
        setCancellationRuleCriteria: setCancellationRuleCriteriaReducer,
        setCancellationRuleDelete: setCancellationRuleDeleteReducer,
        setCancellationRuleDuplicate: setCancellationRuleDuplicateReducer,
        setCancellationRuleList: setCancellationRuleListReducer,
        setContactDelete: setContactDeleteReducer,
        setContractParameters: setContractParametersReducer,
        setEdit: setEditReducer,
        setEditStep: setEditStepReducer,
        setErrors: setErrorsReducer,
        setExportType: setExportTypeReducer,
        setEarlyBooking: setEarlyBookingReducer,
        setEarlyBookingCriteria: setEarlyBookingCriteriaReducer,
        setEarlyBookingDelete: setEarlyBookingDeleteReducer,
        setEarlyBookingDuplicate: setEarlyBookingDuplicateReducer,
        setEarlyBookingList: setEarlyBookingListReducer,
        setExtra: setExtraReducer,
        setExtraCriteria: setExtraCriteriaReducer,
        setExtraDelete: setExtraDeleteReducer,
        setExtraDuplicate: setExtraDuplicateReducer,
        setExtraIntervalAgePrices: setExtraIntervalAgePricesReducer,
        setExtraList: setExtraListReducer,
        setLimit: setLimitReducer,
        setList: setListReducer,
        setManualAccommodation: setManualAccommodationReducer,
        setManualAccommodationContacts: setManualAccommodationContactsReducer,
        setManualAccommodationDuplicate: setManualAccommodationDuplicateReducer,
        setManualAccommodationDuplicateStep:
            setManualAccommodationDuplicateStepReducer,
        setManualAccommodationDuplicateType:
            setManualAccommodationDuplicateTypeReducer,
        setManualAccommodationDuplicateAccommodationTo:
            setManualAccommodationDuplicateAccommodationToReducer,
        setManualAccommodationDuplicateContractTo:
            setManualAccommodationDuplicateContractToReducer,
        setManualAccommodationMealTaxes: setManualAccommodationMealTaxesReducer,
        setManualAccommodationTaxes: setManualAccommodationTaxesReducer,
        setManualAccommodationDuplicateMultiple:
            setManualAccommodationDuplicateMultipleReducer,
        setManualAccommodationDuplicateMultipleItems:
            setManualAccommodationDuplicateMultipleItemsReducer,
        setManualAccommodationDuplicateMultipleExtraModal:
            setManualAccommodationDuplicateMultipleExtraModalReducer,
        setManualAccommodationDuplicateMultipleCancellationRuleModal:
            setManualAccommodationDuplicateMultipleCancellationRuleModalReducer,
        setManualAccommodationDuplicateMultipleEarlyBookingModal:
            setManualAccommodationDuplicateMultipleEarlyBookingModalReducer,
        setManualAccommodationDuplicateMultipleOfferModal:
            setManualAccommodationDuplicateMultipleOfferModalReducer,
        setManualAccommodationDuplicateMultipleYieldModal:
            setManualAccommodationDuplicateMultipleYieldModalReducer,
        setOrderBy: setOrderByReducer,
        setOffer: setOfferReducer,
        setOfferCriteria: setOfferCriteriaReducer,
        setOfferDelete: setOfferDeleteReducer,
        setOfferDuplicate: setOfferDuplicateReducer,
        setOfferList: setOfferListReducer,
        setOfferIntervalAgeOffers: setOfferIntervalAgeOffersReducer,
        addOfferIntervalAgeOffers: addOfferIntervalAgeOffersReducer,
        deleteOfferIntervalAgeOffers: deleteOfferIntervalAgeOffersReducer,
        setOfferIsHoneymoon: setOfferIsHoneymoonReducer,
        setOfferIsOnExtraApplied: setOfferIsOnExtraAppliedReducer,
        setOfferOnExtraApplied: setOfferOnExtraAppliedReducer,
        setPage: setPageReducer,
        setPreferences: setPreferencesReducer,
        setRoom: setRoomReducer,
        setRoomsBlackOut: setRoomsBlackOutReducer,
        setRoomsUnBlackOut: setRoomsUnBlackOutReducer,
        setRoomBlackOutsDuplicate: setRoomBlackOutsDuplicateReducer,
        setRoomBlackOutsRoom: setRoomBlackOutsRoomReducer,
        setRoomCriteria: setRoomCriteriaReducer,
        setRoomDelete: setRoomDeleteReducer,
        setRoomDuplicate: setRoomDuplicateReducer,
        setRoomList: setRoomListReducer,
        setRoomPriceScrollId: setRoomPriceScrollIdReducer,
        setEmptyRoomPrice: setEmptyRoomPriceReducer,
        setIsRoomPricesEmpty: setIsRoomPricesEmptyReducer,
        setRoomPrices: setRoomPricesReducer,
        setRoomPricesDuplicate: setRoomPricesDuplicateReducer,
        setRunning: setRunningReducer,
        setSearch: setSearchReducer,
        setYield: setYieldReducer,
        setYieldCriteria: setYieldCriteriaReducer,
        setYieldDelete: setYieldDeleteReducer,
        setYieldDuplicate: setYieldDuplicateReducer,
        setYieldList: setYieldListReducer,
        setYieldReference: setYieldReferenceReducer,
        toggleColumn: toggleColumnReducer,
        toggleComCalculationAsPercentage: toggleComCalculationAsPercentageReducer,
        toggleContractCreate: toggleContractCreateReducer,
        toggleCreate: toggleCreateReducer,
        toggleEdit: toggleEditReducer,
        toggleExport: toggleExportReducer,
        toggleExportFull: toggleExportFullReducer,
        toggleExtraConflict: toggleExtraConflictReducer,
        toggleExtraIncluded: toggleExtraIncludedReducer,
        toggleExtraTransfer: toggleExtraTransferReducer,
        toggleFact: toggleFactReducer,
        toggleFilters: toggleFiltersReducer,
        toggleOfferCombine: toggleOfferCombineReducer,
        toggleOfferSubRoom: toggleOfferSubRoomReducer,
        togglePictureActivated: togglePictureActivatedReducer,
        toggleRefresh: toggleRefreshReducer,
        toggleRoomCancellationRule: toggleRoomCancellationRuleReducer,
        toggleRoomEarlyBooking: toggleRoomEarlyBookingReducer,
        toggleRoomExtra: toggleRoomExtraReducer,
        toggleRoomOffer: toggleRoomOfferReducer,
        toggleRoomPicturesOrder: toggleRoomPicturesOrderReducer,
        toggleRoomYield: toggleRoomYieldReducer,
        toggleStocksViewMode: toggleStocksViewModeReducer,
        toggleSuperiorStars: toggleSuperiorStarsReducer,
        toggleDisplayForTripPlan: toggleDisplayForTripPlanReducer,
        toggleDisplayForPackage: toggleDisplayForPackageReducer,
        toggleTeenAdultMonoparental: toggleTeenAdultMonoparentalReducer,
        updateAddress: updateAddressReducer,
        updateAddressSearch: updateAddressSearchReducer,
        updateBestOfferLimit: updateBestOfferLimitReducer,
        updateBestOfferOrder: updateBestOfferOrderReducer,
        updateBookingType: updateBookingTypeReducer,
        updateCancellationRuleDays: updateCancellationRuleDaysReducer,
        updateCancellationRuleEndDate: updateCancellationRuleEndDateReducer,
        updateCancellationRuleFree: updateCancellationRuleFreeReducer,
        // updateCancellationRuleName,
        updateCancellationRuleNameLocale:
            updateCancellationRuleNameLocaleReducer,
        updateCancellationRulePrice: updateCancellationRulePriceReducer,
        updateCancellationRulePriceType: updateCancellationRulePriceTypeReducer,
        updateCancellationRuleReference: updateCancellationRuleReferenceReducer,
        updateCancellationRuleStartDate: updateCancellationRuleStartDateReducer,
        updateCancellationRuleType: updateCancellationRuleTypeReducer,
        updateCatalogPrice: updateCatalogPriceReducer,
        updateChain: updateChainReducer,
        updateCheckInHour: updateCheckInHourReducer,
        updateCheckInMinutes: updateCheckInMinutesReducer,
        // updateCheckInRecommendation,
        updateCheckInRecommendationLocale:
            updateCheckInRecommendationLocaleReducer,
        updateCheckOutHour: updateCheckOutHourReducer,
        updateCheckOutMinutes: updateCheckOutMinutesReducer,
        // updateCheckOutRecommendation,
        updateCheckOutRecommendationLocale:
            updateCheckOutRecommendationLocaleReducer,
        updateChildrenLocale: updateChildrenLocaleReducer,
        updateCity: updateCityReducer,
        updateClientHotelIdentifier: updateClientHotelIdentifierReducer,
        updateIsProtectedDescription: updateIsProtectedDescriptionReducer,
        updateComfortRate: updateComfortRateReducer,
        updateContactEmail: updateContactEmailReducer,
        updateContactFirstName: updateContactFirstNameReducer,
        updateContactJob: updateContactJobReducer,
        updateContactLastName: updateContactLastNameReducer,
        updateContactPhoneNumber: updateContactPhoneNumberReducer,
        updateContractPricingType: updateContractPricingTypeReducer,
        updateComAppliedOnTTC: updateComAppliedOnTTCReducer,
        updateContractGlobalCom: updateContractGlobalComReducer,
        updateContractGlobalMarkup: updateContractGlobalMarkupReducer,
        updateContractComIsPercentage: updateContractComIsPercentageReducer,
        updateContractMarkupIsPercentage: updateContractMarkupIsPercentageReducer,
        addStayDuration: addStayDurationReducer,
        deleteStayDuration: deleteStayDurationReducer,
        updateStayDuration: updateStayDurationReducer,
        updateContract: updateContractReducer,
        updateContracts: updateContractsReducer,
        updateContractSelected: updateContractSelectedReducer,
        updateCountry: updateCountryReducer,
        updateCreateName: updateCreateNameReducer,
        updateCreateSearchCity: updateCreateSearchCityReducer,
        updateCreateSearchCountry: updateCreateSearchCountryReducer,
        updateCreateSearchName: updateCreateSearchNameReducer,
        updateCreateSearchResults: updateCreateSearchResultsReducer,
        updateCreateSearchSelected: updateCreateSearchSelectedReducer,
        updateCreateTab: updateCreateTabReducer,
        updateDuplicateContractTargetContract:
            updateDuplicateContractTargetContractReducer,
        // updateDuplicateContractTargetProvider,
        updateDuplicatePrice: updateDuplicatePriceReducer,
        updateDuplicatePriceChange: updateDuplicatePriceChangeReducer,
        updateDuplicatePriceChangeType: updateDuplicatePriceChangeTypeReducer,
        updateDuplicatePriceType: updateDuplicatePriceTypeReducer,
        updateEarlyBookingDays: updateEarlyBookingDaysReducer,
        updateEarlyBookingDaysError: updateEarlyBookingDaysErrorReducer,
        updateEarlyBookingDeduction: updateEarlyBookingDeductionReducer,
        updateEarlyBookingDeductionError: updateEarlyBookingDeductionErrorReducer,
        updateEarlyBookingDeductionType: updateEarlyBookingDeductionTypeReducer,
        updateEarlyBookingEndDate: updateEarlyBookingEndDateReducer,
        updateEarlyBookingExtra: updateEarlyBookingExtraReducer,
        updateEarlyBookingExtraConflicts:
            updateEarlyBookingExtraConflictsReducer,
        updateEarlyBookingMaxDate: updateEarlyBookingMaxDateReducer,
        updateEarlyBookingMaxDateError: updateEarlyBookingMaxDateErrorReducer,
        updateEarlyBookingNameLocale: updateEarlyBookingNameLocaleReducer,
        updateEarlyBookingDescriptionLocale: updateEarlyBookingDescriptionLocaleReducer,
        updateEarlyBookingReference: updateEarlyBookingReferenceReducer,
        updateEarlyBookingMinDays: updateEarlyBookingMinDaysReducer,
        updateEarlyBookingMaxDays: updateEarlyBookingMaxDaysReducer,
        updateEarlyBookingOfferConflicts:
            updateEarlyBookingOfferConflictsReducer,
        updateEarlyBookingStartDate: updateEarlyBookingStartDateReducer,
        updateEarlyBookingType: updateEarlyBookingTypeReducer,
        updateEmail: updateEmailReducer,
        // updateExtraDescription,
        updateExtraDescriptionLocale: updateExtraDescriptionLocaleReducer,
        updateExtraMaximalAge: updateExtraMaximalAgeReducer,
        updateExtraMealPlan: updateExtraMealPlanReducer,
        updateExtraMinimalAge: updateExtraMinimalAgeReducer,
        updateExtraMinimalTravelers: updateExtraMinimalTravelersReducer,
        // updateExtraName,
        updateExtraNameLocale: updateExtraNameLocaleReducer,
        updateExtraPerDay: updateExtraPerDayReducer,
        updateExtraPerPerson: updateExtraPerPersonReducer,
        updateExtraPrice: updateExtraPriceReducer,
        updateExtraPriceAdult: updateExtraPriceAdultReducer,
        updateExtraPriceBy: updateExtraPriceByReducer,
        updateExtraPriceChild: updateExtraPriceChildReducer,
        updateExtraPriceEndDate: updateExtraPriceEndDateReducer,
        updateExtraPriceInfant: updateExtraPriceInfantReducer,
        updateExtraPriceTeen: updateExtraPriceTeenReducer,
        updateExtraPriceStartDate: updateExtraPriceStartDateReducer,
        updateExtraPriceType: updateExtraPriceTypeReducer,
        updateExtraReference: updateExtraReferenceReducer,
        updateExtraType: updateExtraTypeReducer,
        updateFactSearch: updateFactSearchReducer,
        updateFamilyPlan: updateFamilyPlanReducer,
        updateFamilyPlanMax: updateFamilyPlanMaxReducer,
        updateFax: updateFaxReducer,
        updateFilterAddress: updateFilterAddressReducer,
        updateFilterChain: updateFilterChainReducer,
        updateFilterCountry: updateFilterCountryReducer,
        updateFilterCreatedDateGte: updateFilterCreatedDateGteReducer,
        updateFilterCreatedDateLte: updateFilterCreatedDateLteReducer,
        updateFilterCreator: updateFilterCreatorReducer,
        updateFilterDestination: updateFilterDestinationReducer,
        updateFilterLastAuthor: updateFilterLastAuthorReducer,
        updateFilterModifiedDateGte: updateFilterModifiedDateGteReducer,
        updateFilterModifiedDateLte: updateFilterModifiedDateLteReducer,
        updateFilterName: updateFilterNameReducer,
        updateFilterTag: updateFilterTagReducer,
        updateFilterPeriod: updateFilterPeriodReducer,
        updateFilterPeriodType: updateFilterPeriodTypeReducer,
        updateFilterStatus: updateFilterStatusReducer,
        updateFilterTags: updateFilterTagsReducer,
        updateGlobalParameters: updateGlobalParametersReducer,
        updateGoogleId: updateGoogleIdReducer,
        updateHouseNumber: updateHouseNumberReducer,
        updateLatitude: updateLatitudeReducer,
        updateListItemVisibility: updateListItemVisibilityReducer,
        updateListItemLock: updateListItemLockReducer,
        updateListItemPublishOnWeb: updateListItemPublishOnWebReducer,
        updateLongitude: updateLongitudeReducer,
        updateVideoLink: updateVideoLinkReducer,
        updateMaxAgeBaby: updateMaxAgeBabyReducer,
        updateMaxAgeChild: updateMaxAgeChildReducer,
        updateMaxAgeTeen: updateMaxAgeTeenReducer,
        updateName: updateNameReducer,
        updateOfferAppliedFor: updateOfferAppliedForReducer,
        updateOfferAppliedForMax: updateOfferAppliedForMaxReducer,
        updateOfferAppliedForMin: updateOfferAppliedForMinReducer,
        updateOfferAppliedForMinError: updateOfferAppliedForMinErrorReducer,
        updateOfferBookedNight: updateOfferBookedNightReducer,
        updateOfferBookedNightNumber: updateOfferBookedNightNumberReducer,
        updateOfferBookedRoom: updateOfferBookedRoomReducer,
        updateOfferBookedRoomNumber: updateOfferBookedRoomNumberReducer,
        updateOfferBy: updateOfferByReducer,
        updateOfferCheckOutDates: updateOfferCheckOutDatesReducer,
        updateOfferCheckOutEndDate: updateOfferCheckOutEndDateReducer,
        updateOfferCheckOutStartDate: updateOfferCheckOutStartDateReducer,
        updateOfferCheckOutStartDateError:
            updateOfferCheckOutStartDateErrorReducer,
        updateOfferChild: updateOfferChildReducer,
        updateOfferChildNumber: updateOfferChildNumberReducer,
        updateOfferCombineAccommodation: updateOfferCombineAccommodationReducer,
        updateOfferCombineAccommodationChain:
            updateOfferCombineAccommodationChainReducer,
        updateOfferDescriptionLocale: updateOfferDescriptionLocaleReducer,
        updateOfferEndDate: updateOfferEndDateReducer,
        updateOfferEndDateError: updateOfferEndDateErrorReducer,
        updateOfferExtra: updateOfferExtraReducer,
        updateOfferExtraError: updateOfferExtraErrorReducer,
        updateOfferExtras: updateOfferExtrasReducer,
        updateOfferExtrasError: updateOfferExtrasErrorReducer,
        updateOfferExtraNumber: updateOfferExtraNumberReducer,
        updateOfferExtraNumberError: updateOfferExtraNumberErrorReducer,
        updateOfferNameLocale: updateOfferNameLocaleReducer,
        updateOfferNight: updateOfferNightReducer,
        updateOfferNightType: updateOfferNightTypeReducer,
        updateOfferReduction: updateOfferReductionReducer,
        updateOfferReductionError: updateOfferReductionErrorReducer,
        updateOfferReductionType: updateOfferReductionTypeReducer,
        updateOfferReference: updateOfferReferenceReducer,
        updateOfferStartDate: updateOfferStartDateReducer,
        updateOfferStartDateError: updateOfferStartDateErrorReducer,
        updateOfferTripEndDate: updateOfferTripEndDateReducer,
        updateOfferTripEndDateError: updateOfferTripEndDateErrorReducer,
        updateOfferTripStartDate: updateOfferTripStartDateReducer,
        updateOfferTripStartDateError: updateOfferTripStartDateErrorReducer,
        updateOfferType: updateOfferTypeReducer,
        updatePhoneNumber: updatePhoneNumberReducer,
        updatePictureOrder: updatePictureOrderReducer,
        updateProvider: updateProviderReducer,
        updateProviders: updateProvidersReducer,
        updateType: updateTypeReducer,
        updateRecommendation: updateRecommendationReducer,
        updateRestorationRate: updateRestorationRateReducer,
        updateRestorationLocale: updateRestorationLocaleReducer,
        updateRoomBedding: updateRoomBeddingReducer,
        updateRoomBlackOutsTarget: updateRoomBlackOutsTargetReducer,
        updateRoomBlackOutEndDate: updateRoomBlackOutEndDateReducer,
        updateRoomBlackOutEndDateError: updateRoomBlackOutEndDateErrorReducer,
        updateRoomBlackOutStartDate: updateRoomBlackOutStartDateReducer,
        updateRoomBlackOutStartDateError:
            updateRoomBlackOutStartDateErrorReducer,
        updateRoomCancellationRules: updateRoomCancellationRulesReducer,
        updateRoomCategory: updateRoomCategoryReducer,
        updateRoomDescLocale: updateRoomDescLocaleReducer,
        updateRoomDescriptionLocale: updateRoomDescriptionLocaleReducer,
        updateRoomEarlyBookings: updateRoomEarlyBookingsReducer,
        updateRoomExtras: updateRoomExtrasReducer,
        updateRoomLockedDuration: updateRoomLockedDurationReducer,
        updateRoomNameLocale: updateRoomNameLocaleReducer,
        updateRoomMain: updateRoomMainReducer,
        updateRoomMaxAdults: updateRoomMaxAdultsReducer,
        updateRoomMaxChildren: updateRoomMaxChildrenReducer,
        updateRoomMaxNights: updateRoomMaxNightsReducer,
        updateRoomMaxTravelers: updateRoomMaxTravelersReducer,
        updateRoomMinAdults: updateRoomMinAdultsReducer,
        updateRoomMinChildren: updateRoomMinChildrenReducer,
        updateRoomMinNights: updateRoomMinNightsReducer,
        updateRoomMinStayPeriodEndDate: updateRoomMinStayPeriodEndDateReducer,
        updateRoomMinStayPeriodStartDate:
            updateRoomMinStayPeriodStartDateReducer,
        updateRoomMinStayPeriodNights: updateRoomMinStayPeriodNightsReducer,
        updateRoomMinTravelers: updateRoomMinTravelersReducer,
        updateRoomOffers: updateRoomOffersReducer,
        updateRoomPictureOrder: updateRoomPictureOrderReducer,
        updateRoomPriceDouble: updateRoomPriceDoubleReducer,
        updateRoomPriceExtraAdult: updateRoomPriceExtraAdultReducer,
        updateRoomPriceExtraBaby: updateRoomPriceExtraBabyReducer,
        updateRoomPriceExtraInfant: updateRoomPriceExtraInfantReducer,
        updateRoomPriceExtraTeen: updateRoomPriceExtraTeenReducer,
        updateRoomPriceMono: updateRoomPriceMonoReducer,
        updateRoomPriceMonoAdult: updateRoomPriceMonoAdultReducer,
        updateRoomPriceMonoChild: updateRoomPriceMonoChildReducer,
        updateRoomPricePeriodDays: updateRoomPricePeriodDaysReducer,
        updateRoomPricePeriodEndDate: updateRoomPricePeriodEndDateReducer,
        updateRoomPricePeriodStartDate: updateRoomPricePeriodStartDateReducer,
        updateRoomPriceQuad: updateRoomPriceQuadReducer,
        updateRoomPriceRestitutionDays: updateRoomPriceRestitutionDaysReducer,
        updateRoomPriceSingle: updateRoomPriceSingleReducer,
        updateRoomPricePerPerson: updateRoomPricePerPersonReducer,
        updateRoomPriceStock: updateRoomPriceStockReducer,
        updateRoomPriceTarget: updateRoomPriceTargetReducer,
        updateRoomPriceTriple: updateRoomPriceTripleReducer,
        updateRoomProhibitedCompositions:
            updateRoomProhibitedCompositionsReducer,
        updateRoomSize: updateRoomSizeReducer,
        updateRoomYields: updateRoomYieldsReducer,
        updateSituationLocale: updateSituationLocaleReducer,
        updateSituationRate: updateSituationRateReducer,
        updateStars: updateStarsReducer,
        updateStocksClosedHoverDate: updateStocksClosedHoverDateReducer,
        updateStocksClosedSale: updateStocksClosedSaleReducer,
        updateStocksCurrentDay: updateStocksCurrentDayReducer,
        updateStocksCurrentMonth: updateStocksCurrentMonthReducer,
        updateStocksEditMode: updateStocksEditModeReducer,
        updateStocksRoomBlackOut: updateStocksRoomBlackOutReducer,
        updateStocksRoomStock: updateStocksRoomStockReducer,
        updateRoomStockWithoutClose: updateRoomStockWithoutCloseReducer,
        updateStocksState: updateStocksStateReducer,
        updateStocksStock: updateStocksStockReducer,
        updateStocksStockValue: updateStocksStockValueReducer,
        updateTaxesFees: updateTaxesFeesReducer,
        updateTaxesMeal: updateTaxesMealReducer,
        updateTaxesMealVat: updateTaxesMealVatReducer,
        updateTaxesResort: updateTaxesResortReducer,
        updateTaxesResortVat: updateTaxesResortVatReducer,
        updateTaxesTouristic: updateTaxesTouristicReducer,
        updateTaxesTouristicVat: updateTaxesTouristicVatReducer,
        updateVat: updateVatReducer,
        updateWebsite: updateWebsiteReducer,
        updateYieldEndDate: updateYieldEndDateReducer,
        updateYieldNameLocale: updateYieldNameLocaleReducer,
        updateYieldRuleAmount: updateYieldRuleAmountReducer,
        updateYieldRuleAmountType: updateYieldRuleAmountTypeReducer,
        updateYieldRuleDays: updateYieldRuleDaysReducer,
        updateYieldRuleType: updateYieldRuleTypeReducer,
        updateYieldStartDate: updateYieldStartDateReducer,
        updateZipCode: updateZipCodeReducer,
        updatePublishOnWeb: updatePublishOnWebReducer,
        updateLock: updateLockReducer,
        updateDestinationName: updateDestinationNameReducer,
        updateZoom: updateZoomReducer,
        updateStockRoomProductInfo: updateStockRoomProductInfoReducer,
        updateStockRoomProduct: updateStockRoomProductReducer,
        loadStockRoomProduct: loadStockRoomProductReducer,
        initCustomPicture: initCustomPictureReducer,
        setMarginMasterMarkup: setMarginMasterMarkupReducer,
        setColumns: setColumnsReducer,
        updateAccommodationTags: updateAccommodationTagsReducer
    },
    extraReducers: {
        [ADD_CUSTOM_PICTURES]: addCustomPicturesExtraReducer,
        [SET_PICTURE]: setPictureExtraReducer,
        [SET_PREFERENCES]: setPreferencesReducer
    }
});

export const {
    initCustomPicture,
    addCancellationRuleCondition,
    addEarlyBookingCondition,
    addExtraIntervalAgePrices,
    addExtraPrice,
    addOnContracts,
    addPreferences,
    addRoomBedding,
    addRoomBlackOut,
    addRoomMinStayPeriod,
    addRoomPrice,
    addRoomPricePeriod,
    addRoomProhibitedComposition,
    addYieldRule,
    deleteCancellationRuleCondition,
    deleteEarlyBookingCondition,
    deleteCustomPicture,
    deleteExtraIntervalAgePrices,
    deleteExtraPrice,
    deleteOnContracts,
    deleteRoomBedding,
    deleteRoomBlackOut,
    deleteRoomMinStayPeriod,
    deleteRoomPrice,
    deleteRoomPricePeriod,
    deleteRoomProhibitedComposition,
    deleteRoomPicture,
    deleteYieldRule,
    moveDownColumn,
    moveUpColumn,
    refreshCancellationRuleSortedList,
    refreshEarlyBookingSortedList,
    refreshExtraSortedList,
    refreshOfferSortedList,
    refreshRoomSortedList,
    refreshYieldSortedList,
    resetContracts,
    resetCreate,
    resetFilters,
    resetManualAccommodation,
    setCancellationRule,
    setCancellationRuleCriteria,
    setCancellationRuleDelete,
    setCancellationRuleDuplicate,
    setCancellationRuleList,
    setContactDelete,
    setContractParameters,
    setEdit,
    setEditStep,
    setErrors,
    setExportType,
    setEarlyBooking,
    setEarlyBookingCriteria,
    setEarlyBookingDelete,
    setEarlyBookingDuplicate,
    setEarlyBookingList,
    setExtra,
    setExtraCriteria,
    setExtraDelete,
    setExtraDuplicate,
    setExtraIntervalAgePrices,
    setExtraList,
    setLimit,
    setList,
    setManualAccommodation,
    setManualAccommodationContacts,
    setManualAccommodationDuplicate,
    setManualAccommodationDuplicateStep,
    setManualAccommodationDuplicateType,
    setManualAccommodationDuplicateAccommodationTo,
    setManualAccommodationDuplicateContractTo,
    setManualAccommodationMealTaxes,
    setManualAccommodationTaxes,
    setManualAccommodationDuplicateMultiple,
    setManualAccommodationDuplicateMultipleItems,
    setManualAccommodationDuplicateMultipleExtraModal,
    setManualAccommodationDuplicateMultipleCancellationRuleModal,
    setManualAccommodationDuplicateMultipleEarlyBookingModal,
    setManualAccommodationDuplicateMultipleOfferModal,
    setManualAccommodationDuplicateMultipleYieldModal,
    setOrderBy,
    setOffer,
    setOfferCriteria,
    setOfferDelete,
    setOfferDuplicate,
    setOfferList,
    setOfferIntervalAgeOffers,
    addOfferIntervalAgeOffers,
    deleteOfferIntervalAgeOffers,
    setOfferIsHoneymoon,
    setOfferIsOnExtraApplied,
    setOfferOnExtraApplied,
    setPage,
    setPreferences,
    setRoom,
    setRoomsBlackOut,
    setRoomsUnBlackOut,
    setRoomBlackOutsDuplicate,
    setRoomBlackOutsRoom,
    setRoomCriteria,
    setRoomDelete,
    setRoomDuplicate,
    setRoomList,
    setRoomPriceScrollId,
    setEmptyRoomPrice,
    setRoomPrices,
    setIsRoomPricesEmpty,
    setRoomPricesDuplicate,
    setRunning,
    setSearch,
    setYield,
    setYieldCriteria,
    setYieldDelete,
    setYieldDuplicate,
    setYieldList,
    setYieldReference,
    toggleColumn,
    toggleComCalculationAsPercentage,
    toggleContractCreate,
    toggleCreate,
    toggleEdit,
    toggleExport,
    toggleExportFull,
    toggleExtraConflict,
    toggleExtraIncluded,
    toggleExtraTransfer,
    toggleFact,
    toggleFilters,
    toggleOfferCombine,
    toggleOfferSubRoom,
    togglePictureActivated,
    toggleRefresh,
    toggleRoomCancellationRule,
    toggleRoomEarlyBooking,
    toggleRoomExtra,
    toggleRoomOffer,
    toggleRoomPicturesOrder,
    toggleRoomYield,
    toggleStocksViewMode,
    toggleSuperiorStars,
    toggleDisplayForTripPlan,
    toggleDisplayForPackage,
    toggleTeenAdultMonoparental,
    updateAddress,
    updateAddressSearch,
    updateBestOfferLimit,
    updateBestOfferOrder,
    updateBookingType,
    updateCancellationRuleDays,
    updateCancellationRuleEndDate,
    updateCancellationRuleFree,
    // updateCancellationRuleName,
    updateCancellationRuleNameLocale,
    updateCancellationRulePrice,
    updateCancellationRulePriceType,
    updateCancellationRuleReference,
    updateCancellationRuleStartDate,
    updateCancellationRuleType,
    updateCatalogPrice,
    updateChain,
    updateCheckInHour,
    updateCheckInMinutes,
    // updateCheckInRecommendation,
    updateCheckInRecommendationLocale,
    updateCheckOutHour,
    updateCheckOutMinutes,
    // updateCheckOutRecommendation,
    updateCheckOutRecommendationLocale,
    updateChildrenLocale,
    updateCity,
    updateClientHotelIdentifier,
    updateIsProtectedDescription,
    updateComfortRate,
    updateContactEmail,
    updateContactFirstName,
    updateContactJob,
    updateContactLastName,
    updateContactPhoneNumber,
    updateContractPricingType,
    updateComAppliedOnTTC,
    updateContractGlobalCom,
    updateContractGlobalMarkup,
    updateContractComIsPercentage,
    updateContractMarkupIsPercentage,
    addStayDuration,
    deleteStayDuration,
    updateStayDuration,
    updateContract,
    updateContracts,
    updateContractSelected,
    updateCountry,
    updateCreateName,
    updateCreateSearchCity,
    updateCreateSearchCountry,
    updateCreateSearchName,
    updateCreateSearchResults,
    updateCreateSearchSelected,
    updateCreateTab,
    updateDuplicateContractTargetContract,
    // updateDuplicateContractTargetProvider,
    updateDuplicatePrice,
    updateDuplicatePriceChange,
    updateDuplicatePriceChangeType,
    updateDuplicatePriceType,
    updateEarlyBookingDays,
    updateEarlyBookingDaysError,
    updateEarlyBookingDeduction,
    updateEarlyBookingDeductionError,
    updateEarlyBookingDeductionType,
    updateEarlyBookingEndDate,
    updateEarlyBookingExtra,
    updateEarlyBookingExtraConflicts,
    updateEarlyBookingMaxDate,
    updateEarlyBookingMaxDateError,
    updateEarlyBookingNameLocale,
    updateEarlyBookingDescriptionLocale,
    updateEarlyBookingOfferConflicts,
    updateEarlyBookingReference,
    updateEarlyBookingMinDays,
    updateEarlyBookingMaxDays,
    updateEarlyBookingStartDate,
    updateEarlyBookingType,
    updateEmail,
    // updateExtraDescription,
    updateExtraDescriptionLocale,
    updateExtraMaximalAge,
    updateExtraMealPlan,
    updateExtraMinimalAge,
    updateExtraMinimalTravelers,
    // updateExtraName,
    updateExtraNameLocale,
    updateExtraPerDay,
    updateExtraPerPerson,
    updateExtraPrice,
    updateExtraPriceAdult,
    updateExtraPriceBy,
    updateExtraPriceChild,
    updateExtraPriceEndDate,
    updateExtraPriceInfant,
    updateExtraPriceTeen,
    updateExtraPriceStartDate,
    updateExtraPriceType,
    updateExtraReference,
    updateExtraType,
    updateFactSearch,
    updateFamilyPlan,
    updateFamilyPlanMax,
    updateFax,
    updateFilterAddress,
    updateFilterChain,
    updateFilterCountry,
    updateFilterCreatedDateGte,
    updateFilterCreatedDateLte,
    updateFilterCreator,
    updateFilterDestination,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterName,
    updateFilterTag,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateFilterStatus,
    updateFilterTags,
    updateGlobalParameters,
    updateGoogleId,
    updateHouseNumber,
    updateLatitude,
    updateListItemVisibility,
    updateListItemLock,
    updateListItemPublishOnWeb,
    updateLongitude,
    updateVideoLink,
    updateMaxAgeBaby,
    updateMaxAgeChild,
    updateMaxAgeTeen,
    updateName,
    updateOfferAppliedFor,
    updateOfferAppliedForMax,
    updateOfferAppliedForMin,
    updateOfferAppliedForMinError,
    updateOfferBookedNight,
    updateOfferBookedNightNumber,
    updateOfferBookedRoom,
    updateOfferBookedRoomNumber,
    updateOfferBy,
    updateOfferCheckOutDates,
    updateOfferCheckOutEndDate,
    updateOfferCheckOutStartDate,
    updateOfferCheckOutStartDateError,
    updateOfferChild,
    updateOfferChildNumber,
    updateOfferCombineAccommodation,
    updateOfferCombineAccommodationChain,
    updateOfferDescriptionLocale,
    updateOfferEndDate,
    updateOfferEndDateError,
    updateOfferExtra,
    updateOfferExtraError,
    updateOfferExtras,
    updateOfferExtrasError,
    updateOfferExtraNumber,
    updateOfferExtraNumberError,
    updateOfferNameLocale,
    updateOfferNight,
    updateOfferNightType,
    updateOfferReduction,
    updateOfferReductionError,
    updateOfferReductionType,
    updateOfferReference,
    updateOfferStartDate,
    updateOfferStartDateError,
    updateOfferTripEndDate,
    updateOfferTripEndDateError,
    updateOfferTripStartDate,
    updateOfferTripStartDateError,
    updateOfferType,
    updatePhoneNumber,
    updatePictureOrder,
    updateProvider,
    updateProviders,
    updateType,
    updateRecommendation,
    updateRestorationRate,
    updateRestorationLocale,
    updateRoomBedding,
    updateRoomBlackOutsTarget,
    updateRoomBlackOutEndDate,
    updateRoomBlackOutEndDateError,
    updateRoomBlackOutStartDate,
    updateRoomBlackOutStartDateError,
    updateRoomCancellationRules,
    updateRoomCategory,
    updateRoomDescLocale,
    updateRoomDescriptionLocale,
    updateRoomEarlyBookings,
    updateRoomExtras,
    updateRoomLockedDuration,
    updateRoomNameLocale,
    updateRoomMain,
    updateRoomMaxAdults,
    updateRoomMaxChildren,
    updateRoomMaxNights,
    updateRoomMaxTravelers,
    updateRoomMinAdults,
    updateRoomMinChildren,
    updateRoomMinNights,
    updateRoomMinStayPeriodEndDate,
    updateRoomMinStayPeriodStartDate,
    updateRoomMinStayPeriodNights,
    updateRoomMinTravelers,
    updateRoomOffers,
    updateRoomPictureOrder,
    updateRoomPriceDouble,
    updateRoomPriceExtraAdult,
    updateRoomPriceExtraBaby,
    updateRoomPriceExtraInfant,
    updateRoomPriceExtraTeen,
    updateRoomPriceMono,
    updateRoomPriceMonoAdult,
    updateRoomPriceMonoChild,
    updateRoomPricePeriodDays,
    updateRoomPricePeriodEndDate,
    updateRoomPricePeriodStartDate,
    updateRoomPriceQuad,
    updateRoomPriceRestitutionDays,
    updateRoomPriceSingle,
    updateRoomPricePerPerson,
    updateRoomPriceStock,
    updateRoomPriceTarget,
    updateRoomPriceTriple,
    updateRoomProhibitedCompositions,
    updateRoomSize,
    updateRoomYields,
    updateSituationLocale,
    updateSituationRate,
    updateStars,
    updateStocksClosedHoverDate,
    updateStocksClosedSale,
    updateStocksCurrentDay,
    updateStocksCurrentMonth,
    updateStocksEditMode,
    updateStocksRoomBlackOut,
    updateStocksRoomStock,
    updateRoomStockWithoutClose,
    updateStocksState,
    updateStocksStock,
    updateStocksStockValue,
    updateTaxesFees,
    updateTaxesMeal,
    updateTaxesMealVat,
    updateTaxesResort,
    updateTaxesResortVat,
    updateTaxesTouristic,
    updateTaxesTouristicVat,
    updateVat,
    updateWebsite,
    updateYieldEndDate,
    updateYieldNameLocale,
    updateYieldRuleAmount,
    updateYieldRuleAmountType,
    updateYieldRuleDays,
    updateYieldRuleType,
    updateYieldStartDate,
    updateZipCode,
    updateLock,
    updatePublishOnWeb,
    updateDestinationName,
    updateZoom,
    updateStockRoomProductInfo,
    updateStockRoomProduct,
    loadStockRoomProduct,
    setMarginMasterMarkup,
    setColumns,
    updateAccommodationTags
} = manualAccommodation.actions;
